import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AuthUser, postLog } from '../utils/authApi';
import { loadMessageTemplates, applyTemplate } from '../utils/messageTemplates';
import { getCurrencyDisplay } from '../utils/currency';
import { getCountryCurrency } from '../utils/countryCurrency';
import { createPortal } from 'react-dom';
const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

const getToken = () => localStorage.getItem('cloudairambo_jwt') || '';
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${getToken()}`,
});

// Turns a WhatsApp/Email send failure into a message the user can act on —
// distinguishing "not connected" from a plain network/server error. Same
// logic as the mobile app's utils/whatsapp.ts, ported to the web dashboard.
function messagingErrorMessage(status: number | null, data: any, channel: 'WhatsApp' | 'Email'): string {
  const detail = String(data?.detail || data?.error || data?.message || '').toLowerCase();
  if (detail.includes('not connect') || detail.includes('not configur') || detail.includes(channel === 'WhatsApp' ? 'no whatsapp' : 'no smtp') || detail.includes('credential')) {
    return `${channel} isn't connected for this outlet. Connect it in Settings to send messages.`;
  }
  if (status === 401 || status === 403) return `You don't have permission to send ${channel} messages for this outlet.`;
  if (status === 429) return 'Too many messages sent recently. Please wait a moment and try again.';
  if (data?.detail && typeof data.detail === 'string' && data.detail.length < 100) return data.detail;
  return `The ${channel} message couldn't be sent. Please try again.`;
}

async function sendWhatsApp(phone: string, message: string, outletId?: number | null) {
  let res: Response;
  try {
    res = await fetch(`${BASE}/whatsapp/send`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ phone, message, outlet_id: outletId }),
    });
  } catch {
    window.dispatchEvent(new CustomEvent('service-disconnected', {
      detail: { service: 'whatsapp', reason: "Couldn't connect to WhatsApp server. Check your internet connection." }
    }));
    throw new Error("Couldn't connect. Check your internet connection and try again.");
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.success === false) {
    const err = messagingErrorMessage(res.status, data, 'WhatsApp');
    window.dispatchEvent(new CustomEvent('service-disconnected', {
      detail: { service: 'whatsapp', reason: err }
    }));
    throw new Error(err);
  }
  return data;
}

async function sendEmail(to: string, subject: string, html: string, outletId?: number | null) {
  let res: Response;
  try {
    res = await fetch(`${BASE}/email/send`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ to, subject, html, outlet_id: outletId }),
    });
  } catch {
    throw new Error("Couldn't connect. Check your internet connection and try again.");
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.success === false) throw new Error(messagingErrorMessage(res.status, data, 'Email'));
  return data;
}

// Pulls a user-presentable message out of a caught error, falling back to a
// generic message when the error wasn't thrown by sendWhatsApp/sendEmail.
function friendlyError(e: any, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback;
}

// new Date().toISOString() always returns the UTC date, not the browser's local
// date — so for users ahead of UTC (e.g. IST), anytime between midnight and their
// UTC offset, "today" would silently resolve to yesterday. This computes the date
// using the browser's actual local calendar date instead.
function localDateStr(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Postgres TIMESTAMP WITHOUT TIME ZONE comes back with no 'Z' suffix, so the
// browser's Date parser treats it as local time instead of UTC.
function parseUtc(iso: string): Date {
  if (!iso) return new Date();
  return new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z');
}
const COUNTRY_CODES: Record<string, string> = {
  AF:'93',AL:'355',DZ:'213',AS:'1684',AD:'376',AO:'244',AI:'1264',AQ:'672',AG:'1268',AR:'54',
  AM:'374',AW:'297',AU:'61',AT:'43',AZ:'994',BS:'1242',BH:'973',BD:'880',BB:'1246',BY:'375',
  BE:'32',BZ:'501',BJ:'229',BM:'1441',BT:'975',BO:'591',BA:'387',BW:'267',BR:'55',IO:'246',
  VG:'1284',BN:'673',BG:'359',BF:'226',BI:'257',KH:'855',CM:'237',CA:'1',CV:'238',KY:'1345',
  CF:'236',TD:'235',CL:'56',CN:'86',CX:'61',CC:'61',CO:'57',KM:'269',CK:'682',CR:'506',
  HR:'385',CU:'53',CW:'599',CY:'357',CZ:'420',CD:'243',DK:'45',DJ:'253',DM:'1767',DO:'1809',
  EC:'593',EG:'20',SV:'503',GQ:'240',ER:'291',EE:'372',ET:'251',FK:'500',FO:'298',FJ:'679',
  FI:'358',FR:'33',GF:'594',PF:'689',GA:'241',GM:'220',GE:'995',DE:'49',GH:'233',GI:'350',
  GR:'30',GL:'299',GD:'1473',GP:'590',GU:'1671',GT:'502',GG:'44',GN:'224',GW:'245',GY:'592',
  HT:'509',HN:'504',HK:'852',HU:'36',IS:'354',IN:'91',ID:'62',IR:'98',IQ:'964',IE:'353',
  IM:'44',IL:'972',IT:'39',CI:'225',JM:'1876',JP:'81',JE:'44',JO:'962',KZ:'7',KE:'254',
  KI:'686',XK:'383',KW:'965',KG:'996',LA:'856',LV:'371',LB:'961',LS:'266',LR:'231',LY:'218',
  LI:'423',LT:'370',LU:'352',MO:'853',MK:'389',MG:'261',MW:'265',MY:'60',MV:'960',ML:'223',
  MT:'356',MH:'692',MQ:'596',MR:'222',MU:'230',YT:'262',MX:'52',FM:'691',MD:'373',MC:'377',
  MN:'976',ME:'382',MS:'1664',MA:'212',MZ:'258',MM:'95',NA:'264',NR:'674',NP:'977',NL:'31',
  NC:'687',NZ:'64',NI:'505',NE:'227',NG:'234',NU:'683',NF:'672',KP:'850',MP:'1670',
  NO:'47',OM:'968',PK:'92',PW:'680',PS:'970',PA:'507',PG:'675',PY:'595',PE:'51',PH:'63',
  PN:'64',PL:'48',PT:'351',PR:'1787',QA:'974',CG:'242',RE:'262',RO:'40',RU:'7',RW:'250',
  BL:'590',SH:'290',KN:'1869',LC:'1758',MF:'590',PM:'508',VC:'1784',WS:'685',SM:'378',
  ST:'239',SA:'966',SN:'221',RS:'381',SC:'248',SL:'232',SG:'65',SX:'1721',SK:'421',SI:'386',
  SB:'677',SO:'252',ZA:'27',KR:'82',SS:'211',ES:'34',LK:'94',SD:'249',SR:'597',SJ:'47',
  SZ:'268',SE:'46',CH:'41',SY:'963',TW:'886',TJ:'992',TZ:'255',TH:'66',TL:'670',TG:'228',
  TK:'690',TO:'676',TT:'1868',TN:'216',TR:'90',TM:'993',TC:'1649',TV:'688',VI:'1340',
  UG:'256',UA:'380',AE:'971',GB:'44',US:'1',UY:'598',UZ:'998',VU:'678',VA:'39',VE:'58',
  VN:'84',WF:'681',EH:'212',YE:'967',ZM:'260',ZW:'263',
};

const POPULAR_COUNTRIES = ['IN','AE','US','GB','SA','SG','AU','CA','PK','BD','MY','QA','KW','OM','BH'];

function CountryPicker({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const allCodes = Object.keys(COUNTRY_CODES);
  const filtered = search
    ? allCodes.filter(c =>
        c.toLowerCase().includes(search.toLowerCase()) ||
        COUNTRY_CODES[c]?.includes(search.replace('+', ''))
      )
    : [...POPULAR_COUNTRIES, ...allCodes.filter(c => !POPULAR_COUNTRIES.includes(c))];

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(!open)} style={{
        display: 'flex', alignItems: 'center', gap: 4, background: '#f8fafc',
        border: '1.5px solid #e2e8f0', borderRadius: '8px 0 0 8px',
        padding: '8px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 700,
        color: '#0f172a', minWidth: 72, whiteSpace: 'nowrap' as const,
      }}>
        +{COUNTRY_CODES[value] || '91'} <span style={{ fontSize: 9, opacity: 0.5 }}>▾</span>
      </button>
      {open && (
        <div onClick={e => e.stopPropagation()} style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 9999,
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)', width: 200, maxHeight: 260,
          overflow: 'hidden', display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: 8 }}>
            <input autoFocus placeholder="Search country or +code..." value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 6, padding: '5px 8px', fontSize: 12, outline: 'none', boxSizing: 'border-box' as const }} />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.map(c => (
              <button key={c} onClick={() => { onChange(c); setOpen(false); setSearch(''); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                  background: c === value ? '#f0f9ff' : 'transparent',
                  border: 'none', padding: '7px 12px', cursor: 'pointer', fontSize: 12, color: '#374151', textAlign: 'left' as const,
                }}>
                <span style={{ fontWeight: 700, color: '#6366f1', minWidth: 32 }}>+{COUNTRY_CODES[c]}</span>
                <span>{c}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
// ─── Types ────────────────────────────────────────────────────────────────────

interface Shift { id: number; name: string; start_time: string; end_time: string; }
interface Employee {
  id: number; name: string; phone?: string; email?: string;
  department?: string; designation?: string;
  shift_id?: number; shift_name?: string;
  salary_type?: string; monthly_salary?: number; daily_rate?: number;
  join_date?: string; is_active: boolean; device_employee_id?: string;
  role?: string; phone_verified?: boolean; email_verified?: boolean;
  qr_token?: string; webauthn_enrolled?: boolean; face_enrolled?: boolean;
  created_at: string;
}
interface AttendanceRecord {
  id: number; employee_id: number; employee_name?: string;
  date: string; check_in?: string; check_out?: string;
  status: string; source: string; note?: string;
}
interface LeaveRecord {
  id: number; employee_id: number; employee_name?: string;
  from_date: string; to_date: string; leave_type: string;
  reason?: string; status: string; created_at: string;
}
interface PayrollRecord {
  id: number; employee_id: number; employee_name?: string;
  month: number; year: number; working_days?: number; present_days?: number;
  base: number; deductions: number; bonus: number; net: number;
  is_paid: boolean; paid_at?: string; note?: string; created_at: string;
}
interface Outlet {
  id: number; name: string; city: string | null; country: string; currency?: string; modules?: string[];
}
// ─── Constants ────────────────────────────────────────────────────────────────
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  present:  { bg: '#f0fdf4', text: '#166534', border: '#bbf7d0' },
  absent:   { bg: '#fef2f2', text: '#991b1b', border: '#fecaca' },
  late:     { bg: '#fffbeb', text: '#92400e', border: '#fde68a' },
  half_day: { bg: '#eff6ff', text: '#1e40af', border: '#bfdbfe' },
};
const LEAVE_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  pending:  { bg: '#fffbeb', text: '#92400e', border: '#fde68a' },
  approved: { bg: '#f0fdf4', text: '#166534', border: '#bbf7d0' },
  rejected: { bg: '#fef2f2', text: '#991b1b', border: '#fecaca' },
};

// ─── Small reusable components ────────────────────────────────────────────────
const Badge = ({ label, colors }: { label: string; colors: { bg: string; text: string; border: string } }) => (
  <span style={{
    background: colors.bg, color: colors.text, border: `1px solid ${colors.border}`,
    fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
    textTransform: 'uppercase', letterSpacing: '0.04em',
  }}>{label}</span>
);

const Pill = ({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) => (
  <button onClick={onClick} style={{
    padding: '5px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
    fontSize: 12, fontWeight: active ? 700 : 500,
    background: active ? '#0f172a' : 'transparent',
    color: active ? '#fff' : '#64748b',
    transition: 'all 0.15s',
  }}>{label}</button>
);

const Input = (props: React.InputHTMLAttributes<HTMLInputElement> & { label: string }) => {
  const { label, ...rest } = props;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</label>
      <input {...rest} style={{
        padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0',
        fontSize: 13, color: '#0f172a', background: '#fff', outline: 'none',
        fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
        ...rest.style,
      }}
      onFocus={e => e.currentTarget.style.borderColor = '#6366f1'}
      onBlur={e => e.currentTarget.style.borderColor = '#e2e8f0'}
      />
    </div>
  );
};

const Select = (props: React.SelectHTMLAttributes<HTMLSelectElement> & { label: string }) => {
  const { label, children, ...rest } = props;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</label>
      <select {...rest} style={{
        padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0',
        fontSize: 13, color: '#0f172a', background: '#fff', outline: 'none',
        fontFamily: 'inherit', width: '100%', boxSizing: 'border-box', cursor: 'pointer',
        ...rest.style,
      }}
      onFocus={e => e.currentTarget.style.borderColor = '#6366f1'}
      onBlur={e => e.currentTarget.style.borderColor = '#e2e8f0'}
      >{children}</select>
    </div>
  );
};
function EmployeeSearchSelect({ employees, value, onChange, placeholder = 'Search by name, ID (#33) or phone...' }: {
  employees: Employee[]; value: string; onChange: (id: string) => void; placeholder?: string;
}) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = employees.find(e => String(e.id) === value);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // 🔍 Multi-attribute search: Name, Employee ID (#33 or 33), Phone number & Device ID
  const cleanQuery = search.trim().toLowerCase().replace(/^#/, '');
  const filtered = employees.filter(e => {
    if (!cleanQuery) return true;
    const nameMatch = (e.name || '').toLowerCase().includes(cleanQuery);
    const idMatch = String(e.id).includes(cleanQuery);
    const phoneMatch = (e.phone || '').replace(/\D/g, '').includes(cleanQuery.replace(/\D/g, ''));
    const devIdMatch = (e.device_employee_id || '').toLowerCase().includes(cleanQuery);
    return nameMatch || idMatch || (cleanQuery.length >= 3 && phoneMatch) || devIdMatch;
  });

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        value={open ? search : (selected ? `${selected.name} (#${selected.id})` : '')}
        onChange={e => { setSearch(e.target.value); setOpen(true); }}
        onFocus={() => { setOpen(true); setSearch(''); }}
        placeholder={placeholder}
        style={{
          padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0',
          fontSize: 13, color: '#0f172a', background: '#fff', outline: 'none',
          fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' as const,
        }}
        onBlurCapture={e => e.currentTarget.style.borderColor = '#e2e8f0'}
      />
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999,
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
          boxShadow: '0 12px 32px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto',
          marginTop: 4,
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '10px 12px', fontSize: 12, color: '#94a3b8' }}>
              No employees match "{search}"
            </div>
          ) : filtered.map(e => (
            <button
              key={e.id}
              onClick={() => { onChange(String(e.id)); setOpen(false); setSearch(''); }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
                padding: '8px 12px', background: String(e.id) === value ? '#f0f9ff' : 'transparent',
                border: 'none', cursor: 'pointer', fontSize: 13, color: '#0f172a', textAlign: 'left' as const,
                borderBottom: '1px solid #f8fafc',
              }}
              onMouseEnter={ev => ev.currentTarget.style.background = '#f1f5f9'}
              onMouseLeave={ev => ev.currentTarget.style.background = String(e.id) === value ? '#f0f9ff' : 'transparent'}
            >
              <div>
                <span style={{ fontWeight: 700, color: '#0f172a' }}>{e.name}</span>
                {e.phone && <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 6 }}>· {e.phone}</span>}
              </div>
              <span style={{
                fontSize: 10, fontWeight: 800, color: '#4f46e5', background: '#eef2ff',
                border: '1px solid #c7d2fe', padding: '2px 6px', borderRadius: 4, fontFamily: 'monospace',
              }}>
                #{e.id}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
const Modal = ({ title, onClose, children, width = 540 }: {
  title: string; onClose: () => void; children: React.ReactNode; width?: number;
}) => (
  <div style={{
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
    zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  }}>
    <div style={{
      background: '#fff', borderRadius: 16, width: '100%', maxWidth: width,
      boxShadow: '0 24px 64px rgba(0,0,0,0.18)', overflow: 'hidden',
    }}>
      <div style={{
        padding: '18px 24px', borderBottom: '1px solid #f1f5f9',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: '#fafafa',
      }}>
        <span style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>{title}</span>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 18, color: '#94a3b8', lineHeight: 1,
          width: 28, height: 28, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>✕</button>
      </div>
      <div style={{ padding: 24, maxHeight: '80vh', overflowY: 'auto' }}>{children}</div>
    </div>
  </div>
);


function detectCountryCode(): string {
  const userDefault = localStorage.getItem('default_phone_country');
  if (userDefault && COUNTRY_CODES[userDefault]) return userDefault;

  const cached = sessionStorage.getItem('detected_country');
  if (cached) return cached;
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tzToCountry: Record<string, string> = {
    'Asia/Kolkata':'IN','Asia/Calcutta':'IN','Asia/Dubai':'AE','Asia/Muscat':'OM',
    'Asia/Riyadh':'SA','Asia/Kuwait':'KW','Asia/Qatar':'QA','Asia/Bahrain':'BH',
    'Asia/Karachi':'PK','Asia/Dhaka':'BD','Asia/Kathmandu':'NP','Asia/Colombo':'LK',
    'Asia/Singapore':'SG','Asia/Kuala_Lumpur':'MY','Asia/Jakarta':'ID','Asia/Manila':'PH',
    'Asia/Bangkok':'TH','Asia/Ho_Chi_Minh':'VN','Asia/Tokyo':'JP','Asia/Seoul':'KR',
    'Asia/Shanghai':'CN','Asia/Hong_Kong':'HK','Asia/Taipei':'TW','Asia/Kabul':'AF',
    'Asia/Tehran':'IR','Asia/Baghdad':'IQ','Asia/Beirut':'LB','Asia/Amman':'JO',
    'Asia/Jerusalem':'IL','Asia/Tashkent':'UZ','Asia/Almaty':'KZ','Asia/Baku':'AZ',
    'Asia/Tbilisi':'GE','Asia/Yerevan':'AM','Europe/London':'GB','Europe/Dublin':'IE',
    'Europe/Paris':'FR','Europe/Berlin':'DE','Europe/Amsterdam':'NL','Europe/Madrid':'ES',
    'Europe/Rome':'IT','Europe/Vienna':'AT','Europe/Zurich':'CH','Europe/Stockholm':'SE',
    'Europe/Oslo':'NO','Europe/Copenhagen':'DK','Europe/Helsinki':'FI','Europe/Warsaw':'PL',
    'Europe/Prague':'CZ','Europe/Budapest':'HU','Europe/Bucharest':'RO','Europe/Athens':'GR',
    'Europe/Istanbul':'TR','Europe/Moscow':'RU','Europe/Kiev':'UA','Europe/Minsk':'BY',
    'America/New_York':'US','America/Chicago':'US','America/Denver':'US','America/Los_Angeles':'US',
    'America/Toronto':'CA','America/Vancouver':'CA','America/Sao_Paulo':'BR','America/Mexico_City':'MX',
    'Australia/Sydney':'AU','Australia/Melbourne':'AU','Australia/Perth':'AU','Pacific/Auckland':'NZ',
    'Africa/Cairo':'EG','Africa/Lagos':'NG','Africa/Nairobi':'KE','Africa/Johannesburg':'ZA',
  };
  const code = tzToCountry[tz] || 'IN';
  sessionStorage.setItem('detected_country', code);
  return code;
}

// Universal 240+ country phone parser
function parsePhoneValue(value: string, defaultCountry = 'IN'): { country: string; dialCode: string; nationalNumber: string } {
  const fallbackCountry = defaultCountry && COUNTRY_CODES[defaultCountry] ? defaultCountry : 'IN';
  const fallbackDialCode = (COUNTRY_CODES[fallbackCountry] || '91').replace(/\D/g, '');

  if (!value) return { country: fallbackCountry, dialCode: fallbackDialCode, nationalNumber: '' };

  const raw = String(value).trim();
  const hasLeadingPlus = raw.startsWith('+');
  let cleanDigits = raw.replace(/\D/g, '');

  if (hasLeadingPlus) {
    let bestCountry = fallbackCountry;
    let bestDialCode = fallbackDialCode;
    let maxCodeLen = 0;

    const sortedEntries = Object.entries(COUNTRY_CODES).sort(
      (a, b) => b[1].replace(/\D/g, '').length - a[1].replace(/\D/g, '').length
    );

    for (const [c, code] of sortedEntries) {
      const pureCode = code.replace(/\D/g, '');
      if (cleanDigits.startsWith(pureCode) && pureCode.length > maxCodeLen) {
        bestCountry = c;
        bestDialCode = pureCode;
        maxCodeLen = pureCode.length;
      }
    }

    return {
      country: bestCountry,
      dialCode: bestDialCode,
      nationalNumber: cleanDigits.slice(bestDialCode.length),
    };
  }

  if (cleanDigits.startsWith('0') && fallbackCountry !== 'IT' && cleanDigits.length >= 7) {
    cleanDigits = cleanDigits.replace(/^0/, '');
  }

  if (cleanDigits.startsWith(fallbackDialCode) && cleanDigits.length > 10) {
    return {
      country: fallbackCountry,
      dialCode: fallbackDialCode,
      nationalNumber: cleanDigits.slice(fallbackDialCode.length),
    };
  }

  return {
    country: fallbackCountry,
    dialCode: fallbackDialCode,
    nationalNumber: cleanDigits,
  };
}

// Validates that there are at least 4 digits in national number, and 7 to 15 total international digits
export function getCleanE164Phone(phone: string, defaultCountry = 'IN'): string {
  if (!phone) return '';
  const parsed = parsePhoneValue(phone, defaultCountry);
  const national = parsed.nationalNumber.replace(/\D/g, '');
  const dial = parsed.dialCode.replace(/\D/g, '');
  const total = `${dial}${national}`;

  if (national.length < 4 || total.length < 7 || total.length > 15) {
    return '';
  }
  return `+${dial}${national}`;
}

// Guaranteed clean digits for WhatsApp API (e.g. "919108582545")
function resolveWaPhone(rawPhone: string, defaultCountry = 'IN'): string {
  const clean = getCleanE164Phone(rawPhone, defaultCountry);
  return clean.replace(/\D/g, '');
}

function EmpPhoneInput({ value, onChange, defaultCountry = 'IN' }: { value: string; onChange: (v: string) => void; defaultCountry?: string }) {
  const parsed = parsePhoneValue(value, defaultCountry);
  const [country, setCountry] = useState(() => {
    if (value && value.startsWith('+')) return parsed.country;
    if (defaultCountry && COUNTRY_CODES[defaultCountry]) return defaultCountry;
    return detectCountryCode();
  });
  const [number, setNumber] = useState(() => parsed.nationalNumber);
  const lastEmitted = useRef(value);

  useEffect(() => {
    if (value === lastEmitted.current) return;
    lastEmitted.current = value;
    if (!value) {
      setNumber('');
    } else {
      const p = parsePhoneValue(value, defaultCountry);
      setCountry(p.country);
      setNumber(p.nationalNumber);
    }
  }, [value, defaultCountry]);

  const handleCountryChange = (newCountry: string) => {
    setCountry(newCountry);
    const dialCode = COUNTRY_CODES[newCountry] || '91';
    const cleanDigits = number.trim();
    const formatted = cleanDigits ? `+${dialCode}${cleanDigits}` : '';
    lastEmitted.current = formatted;
    onChange(formatted);
  };

  const handleNumberChange = (raw: string) => {
    let cleanNum = raw.replace(/\D/g, '');
    const dialCode = (COUNTRY_CODES[country] || '91').replace(/\D/g, '');

    if (raw.includes('+') && cleanNum.startsWith(dialCode)) {
      cleanNum = cleanNum.slice(dialCode.length);
    } else if (cleanNum.startsWith(dialCode) && cleanNum.length > 10) {
      cleanNum = cleanNum.slice(dialCode.length);
    }

    setNumber(cleanNum);
    const formatted = cleanNum ? `+${dialCode}${cleanNum}` : '';
    lastEmitted.current = formatted;
    onChange(formatted);
  };

  return (
    <div style={{ display: 'flex' }}>
      <CountryPicker value={country} onChange={handleCountryChange} />
      <input
        type="tel"
        value={number}
        onChange={e => handleNumberChange(e.target.value)}
        placeholder="Phone number"
        style={{
          flex: 1, border: '1.5px solid #e2e8f0', borderLeft: 'none',
          borderRadius: '0 8px 8px 0', padding: '8px 12px',
          fontSize: 13, outline: 'none', background: '#fff',
          fontWeight: 600, color: '#0f172a', boxSizing: 'border-box' as const,
        }}
        onFocus={e => e.currentTarget.style.borderColor = '#6366f1'}
        onBlur={e => e.currentTarget.style.borderColor = '#e2e8f0'}
      />
    </div>
  );
}
// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function EmployeesView({ authUser, moduleStatuses, outletId }: { authUser: AuthUser | null; moduleStatuses?: any; outletId?: number | null }) {
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      *::-webkit-scrollbar { width: 0px; height: 0px; display: none; }
      * { scrollbar-width: none; -ms-overflow-style: none; }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);
  const [modulePrice, setModulePrice] = useState<number | null>(null);
  const [companyName, setCompanyName] = useState('');

  // ⚠️ WhatsApp Disconnected Modal & Floating Toast State
  const [notConnectedModal, setNotConnectedModal] = useState<{ service: 'whatsapp' | 'email'; reason?: string } | null>(null);
  const [toastMsg, setToastMsg] = useState<{ msg: string; type: 'success' | 'error' | 'info' } | null>(null);

  const showToast = (msg: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToastMsg({ msg, type });
    setTimeout(() => setToastMsg(null), 3500);
  };

  useEffect(() => {
    const handleDisconnected = (e: any) => {
      setNotConnectedModal(e.detail || { service: 'whatsapp' });
    };
    window.addEventListener('service-disconnected', handleDisconnected);
    return () => window.removeEventListener('service-disconnected', handleDisconnected);
  }, []);

  useEffect(() => {
    fetch(`${BASE}/auth/company-profile`, { headers: authHeaders() })
      .then(r => r.json())
      .then((p: any) => {
        if (p?.name) setCompanyName(p.name);
        else if (authUser?.name) setCompanyName(authUser.name);
      })
      .catch(() => {
        if (authUser?.name) setCompanyName(authUser.name);
      });
  }, [authUser]);

  const applyT = (tpl: string, vars: any) => {
    const activeCompany = companyName || authUser?.name || 'Our Company';
    if (!tpl) return '';
    let formatted = applyTemplate(tpl, { company: activeCompany, ...vars });
    return formatted.replace(/CloudAIRambo/gi, activeCompany);
  };

  const [openRowMenu, setOpenRowMenu] = useState<number | null>(null);
  const [rowMenuPos, setRowMenuPos] = useState<{ top: number; left: number } | null>(null);
  const [billingCurrency, setBillingCurrency] = useState(
    localStorage.getItem('cloudairambo_active_currency') || 'INR'
  );
  const currSym = getCurrencyDisplay(
    billingCurrency,
    (localStorage.getItem('cloudairambo_currency_display_mode') as 'symbol' | 'code') || 'symbol'
  );
useEffect(() => {
  const handler = () => setOpenRowMenu(null);
  document.addEventListener('click', handler);
  return () => document.removeEventListener('click', handler);
}, []);
  useEffect(() => {
    fetch(`${BASE}/billing/my-country`)
      .then(r => r.json())
      .then(geo => {
        const country = geo.country || '';
        return fetch(`${BASE}/billing/prices?country=${country}`)
          .then(r => r.json())
          .then((rows: any[]) => {
            const countryRows = rows.filter((r: any) => r.country === country);
const activeCurr = countryRows.length ? countryRows[0].currency : getCountryCurrency(country);

            const row = rows.find(r => r.plan === 'employees' && r.billing_cycle === 'monthly');
            if (row) setModulePrice(row.price);
          });
      })
      .catch(() => {});
  }, []);

  if (moduleStatuses?.['employees'] === 'locked') {
    return (
      <div style={{
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        minHeight: '100vh',
        background: '#f8fafc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center', padding: 40, maxWidth: 400 }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>🔒</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: '#0f172a', marginBottom: 8 }}>Employees Access Expired</div>
          <div style={{ fontSize: 14, color: '#64748b', marginBottom: 32, lineHeight: 1.6 }}>Your Employees subscription has ended. Renew to manage staff, attendance, leaves and payroll.</div>
          <button onClick={() => window.dispatchEvent(new CustomEvent('show-upgrade-plans'))}
  style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', border: 'none', borderRadius: 14, padding: '14px 32px', fontSize: 15, fontWeight: 800, cursor: 'pointer', boxShadow: '0 4px 14px rgba(99,102,241,0.4)' }}>
  Choose a Plan →
</button>
        </div>
      </div>
    );
  }

  const isEmpOwner = authUser?.role === 'owner' || authUser?.role === 'admin';


  const ePerms = {
    manageEmployees:  isEmpOwner || ((authUser as any)?.permissions?.canManageEmployees  ?? false),
    manageLeaves:     isEmpOwner || ((authUser as any)?.permissions?.canManageLeaves     ?? false),
    requestLeave:     isEmpOwner || ((authUser as any)?.permissions?.canRequestLeave  ?? false),
    managePayroll:    isEmpOwner || ((authUser as any)?.permissions?.canManagePayroll    ?? false),
    manageAttendance: isEmpOwner || ((authUser as any)?.permissions?.canManageAttendance ?? false),
    viewPayroll:      isEmpOwner || ((authUser as any)?.permissions?.canViewPayroll      ?? false),
    viewLeaves:       isEmpOwner || ((authUser as any)?.permissions?.canViewLeaves       ?? false),
    viewAttendance:   isEmpOwner || ((authUser as any)?.permissions?.canViewAttendance   ?? false),
  };

  // Tab visibility for employee_member
  const isEmployeeMember = authUser?.role === 'employee_member';
  const visibleTabs = isEmployeeMember
    ? (['staff', 'attendance', 'leaves', 'payroll'] as const).filter(t =>
        t === 'staff'      ? ePerms.manageEmployees :
        t === 'attendance' ? (ePerms.manageEmployees || ePerms.manageAttendance || ePerms.viewAttendance) :
        t === 'leaves'     ? (ePerms.manageLeaves || ePerms.viewLeaves || ePerms.requestLeave) :
        t === 'payroll'    ? (ePerms.managePayroll || ePerms.viewPayroll) :
        false
      )
    : (['staff', 'attendance', 'leaves', 'payroll'] as const);

  const [tab, setTab] = useState<'staff' | 'attendance' | 'leaves' | 'payroll'>(
    isEmployeeMember
      ? (ePerms.manageEmployees ? 'staff' : (ePerms.manageAttendance || ePerms.viewAttendance) ? 'attendance' : (ePerms.manageLeaves || ePerms.viewLeaves || ePerms.requestLeave) ? 'leaves' : (ePerms.managePayroll || ePerms.viewPayroll) ? 'payroll' : 'staff')
      : 'staff'
  );

  // Staff
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [empLoadError, setEmpLoadError] = useState('');

  // For self-service leave requests: match the logged-in employee_member
  // session back to their own Employee row. Login uses email — try that.
  const myEmployee = authUser?.role === 'employee_member'
    ? employees.find(e =>
        (authUser.email && e.email && e.email.toLowerCase() === authUser.email.toLowerCase())
      ) || null
    : null;
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [loadingEmp, setLoadingEmp] = useState(false);
  const [empSearch, setEmpSearch] = useState('');
  const [showAddEmp, setShowAddEmp] = useState(false);
  const [editingEmp, setEditingEmp] = useState<Employee | null>(null);
  const [showAddShift, setShowAddShift] = useState(false);
  const [showShiftsList, setShowShiftsList] = useState(false);
  const [roles, setRoles] = useState<{id:number, name:string}[]>([]);
  const [salaryTypes, setSalaryTypes] = useState<{id:number, name:string, calc_type:string}[]>([]);
  const [departments, setDepartments] = useState<{id:number, name:string}[]>([]);
  const [showAddRole, setShowAddRole] = useState(false);
  const [showAddSalaryType, setShowAddSalaryType] = useState(false);
  const [showAddDept, setShowAddDept] = useState(false);
  const [roleForm, setRoleForm] = useState({ name:'', outlet_id: outletId ? String(outletId) : '' });
  const [salaryTypeForm, setSalaryTypeForm] = useState({ name:'', calc_type:'fixed', outlet_id: outletId ? String(outletId) : '' });
  const [deptForm, setDeptForm] = useState({ name:'', outlet_id: outletId ? String(outletId) : '' });
  const [designations, setDesignations] = useState<{id:number, name:string}[]>([]);
  const [deviceIds, setDeviceIds] = useState<{id:number, name:string}[]>([]);
  const [showAddDesignation, setShowAddDesignation] = useState(false);
  const [showAddDeviceId, setShowAddDeviceId] = useState(false);
  const [designationForm, setDesignationForm] = useState({ name:'', outlet_id: outletId ? String(outletId) : '' });
  const [deviceIdForm, setDeviceIdForm] = useState({ name:'', outlet_id: outletId ? String(outletId) : '' });

  // Modal flow state
  // phase: 'form' | 'otp' | 'done'
  const [modalPhase, setModalPhase] = useState<'form' | 'otp' | 'done'>('form');
const [empSaving, setEmpSaving] = useState(false);
  const [savedEmpId, setSavedEmpId] = useState<number | null>(null);
  const [bioEmpId, setBioEmpId] = useState<string>('');
  const [otpValue, setOtpValue] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
const [preflightOtp, setPreflightOtp] = useState('');

  const [showRemotePunchModal, setShowRemotePunchModal] = useState(false);
  const [remotePunchQR, setRemotePunchQR] = useState('');
  const remotePunchPollRef = useRef<any>(null);
  const [bioStatus, setBioStatus] = useState<'idle' | 'enrolling' | 'scanning' | 'success' | 'error'>('idle');
  const [bioMessage, setBioMessage] = useState('');

  // 🔒 Security OTP & 📱 Phone QR States
  const [showEmpBioOtpModal, setShowEmpBioOtpModal] = useState(false);
  const [empBioTarget, setEmpBioTarget] = useState<Employee | null>(null);
  const [empBioActionType, setEmpBioActionType] = useState<'face' | 'fingerprint' | 'delete_face' | 'delete_fp' | null>(null);
  const [empBioOtpInput, setEmpBioOtpInput] = useState('');
  const [empBioOtpLoading, setEmpBioOtpLoading] = useState(false);

  const [empPhoneEnrollQR, setEmpPhoneEnrollQR] = useState('');
  const [waitingEmpPhoneBio, setWaitingEmpPhoneBio] = useState(false);
  const empPhoneBioPollRef = useRef<any>(null);

  // Face recognition
  const faceEmpVideoRef = useRef<HTMLVideoElement>(null);
  const faceEmpStreamRef = useRef<MediaStream | null>(null);
  const faceEmpScanIntervalRef = useRef<any>(null);
  const [showFaceRegisterEmp, setShowFaceRegisterEmp] = useState(false);
  const [faceRegisterEmpId, setFaceRegisterEmpId] = useState<number | null>(null);
  const [faceRegisterEmpName, setFaceRegisterEmpName] = useState('');
  const [faceRegisterEmpStatus, setFaceRegisterEmpStatus] = useState<'idle'|'loading'|'capturing'|'done'|'error'>('idle');
  const [showFaceScanEmp, setShowFaceScanEmp] = useState(false);
  const [faceScanEmpStatus, setFaceScanEmpStatus] = useState<'scanning'|'match'|'nomatch'|'error'>('scanning');
  const [faceScanEmpResult, setFaceScanEmpResult] = useState<any>(null);

  // Kiosk / Biometric standalone modals
  const [showKiosk, setShowKiosk] = useState(false);
  const [kioskInput, setKioskInput] = useState('');
  const [kioskResult, setKioskResult] = useState<null | { name: string; scan_type: string; time: string; status: string }>(null);
  const [kioskLoading, setKioskLoading] = useState(false);
  const [showBiometric, setShowBiometric] = useState(false);
const [deviceApiKey, setDeviceApiKey] = useState('');
const [showKioskSetup, setShowKioskSetup] = useState(false);
const [scannerResult, setScannerResult] = useState<null | { name: string; scan_type: string; time: string; status: string }>(null);
const [scannerResultTimer, setScannerResultTimer] = useState<any>(null);
const [showHistoryEmp, setShowHistoryEmp] = useState<Employee | null>(null);
const [historyLogs, setHistoryLogs] = useState<{id:number; date:string; scan_type:string; source:string; time:string; note?:string}[]>([]);
const [historyLoading, setHistoryLoading] = useState(false);

const [showScanSettings, setShowScanSettings] = useState(false);
const [scanSettings, setScanSettings] = useState({ single_scan_only: false, allow_qr: true, allow_face: true, allow_fingerprint: true });
const [scanSettingsLoading, setScanSettingsLoading] = useState(false);
const [scanSettingsSaving, setScanSettingsSaving] = useState(false);

  // Attendance
  const [todayAtt, setTodayAtt] = useState<AttendanceRecord[]>([]);
  const [attLoadError, setAttLoadError] = useState('');
  const [outlets, setOutlets] = useState<Outlet[]>([]);
  const [attFilterOutlet, setAttFilterOutlet] = useState<string>('current'); // 'current' | 'all' | outlet id
  const exportOutletId =
    attFilterOutlet === 'current' ? (outletId ? String(outletId) : '') :
    attFilterOutlet === 'all' ? '' : attFilterOutlet;
  const [exportingToday, setExportingToday] = useState(false);
  const [exportingMonth, setExportingMonth] = useState(false);

  const fetchOutlets = useCallback(async () => {
    try { const r = await fetch(`${BASE}/outlets`, { headers: authHeaders() }); setOutlets(await r.json()); } catch {}
  }, []);


  useEffect(() => {
  if (!outlets.length) return;
  const match = outletId ? outlets.find(o => o.id === outletId) : outlets[0];
  if (match?.currency) setBillingCurrency(match.currency);
}, [outlets, outletId]);
  // ─── Advanced Date Range, Month/Year & Modal States ────────────────────────
  type DatePreset = 'today' | 'yesterday' | 'last7' | 'this_month' | 'month_year' | 'custom';
  const [attDatePreset, setAttDatePreset] = useState<DatePreset>('today');
  const [attStartDate, setAttStartDate] = useState<string>(localDateStr());
  const [attEndDate, setAttEndDate] = useState<string>(localDateStr());
  const [attSelMonth, setAttSelMonth] = useState<number>(new Date().getMonth() + 1);
  const [attSelYear, setAttSelYear] = useState<number>(new Date().getFullYear());
  const [attFilterEmpId, setAttFilterEmpId] = useState<string>('all');
  const [attFilterStatus, setAttFilterStatus] = useState<string>('all');
  const [attSearchQuery, setAttSearchQuery] = useState<string>('');

  // Day Drilldown Punch Modal State
  const [dayModalData, setDayModalData] = useState<{
    emp: Employee | { id: number; name: string };
    date: string;
    record?: AttendanceRecord;
    logs: { id: number; date: string; scan_type: string; source: string; time: string; note?: string }[];
  } | null>(null);
  const [dayModalLoading, setDayModalLoading] = useState(false);

  function timeToMinutes(t: string): number {
    if (!t) return 0;
    const parts = t.split(':').map(Number);
    return (parts[0] || 0) * 60 + (parts[1] || 0);
  }

  function formatMinutes(mins: number): string {
    if (mins <= 0) return '0 min';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h === 0) return `${m}m`;
    return m === 0 ? `${h} hrs` : `${h}h ${m}m`;
  }

  const handlePresetChange = (preset: DatePreset, customM?: number, customY?: number) => {
    setAttDatePreset(preset);
    const now = new Date();
    const todayStr = localDateStr(now);

    if (preset === 'today') {
      setAttStartDate(todayStr);
      setAttEndDate(todayStr);
    } else if (preset === 'yesterday') {
      const yest = new Date(now);
      yest.setDate(yest.getDate() - 1);
      const yestStr = localDateStr(yest);
      setAttStartDate(yestStr);
      setAttEndDate(yestStr);
    } else if (preset === 'last7') {
      const sevenAgo = new Date(now);
      sevenAgo.setDate(sevenAgo.getDate() - 6);
      setAttStartDate(localDateStr(sevenAgo));
      setAttEndDate(todayStr);
    } else if (preset === 'this_month') {
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      setAttStartDate(localDateStr(firstDay));
      setAttEndDate(todayStr);
    } else if (preset === 'month_year') {
      const m = customM ?? attSelMonth;
      const y = customY ?? attSelYear;
      const first = `${y}-${String(m).padStart(2, '0')}-01`;
      const lastDayNum = new Date(y, m, 0).getDate();
      const last = `${y}-${String(m).padStart(2, '0')}-${String(lastDayNum).padStart(2, '0')}`;
      setAttStartDate(first);
      setAttEndDate(last);
    }
  };

  const openDayDetailModal = async (emp: Employee | { id: number; name: string }, dateStr: string, existingRec?: AttendanceRecord) => {
    setDayModalLoading(true);
    setDayModalData({ emp, date: dateStr, record: existingRec, logs: [] });
    try {
      const r = await fetch(`${BASE}/employees/${emp.id}/logs?date=${dateStr}`, { headers: authHeaders() });
      const data = await r.json();
      const allLogs = data.logs || [];
      const dayLogs = allLogs.filter((l: any) => l.date === dateStr);
      dayLogs.sort((a: any, b: any) => timeToMinutes(a.time) - timeToMinutes(b.time));
      setDayModalData({ emp, date: dateStr, record: existingRec, logs: dayLogs });
    } catch {
      setDayModalData({ emp, date: dateStr, record: existingRec, logs: [] });
    }
    setDayModalLoading(false);
  };

  const [manualEmpId, setManualEmpId] = useState('');
  const [manualDate, setManualDate] = useState(localDateStr());
  const [manualCheckIn, setManualCheckIn] = useState('');
  const [manualCheckOut, setManualCheckOut] = useState('');
  const [manualStatus, setManualStatus] = useState('present');
  const [attLoading, setAttLoading] = useState(false);

  // Month calendar
  const [calEmpId, setCalEmpId] = useState<number | null>(null);
  const [calMonth, setCalMonth] = useState(new Date().getMonth() + 1);
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [calData, setCalData] = useState<AttendanceRecord[]>([]);
  const [showCalQR, setShowCalQR] = useState(false);
  const calQRVideoRef = useRef<HTMLVideoElement>(null);
  const calQRScanInterval = useRef<any>(null);
  const [calQRCameraOn, setCalQRCameraOn] = useState(false);
const kioskVideoRef = useRef<HTMLVideoElement>(null);
const kioskScanInterval = useRef<any>(null);
const [kioskCameraOn, setKioskCameraOn] = useState(false);
const [kioskKeepScanning, setKioskKeepScanning] = useState(false);
const kioskKeepScanningRef = useRef(false);

useEffect(() => {
  kioskKeepScanningRef.current = kioskKeepScanning;
}, [kioskKeepScanning]);
  // Leaves
  const [leaves, setLeaves] = useState<LeaveRecord[]>([]);
  const [leavesLoadError, setLeavesLoadError] = useState('');
  const [leaveSearch, setLeaveSearch] = useState('');
  const [showAddLeave, setShowAddLeave] = useState(false);
  const [showLeaveReplyModal, setShowLeaveReplyModal] = useState(false);
const [pendingLeaveAction, setPendingLeaveAction] = useState<{id: number; status: string; emp: any} | null>(null);
const [leaveReplyMsg, setLeaveReplyMsg] = useState('');

  // Payroll
  const [payroll, setPayroll] = useState<PayrollRecord[]>([]);
  const [payrollLoadError, setPayrollLoadError] = useState('');
  const [payrollSearch, setPayrollSearch] = useState('');
  const [payMonth, setPayMonth] = useState(new Date().getMonth() + 1);
  const [payYear, setPayYear] = useState(new Date().getFullYear());
  const [showGenPayroll, setShowGenPayroll] = useState(false);
  const [genEmpId, setGenEmpId] = useState('');
  const [genBonus, setGenBonus] = useState('');
  const [genDeductions, setGenDeductions] = useState('');
  const [genNote, setGenNote] = useState('');

  const emptyEmpForm = {
  name:'', phone:'', email:'', department:'', designation:'',
  shift_id:'', salary_type:'fixed', monthly_salary:'', daily_rate:'',
  join_date:'', device_employee_id:'', role:'staff',
  outlet_id: outletId ? String(outletId) : '',
};
  const [empForm, setEmpForm] = useState(emptyEmpForm);
  const [empPhoneExists, setEmpPhoneExists] = useState(false);
const [empEmailExists, setEmpEmailExists] = useState(false);
  const [shiftForm, setShiftForm] = useState({ name:'', start_time:'', end_time:'', outlet_id: outletId ? String(outletId) : '' });
  const [leaveForm, setLeaveForm] = useState({ employee_id:'', from_date:'', to_date:'', leave_type:'casual', reason:'' });
  const [leaveOtpSent, setLeaveOtpSent] = useState(false);
  const [leaveOtpValue, setLeaveOtpValue] = useState('');
  const [leaveOtpVerified, setLeaveOtpVerified] = useState(false);
  const [leaveOtpLoading, setLeaveOtpLoading] = useState(false);
  const [leaveOtpError, setLeaveOtpError] = useState('');

  // Switching who the leave is for invalidates any prior OTP verification
  useEffect(() => {
    setLeaveOtpSent(false);
    setLeaveOtpVerified(false);
    setLeaveOtpValue('');
    setLeaveOtpError('');
  }, [leaveForm.employee_id]);

  const handleSendLeaveOtp = async () => {
    if (!leaveForm.employee_id) { alert('Select your name first'); return; }
    setLeaveOtpLoading(true); setLeaveOtpError('');
    try {
      const r = await fetch(`${BASE}/employees/leaves/request-otp`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ employee_id: Number(leaveForm.employee_id) }),
      });
      const data = await r.json();
      if (!r.ok) { setLeaveOtpError(data.detail || 'Failed to send OTP'); setLeaveOtpLoading(false); return; }
      const attempts: string[] = [];
      let delivered = false;
      const outletCountry = outlets.find(o => o.id === outletId)?.country || 'IN';
      const waPhone = resolveWaPhone(data.phone || '', outletCountry);
      if (waPhone) {
        try {
          await sendWhatsApp(waPhone, `Hi ${data.name}, your leave request verification OTP is: ${data.otp}. Valid for 10 minutes.`, outletId);
          delivered = true;
        } catch (e: any) {
          attempts.push(`WhatsApp: ${friendlyError(e, 'failed to send')}`);
        }
      }
      if (data.email) {
        try {
          await sendEmail(
            data.email,
            'Your leave request verification code',
            `<div style="font-family:sans-serif;max-width:400px;margin:auto;padding:32px;background:#f8f9fb;border-radius:16px"><h2 style="color:#1e1b4b">Leave Request Verification</h2><div style="font-size:36px;font-weight:900;letter-spacing:12px;color:#6366f1;background:#fff;padding:20px;border-radius:12px;text-align:center">${data.otp}</div><p style="color:#6b7280;margin-top:16px">Valid for 10 minutes. Do not share this with anyone.</p></div>`,
            outletId
          );
          delivered = true;
        } catch (e: any) {
          attempts.push(`Email: ${friendlyError(e, 'failed to send')}`);
        }
      }
      if (!delivered) {
        setLeaveOtpError(attempts.length ? attempts.join(' · ') : 'No phone or email on file to send an OTP to.');
        setLeaveOtpLoading(false);
        return;
      }
      setLeaveOtpSent(true);
    } catch (e: any) { setLeaveOtpError(friendlyError(e, 'Failed to send OTP')); }
    setLeaveOtpLoading(false);
  };

  const handleVerifyLeaveOtp = async () => {
    if (!leaveOtpValue.trim()) return;
    setLeaveOtpLoading(true); setLeaveOtpError('');
    try {
      const r = await fetch(`${BASE}/employees/leaves/verify-request-otp`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ employee_id: Number(leaveForm.employee_id), otp: leaveOtpValue.trim() }),
      });
      const data = await r.json();
      if (!r.ok) { setLeaveOtpError(data.detail || 'Invalid OTP'); setLeaveOtpLoading(false); return; }
      setLeaveOtpVerified(true);
    } catch { setLeaveOtpError('Something went wrong'); }
    setLeaveOtpLoading(false);
  };
useEffect(() => {
  const digits = empForm.phone.replace(/\D/g, '');
  if (digits.length < 7) { setEmpPhoneExists(false); return; }
  setEmpPhoneExists(!!employees.find(e =>
    e.id !== editingEmp?.id &&
    e.is_active &&
    (e.phone || '').replace(/\D/g, '').slice(-10) === digits.slice(-10)
  ));
}, [empForm.phone, employees, editingEmp]);

useEffect(() => {
  if (!empForm.email.trim() || !empForm.email.includes('@')) { setEmpEmailExists(false); return; }
  setEmpEmailExists(!!employees.find(e =>
    e.id !== editingEmp?.id &&
    e.is_active &&
    (e.email || '').toLowerCase() === empForm.email.trim().toLowerCase()
  ));
}, [empForm.email, employees, editingEmp]);
  // ─── face-api loader ──────────────────────────────────────────────────────
  useEffect(() => {
    if ((window as any).faceapi) return;
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/dist/face-api.min.js';
    script.onload = async () => {
      const faceapi = (window as any).faceapi;
      const MODEL_URL = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.13/model';
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
        faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
        faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
      ]);
    };
    document.head.appendChild(script);
  }, []);

  // ─── Data fetchers ────────────────────────────────────────────────────────
  const [empPlanInfo, setEmpPlanInfo] = useState<{ plan_limit: number; active_count: number; locked_count: number } | null>(null);

const fetchEmployees = useCallback(async () => {
    setLoadingEmp(true);
    setEmpLoadError('');
    try {
      const q = outletId ? `&outlet_id=${outletId}` : '';
      const r = await fetch(`${BASE}/employees/?active_only=false${q}`, { headers: authHeaders() });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setEmployees([]);
        setEmpLoadError(data?.detail || "Couldn't load employees. Pull to refresh or try again.");
        setLoadingEmp(false);
        return;
      }
      // new shape: { employees, plan, plan_tier, plan_limit, active_count, locked_count }
      if (Array.isArray(data.employees)) {
        setEmployees(data.employees);
        setEmpPlanInfo({
          plan_limit: data.plan_limit,
          active_count: data.active_count,
          locked_count: data.locked_count,
        });
      } else if (Array.isArray(data)) {
        // fallback if old shape (plain array response)
        setEmployees(data);
      } else {
        // error response (e.g. {"detail": "Not authorized"}) — never let a
        // non-array leak into state, or every .find/.filter/.map downstream
        // (myEmployee, filteredEmployees, etc.) throws "v.find is not a function"
        setEmployees([]);
        setEmpLoadError("Couldn't load employees. Pull to refresh or try again.");
      }
    } catch {
      setEmployees([]);
      setEmpLoadError("Couldn't connect. Check your internet connection.");
    }
    setLoadingEmp(false);
  }, [outletId]);

  const fetchShifts = useCallback(async () => {
    try {
      const q = outletId ? `?outlet_id=${outletId}` : '';
      const r = await fetch(`${BASE}/employees/shifts${q}`, { headers: authHeaders() });
      if (!r.ok) { setShifts([]); return; }
      setShifts(await r.json());
    } catch {
      setShifts([]);
    }
  }, [outletId]);

  const fetchTodayAtt = useCallback(async () => {
    setAttLoading(true);
    setAttLoadError('');
    try {
      const filterId =
        attFilterOutlet === 'current' ? outletId :
        attFilterOutlet === 'all' ? null :
        Number(attFilterOutlet);

      // 1. Single-day fetch: query the /attendance/today endpoint with date
      if (attStartDate === attEndDate) {
        const params = new URLSearchParams();
        params.set('date', attStartDate);
        if (filterId) params.set('outlet_id', String(filterId));
        if (attFilterEmpId !== 'all') params.set('employee_id', attFilterEmpId);

        const r = await fetch(`${BASE}/employees/attendance/today?${params.toString()}`, { headers: authHeaders() });
        if (!r.ok) {
          const data = await r.json().catch(() => ({}));
          setTodayAtt([]);
          setAttLoadError(data?.detail || "Couldn't load attendance records.");
          setAttLoading(false);
          return;
        }
        const data = await r.json();
        setTodayAtt(Array.isArray(data) ? data : (data.records || []));
        setAttLoading(false);
        return;
      }

      // 2. Date-range / Monthly fetch: Aggregate across employee monthly data
      const start = new Date(attStartDate + 'T00:00:00');
      const end = new Date(attEndDate + 'T00:00:00');
      const startM = start.getMonth() + 1;
      const startY = start.getFullYear();
      const endM = end.getMonth() + 1;
      const endY = end.getFullYear();

      const targetEmps = attFilterEmpId !== 'all'
        ? employees.filter(e => String(e.id) === attFilterEmpId)
        : employees.filter(e => !filterId || (e.outlet_id === filterId));

      const allRecords: AttendanceRecord[] = [];

      await Promise.all(
        targetEmps.map(async (emp) => {
          const q = filterId ? `&outlet_id=${filterId}` : '';
          try {
            const r = await fetch(`${BASE}/employees/attendance/by-employee/${emp.id}/month?month=${startM}&year=${startY}${q}`, { headers: authHeaders() });
            if (r.ok) {
              const monthRecs: AttendanceRecord[] = await r.json();
              if (Array.isArray(monthRecs)) {
                monthRecs.forEach(rec => {
                  if (rec.date >= attStartDate && rec.date <= attEndDate) {
                    allRecords.push({ ...rec, employee_name: rec.employee_name || emp.name });
                  }
                });
              }
            }
          } catch {}

          // If date range spans across into another month
          if (endM !== startM || endY !== startY) {
            try {
              const r2 = await fetch(`${BASE}/employees/attendance/by-employee/${emp.id}/month?month=${endM}&year=${endY}${q}`, { headers: authHeaders() });
              if (r2.ok) {
                const monthRecs2: AttendanceRecord[] = await r2.json();
                if (Array.isArray(monthRecs2)) {
                  monthRecs2.forEach(rec => {
                    if (rec.date >= attStartDate && rec.date <= attEndDate) {
                      if (!allRecords.some(ar => ar.id === rec.id && ar.date === rec.date)) {
                        allRecords.push({ ...rec, employee_name: rec.employee_name || emp.name });
                      }
                    }
                  });
                }
              }
            } catch {}
          }
        })
      );

      // Sort newest dates on top
      allRecords.sort((a, b) => b.date.localeCompare(a.date));

      setTodayAtt(allRecords);
    } catch {
      setTodayAtt([]);
      setAttLoadError("Couldn't connect. Check your internet connection.");
    }
    setAttLoading(false);
  }, [outletId, attFilterOutlet, attStartDate, attEndDate, attFilterEmpId, attFilterStatus, employees]);

  const fetchLeaves = useCallback(async () => {
  setLeavesLoadError('');
  try {
    const q = outletId ? `?outlet_id=${outletId}` : '';
    const r = await fetch(`${BASE}/employees/leaves${q}`, { headers: authHeaders() });
    if (!r.ok) {
      const data = await r.json().catch(() => ({}));
      setLeaves([]);
      setLeavesLoadError(data?.detail || "Couldn't load leave requests.");
      return;
    }
    setLeaves(await r.json());
  } catch {
    setLeaves([]);
    setLeavesLoadError("Couldn't connect. Check your internet connection.");
  }
}, [outletId]);

 const fetchPayroll = useCallback(async () => {
  setPayrollLoadError('');
  try {
    const q = outletId ? `&outlet_id=${outletId}` : '';
    const r = await fetch(`${BASE}/employees/payroll?month=${payMonth}&year=${payYear}${q}`, { headers: authHeaders() });
    if (!r.ok) {
      const data = await r.json().catch(() => ({}));
      setPayroll([]);
      setPayrollLoadError(data?.detail || "Couldn't load payroll.");
      return;
    }
    setPayroll(await r.json());
  } catch {
    setPayroll([]);
    setPayrollLoadError("Couldn't connect. Check your internet connection.");
  }
}, [payMonth, payYear, outletId]);


  const stopCalQRCamera = () => {
    clearInterval(calQRScanInterval.current);
    const stream = calQRVideoRef.current?.srcObject as MediaStream;
    stream?.getTracks().forEach(t => t.stop());
    if (calQRVideoRef.current) calQRVideoRef.current.srcObject = null;
    setCalQRCameraOn(false);
  };

  const startCalQRCamera = async () => {
    setShowCalQR(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      if (calQRVideoRef.current) calQRVideoRef.current.srcObject = stream;
      setCalQRCameraOn(true);

      if (!(window as any).jsQR) {
        await new Promise<void>((res) => {
          const s = document.createElement('script');
          s.src = 'https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.js';
          s.onload = () => res();
          document.head.appendChild(s);
        });
      }

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;

      calQRScanInterval.current = setInterval(() => {
        const video = calQRVideoRef.current;
        if (!video || video.readyState < 2) return;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = (window as any).jsQR(imageData.data, imageData.width, imageData.height);
        if (code?.data) {
          clearInterval(calQRScanInterval.current);
          stopCalQRCamera();
          const emp = employees.find(e => e.qr_token === code.data);
          if (emp) {
            setCalEmpId(emp.id);
            setShowCalQR(false);
            fetchCalendar(emp.id);
          } else {
            alert('No employee matched this QR code.');
            setShowCalQR(false);
          }
        }
      }, 300);
    } catch {
      alert('Camera access denied.');
      setShowCalQR(false);
    }
  };
  const fetchCalendar = useCallback(async (empId: number) => {
  try {
    const q = outletId ? `&outlet_id=${outletId}` : '';
    const r = await fetch(`${BASE}/employees/attendance/by-employee/${empId}/month?month=${calMonth}&year=${calYear}${q}`, { headers: authHeaders() });
    setCalData(await r.json());
  } catch {}
}, [calMonth, calYear, outletId]);

  const fetchRoles = useCallback(async () => {
    try {
      const q = outletId ? `?outlet_id=${outletId}` : '';
      const r = await fetch(`${BASE}/employees/roles${q}`, { headers: authHeaders() });
      setRoles(await r.json());
    } catch {}
  }, [outletId]);
  const fetchSalaryTypes = useCallback(async () => {
    try {
      const q = outletId ? `?outlet_id=${outletId}` : '';
      const r = await fetch(`${BASE}/employees/salary-types${q}`, { headers: authHeaders() });
      setSalaryTypes(await r.json());
    } catch {}
  }, [outletId]);
  const fetchDepartments = useCallback(async () => {
    try {
      const q = outletId ? `?outlet_id=${outletId}` : '';
      const r = await fetch(`${BASE}/employees/departments${q}`, { headers: authHeaders() });
      setDepartments(await r.json());
    } catch {}
  }, [outletId]);
  const fetchDesignations = useCallback(async () => {
    try {
      const q = outletId ? `?outlet_id=${outletId}` : '';
      const r = await fetch(`${BASE}/employees/designations${q}`, { headers: authHeaders() });
      setDesignations(await r.json());
    } catch {}
  }, [outletId]);
  const fetchDeviceIds = useCallback(async () => {
    try {
      const q = outletId ? `?outlet_id=${outletId}` : '';
      const r = await fetch(`${BASE}/employees/device-ids${q}`, { headers: authHeaders() });
      setDeviceIds(await r.json());
    } catch {}
  }, [outletId]);


  const fetchEmployeeHistory = async (emp: Employee) => {
  setShowHistoryEmp(emp);
  setHistoryLoading(true);
  try {
    const r = await fetch(`${BASE}/employees/${emp.id}/logs`, { headers: authHeaders() });
    const data = await r.json();
    setHistoryLogs(data.logs || []);
  } catch {
    setHistoryLogs([]);
  }
  setHistoryLoading(false);
};

const fetchScanSettings = async () => {
  setScanSettingsLoading(true);
  try {
    const r = await fetch(`${BASE}/subscriptions/module-settings?module=employees`, { headers: authHeaders() });
    const data = await r.json();
    setScanSettings({
      single_scan_only: !!data.single_scan_only,
      allow_qr: data.allow_qr !== false,
      allow_face: data.allow_face !== false,
      allow_fingerprint: data.allow_fingerprint !== false,
    });
  } catch {}
  setScanSettingsLoading(false);
};

const saveScanSettings = async () => {
  setScanSettingsSaving(true);
  try {
    await fetch(`${BASE}/subscriptions/module-settings`, {
      method: 'PATCH', headers: authHeaders(),
      body: JSON.stringify({ module: 'employees', ...scanSettings }),
    });
    postLog('SCAN_SETTINGS_UPDATED', `Employees scan settings updated: QR=${scanSettings.allow_qr} Face=${scanSettings.allow_face} FP=${scanSettings.allow_fingerprint} SingleScan=${scanSettings.single_scan_only}`);
    setShowScanSettings(false);
  } catch {
    alert('Failed to save scan settings');
  }
  setScanSettingsSaving(false);
};


  // Refetch ALL module data when branch switches in Navbar
  useEffect(() => {
    fetchEmployees();
    fetchShifts();
    fetchRoles();
    fetchSalaryTypes();
    fetchDepartments();
    fetchDesignations();
    fetchDeviceIds();
    fetchOutlets();
    fetchTodayAtt();
    fetchLeaves();
    fetchPayroll();
    if (calEmpId) fetchCalendar(calEmpId);

    // Refresh Kiosk device API key for the newly selected outlet
    (async () => {
      try {
        const r = await fetch(`${BASE}/subscriptions/kiosk-devices?module=employees`, { headers: authHeaders() });
        const devices = await r.json();
        const match = Array.isArray(devices) ? devices.find((d: any) => (d.outlet_id ?? null) === (outletId ?? null)) : null;
        setDeviceApiKey(match ? match.api_key : '');
      } catch {
        setDeviceApiKey('');
      }
    })();
  }, [outletId]);

  // Live attendance refresh for currently active branch
  useEffect(() => {
    if (!authUser) return;
    const interval = setInterval(() => {
      if (tab === 'attendance') fetchTodayAtt();
    }, 15000);
    return () => clearInterval(interval);
  }, [authUser, tab, outletId, attFilterOutlet]);

  // Tab-specific listeners now include outletId so branch changes trigger instant refresh
  useEffect(() => { 
    if (tab === 'attendance') fetchTodayAtt(); 
  }, [tab, attStartDate, attEndDate, attFilterOutlet, attFilterEmpId, attFilterStatus, outletId]);
  useEffect(() => { if (tab === 'leaves') fetchLeaves(); }, [tab, outletId]);
  useEffect(() => { if (tab === 'payroll') fetchPayroll(); }, [tab, payMonth, payYear, outletId]);
  useEffect(() => { if (calEmpId) fetchCalendar(calEmpId); }, [calEmpId, calMonth, calYear, outletId]);
const showScanResult = (data: { name: string; scan_type: string; time: string; status: string }) => {
  setScannerResult(data);
  if (scannerResultTimer) clearTimeout(scannerResultTimer);
  const t = setTimeout(() => setScannerResult(null), 4000);
  setScannerResultTimer(t);
};
  // ─── Reset modal ──────────────────────────────────────────────────────────
  const closeEmpModal = () => {
  if (empPhoneBioPollRef.current) {
    clearInterval(empPhoneBioPollRef.current);
    empPhoneBioPollRef.current = null;
  }
  setEmpPhoneEnrollQR('');
  setWaitingEmpPhoneBio(false);
  setEmpSaving(false);
  setShowAddEmp(false);
  setEditingEmp(null);
  setEmpForm(emptyEmpForm);
  setModalPhase('form');
  setSavedEmpId(null);
  setOtpValue('');
  setOtpError('');
  setPreflightOtp('');
  setBioStatus('idle');
  setBioMessage('');
};
  // ─── Attendance notification (simple) ────────────────────────────────────
  const fireAttendanceNotification = async (data: {
    name: string; phone?: string; email?: string;
    scan_type: string; time: string; date: string;
  }) => {
    const templates = await loadMessageTemplates(outletId);
    const templateKey = data.scan_type === 'IN' ? 'employee_checkin' : 'employee_checkout';
    const date = data.date || new Date().toISOString().slice(0, 10);
    const msg = applyT((templates as any)[templateKey], {
      name: data.name, time: data.time, date, scan_type: data.scan_type,
    });
    const outletCountry = outlets.find(o => o.id === outletId)?.country || 'IN';
    const waPhone = resolveWaPhone(data.phone || '', outletCountry);
    if (waPhone) {
      try {
        await sendWhatsApp(waPhone, msg, outletId);
      } catch (e: any) {
        // Scan already succeeded — a notification failure shouldn't read as a
        // scan failure to whoever is watching the scanner. Log it instead.
        postLog('ATTENDANCE_NOTIFY_FAILED', `WhatsApp notification failed for ${data.name}: ${friendlyError(e, 'unknown error')}`);
      }
    }
    if (data.email) {
      try {
        await sendEmail(
          data.email,
          `Attendance ${data.scan_type === 'IN' ? 'Check-In' : 'Check-Out'}`,
          `<div style="font-family:sans-serif;max-width:480px;margin:auto;padding:32px;background:#f8fafc;border-radius:16px"><h2 style="color:#0f172a">${data.scan_type === 'IN' ? '✅ Checked In' : '🔴 Checked Out'}</h2><p>${msg.replace(/\n/g, '<br/>')}</p></div>`,
          outletId
        );
      } catch (e: any) {
        postLog('ATTENDANCE_NOTIFY_FAILED', `Email notification failed for ${data.name}: ${friendlyError(e, 'unknown error')}`);
      }
    }
  };

  // ─── Save employee ────────────────────────────────────────────────────────
  const handleSaveEmployee = async () => {
    if (empSaving) return;
    setEmpSaving(true);
    const phoneChanged = editingEmp ? (editingEmp.phone || '') !== empForm.phone : true;
    const emailChanged = editingEmp ? (editingEmp.email || '') !== empForm.email : true;
    const needsOtp = !!editingEmp
      ? (
          empForm.name !== (editingEmp.name || '') ||
          empForm.phone !== (editingEmp.phone || '') ||
          empForm.email !== (editingEmp.email || '') ||
          empForm.department !== (editingEmp.department || '') ||
          empForm.designation !== (editingEmp.designation || '') ||
          empForm.salary_type !== (editingEmp.salary_type || '') ||
          empForm.monthly_salary !== String(editingEmp.monthly_salary || '') ||
          empForm.daily_rate !== String(editingEmp.daily_rate || '') ||
          empForm.join_date !== (editingEmp.join_date || '') ||
          empForm.role !== (editingEmp.role || '') ||
          empForm.device_employee_id !== (editingEmp.device_employee_id || '') ||
          empForm.shift_id !== String(editingEmp.shift_id || '')
        )
      : (empForm.phone || empForm.email) && (phoneChanged || emailChanged);

    if (needsOtp) {
      // Send OTP first using a temp endpoint — pass phone/email directly
      setOtpLoading(true);
      try {
        const otpR = await fetch(`${BASE}/employees/send-otp-preflight`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({
            phone: empForm.phone,
            email: empForm.email,
            name: empForm.name,
            employee_id: editingEmp?.id || null,
          }),
        });
        const otpData = await otpR.json();
        if (!otpR.ok) {
          alert(otpData?.detail || "Couldn't start verification. Please try again.");
          setOtpLoading(false); setEmpSaving(false);
          return;
        }
        const attempts: string[] = [];
        let delivered = false;
        const outletCountry = outlets.find(o => o.id === outletId)?.country || 'IN';
        const waPhone = resolveWaPhone(otpData.phone || '', outletCountry);
        if (waPhone) {
          try {
            await sendWhatsApp(waPhone, `Hi ${otpData.name}, your verification OTP is: ${otpData.otp}. Valid for 10 minutes.`, outletId);
            delivered = true;
          } catch (e: any) {
            attempts.push(`WhatsApp: ${friendlyError(e, 'failed to send')}`);
          }
        }
        if (otpData.email) {
          try {
            await sendEmail(
              otpData.email,
              'Your Verification OTP',
              `<div style="font-family:sans-serif;max-width:400px;margin:auto;padding:32px;background:#f8f9fb;border-radius:16px"><h2 style="color:#1e1b4b">Verification OTP</h2><div style="font-size:36px;font-weight:900;letter-spacing:12px;color:#6366f1;background:#fff;padding:20px;border-radius:12px;text-align:center">${otpData.otp}</div><p style="color:#6b7280;margin-top:16px">Valid for 10 minutes. Do not share this with anyone.</p></div>`,
              outletId
            );
            delivered = true;
          } catch (e: any) {
            attempts.push(`Email: ${friendlyError(e, 'failed to send')}`);
          }
        }
        if (!delivered) {
          postLog('EMPLOYEE_OTP_FAILED', `OTP send failed for ${empForm.name}${attempts.length ? ` | ${attempts.join(' | ')}` : ' | No phone or email on file'}`);
          alert(attempts.length ? `Couldn't deliver the OTP:\n${attempts.join('\n')}` : 'This employee has no phone or email to send an OTP to.');
          setOtpLoading(false); setEmpSaving(false);
          return;
        }
        postLog(
          attempts.length ? 'EMPLOYEE_OTP_PARTIAL' : 'EMPLOYEE_OTP_SENT',
          attempts.length
            ? `OTP sent but one channel failed for ${empForm.name}: ${attempts.join(' | ')}`
            : `OTP sent to ${empForm.name}${empForm.phone ? ` | Phone: ${empForm.phone}` : ''}${empForm.email ? ` | Email: ${empForm.email}` : ''}`
        );
        setPreflightOtp(otpData.otp);
        setModalPhase('otp');
      } catch (e: any) {
        postLog('EMPLOYEE_OTP_FAILED', `OTP send failed for ${empForm.name}: ${friendlyError(e, 'unknown error')}`);
        alert(friendlyError(e, "Couldn't send the verification OTP. Please try again."));
      }
      setOtpLoading(false);
      setEmpSaving(false);
    } else {
      // No OTP needed — save directly
      await commitSaveEmployee();
    }
  };

  const commitSaveEmployee = async (): Promise<number | null> => {
    const url = editingEmp ? `${BASE}/employees/${editingEmp.id}` : `${BASE}/employees/`;
    const method = editingEmp ? 'PATCH' : 'POST';
    const outletCountry = outlets.find(o => o.id === (empForm.outlet_id ? Number(empForm.outlet_id) : outletId))?.country || 'IN';
    const body = {
      name: empForm.name,
      phone: getCleanE164Phone(empForm.phone, outletCountry),
      email: empForm.email,
      department: empForm.department, designation: empForm.designation,
      shift_id: empForm.shift_id ? Number(empForm.shift_id) : null,
      salary_type: empForm.salary_type,
      monthly_salary: empForm.monthly_salary ? Number(empForm.monthly_salary) : null,
      daily_rate: empForm.daily_rate ? Number(empForm.daily_rate) : null,
      join_date: empForm.join_date || null,
      device_employee_id: empForm.device_employee_id || null,
      role: empForm.role || 'staff',
      outlet_id: empForm.outlet_id ? Number(empForm.outlet_id) : null,
    };
    let r: Response;
    try {
      r = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(body) });
    } catch {
      setEmpSaving(false);
      alert("Couldn't connect. Check your internet connection and try again.");
      return null;
    }
    const saved = await r.json().catch(() => ({}));
    if (!r.ok) {
      setEmpSaving(false);
      postLog('EMPLOYEE_SAVE_FAILED', `${editingEmp ? 'Update' : 'Add'} failed for ${body.name}: ${saved?.detail || `HTTP ${r.status}`}`);
      alert(saved?.detail || `This employee couldn't be ${editingEmp ? 'updated' : 'added'}. Please check the details and try again.`);
      return null;
    }
    const empId = saved?.id || null;
    if (empId) setSavedEmpId(empId);
    fetchEmployees();
    setModalPhase('done');
    setEmpSaving(false);
    postLog(editingEmp ? 'EMPLOYEE_UPDATED' : 'EMPLOYEE_ADDED', `${editingEmp ? 'Updated' : 'Added'} employee: ${body.name}${body.phone ? ` | Phone: ${body.phone}` : ''}${body.department ? ` | Dept: ${body.department}` : ''}`);
    return empId;
  };

  // ─── Verify OTP ───────────────────────────────────────────────────────────
  const handleVerifyOTP = async () => {
  if (!otpValue.trim()) return;
  setOtpLoading(true);
  setOtpError('');
  try {
    if (!editingEmp) {
      // New employee — verify client-side (no DB record yet)
      if (otpValue.trim() !== preflightOtp) {
        setOtpError('Invalid OTP. Try again.');
        setOtpLoading(false);
        return;
      }
      // OTP correct — save and get back the real ID
      postLog('EMPLOYEE_OTP_VERIFIED', `OTP verified for new employee: ${empForm.name}`);
      const empId = await commitSaveEmployee();
      if (empId) {
        const qrFailures: string[] = [];
        try {
          const waR = await fetch(`${BASE}/employees/${empId}/send-qr-whatsapp`, { method: 'POST', headers: authHeaders() });
          if (!waR.ok) qrFailures.push('WhatsApp');
        } catch {
          qrFailures.push('WhatsApp');
        }
        const emailTo = empForm.email || employees.find(e => e.id === empId)?.email;
        if (emailTo) {
          try {
            const qrR2 = await fetch(`${BASE}/employees/${empId}/qr`, { headers: authHeaders() });
            const qrData2 = await qrR2.json();
            await sendEmail(
              emailTo,
              `Your Employee QR Badge — ${empForm.name}`,
              `<div style="font-family:sans-serif;max-width:480px;margin:auto;padding:32px;background:#f8fafc;border-radius:16px"><h2 style="color:#0f172a">👔 Employee QR Badge</h2><p>Hi <strong>${empForm.name}</strong>! Your attendance QR badge is below. Show this at the entrance to check in/out.</p><div style="text-align:center;margin:20px 0"><img src="${qrData2.qr_image || ''}" style="width:200px;height:200px" /></div><p style="color:#6b7280;font-size:12px">— ${companyName || authUser?.name || 'Our Company'}</p></div>`,
              outletId
            );
          } catch (e: any) {
            qrFailures.push(`Email (${friendlyError(e, 'failed to send')})`);
          }
        }
        if (qrFailures.length) {
          postLog('EMPLOYEE_QR_DELIVERY_FAILED', `QR badge delivery failed for ${empForm.name}: ${qrFailures.join(', ')}`);
          alert(`Employee verified, but the QR badge couldn't be delivered via ${qrFailures.join(' or ')}. You can resend it anytime from the ⋯ menu next to their name.`);
        }
      }
    } else {
      // Editing employee — verify via API
      if (!savedEmpId) { setOtpError('Something went wrong.'); setOtpLoading(false); return; }
      const r = await fetch(`${BASE}/employees/verify-otp`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ employee_id: savedEmpId, otp: otpValue.trim() }),
      });
      if (r.ok) {
        postLog('EMPLOYEE_OTP_VERIFIED', `OTP verified for employee update: ${editingEmp?.name}`);
        await commitSaveEmployee();
        try {
          const waR = await fetch(`${BASE}/employees/${savedEmpId}/send-qr-whatsapp`, { method: 'POST', headers: authHeaders() });
          if (!waR.ok) {
            postLog('EMPLOYEE_QR_DELIVERY_FAILED', `QR badge resend failed for ${editingEmp?.name}`);
            alert("Employee updated, but the QR badge couldn't be re-sent via WhatsApp. You can retry from the ⋯ menu.");
          }
        } catch {
          postLog('EMPLOYEE_QR_DELIVERY_FAILED', `QR badge resend failed for ${editingEmp?.name}`);
          alert("Employee updated, but the QR badge couldn't be re-sent via WhatsApp. You can retry from the ⋯ menu.");
        }
      } else {
        setOtpError('Invalid or expired OTP. Try again.');
      }
    }
  } catch {
    setOtpError('Something went wrong. Try again.');
  }
  setOtpLoading(false);
};
  const handleResendOTP = async () => {
    if (!savedEmpId) return;
    setOtpLoading(true);
    setOtpError('');
    try {
      const otpR = await fetch(`${BASE}/employees/send-otp`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ employee_id: savedEmpId }),
      });
      const otpData = await otpR.json();
      if (!otpR.ok) {
        setOtpError(otpData?.detail || "Couldn't resend the OTP. Please try again.");
        setOtpLoading(false);
        return;
      }
      const attempts: string[] = [];
      let delivered = false;
      const outletCountry = outlets.find(o => o.id === outletId)?.country || 'IN';
      const waPhone = resolveWaPhone(otpData.phone || '', outletCountry);
      if (waPhone) {
        try {
          await sendWhatsApp(waPhone, `Hi ${otpData.name}, your verification OTP is: ${otpData.otp}. Valid for 10 minutes.`, outletId);
          delivered = true;
        } catch (e: any) {
          attempts.push(`WhatsApp: ${friendlyError(e, 'failed to send')}`);
        }
      }
      if (otpData.email) {
        try {
          await sendEmail(
            otpData.email,
            'Your Verification OTP',
            `<div style="font-family:sans-serif;max-width:400px;margin:auto;padding:32px;background:#f8f9fb;border-radius:16px"><h2 style="color:#1e1b4b">Verification OTP</h2><div style="font-size:36px;font-weight:900;letter-spacing:12px;color:#6366f1;background:#fff;padding:20px;border-radius:12px;text-align:center">${otpData.otp}</div><p style="color:#6b7280;margin-top:16px">Valid for 10 minutes. Do not share this with anyone.</p></div>`,
            outletId
          );
          delivered = true;
        } catch (e: any) {
          attempts.push(`Email: ${friendlyError(e, 'failed to send')}`);
        }
      }
      if (!delivered) {
        setOtpError(attempts.length ? `Couldn't resend the OTP — ${attempts.join(' · ')}` : 'This employee has no phone or email on file.');
      }
    } catch (e: any) {
      setOtpError(friendlyError(e, "Couldn't resend the OTP. Please try again."));
    }
    setOtpLoading(false);
  };

  // ─── Delete biometric ─────────────────────────────────────────────────────
  const handleDeleteFace = async (empId: number) => {
    if (!window.confirm('Remove face registration for this employee?')) return;
    const emp = employees.find(e => e.id === empId);
    let r: Response;
    try {
      r = await fetch(`${BASE}/employees/${empId}/face`, { method: 'DELETE', headers: authHeaders() });
    } catch {
      alert("Couldn't connect. Check your internet connection and try again.");
      return;
    }
    if (!r.ok) {
      const data = await r.json().catch(() => ({}));
      alert(data?.detail || "Face registration couldn't be removed. Please try again.");
      return;
    }
    postLog('BIOMETRIC_FACE_REMOVED', `Face registration removed for: ${emp?.name || `#${empId}`}`);
    fetchEmployees();
  };

  const handleDeleteWebAuthn = async (empId: number) => {
    if (!window.confirm('Remove fingerprint/WebAuthn enrollment for this employee?')) return;
    const emp = employees.find(e => e.id === empId);
    let r: Response;
    try {
      r = await fetch(`${BASE}/employees/${empId}/webauthn`, { method: 'DELETE', headers: authHeaders() });
    } catch {
      alert("Couldn't connect. Check your internet connection and try again.");
      return;
    }
    if (!r.ok) {
      const data = await r.json().catch(() => ({}));
      alert(data?.detail || "Fingerprint enrollment couldn't be removed. Please try again.");
      return;
    }
    postLog('BIOMETRIC_FP_REMOVED', `Fingerprint enrollment removed for: ${emp?.name || `#${empId}`}`);
    fetchEmployees();
  };

  // ─── Face functions ───────────────────────────────────────────────────────
  const stopFaceEmpStream = () => {
    clearInterval(faceEmpScanIntervalRef.current);
    faceEmpStreamRef.current?.getTracks().forEach(t => t.stop());
    faceEmpStreamRef.current = null;
  };
  // 🔒 Anti-Fraud Biometric Security Handler
  const handleEmpBiometricGuard = async (emp: Employee, action: 'face' | 'fingerprint' | 'delete_face' | 'delete_fp') => {
    const isLocked = action.includes('face')
      ? !!(emp.face_enrolled)
      : !!(emp.webauthn_enrolled);

    if (isLocked) {
      // 🔒 Locked Biometric -> Require 6-digit OTP before allowing re-enrollment or changes
      setEmpBioTarget(emp);
      setEmpBioActionType(action);
      setEmpBioOtpInput('');
      setShowEmpBioOtpModal(true);
      try {
        setEmpBioOtpLoading(true);
        const r = await fetch(`${BASE}/employees/${emp.id}/request-bio-otp`, { method: 'POST', headers: authHeaders() });
        const data = await r.json();
        const otpCode = data.debug_otp || data.otp || '123456';

        // 1. Send via WhatsApp
        const outletCountry = outlets.find(o => o.id === outletId)?.country || 'IN';
        const waPhone = resolveWaPhone(emp.phone || data.phone || '', outletCountry);
        if (waPhone) {
          try {
            await sendWhatsApp(
              waPhone, 
              `🔐 *Biometric Security OTP*\n\nHi ${emp.name}! Your verification OTP to unlock and change your biometrics is: *${otpCode}*\n\nValid for 10 minutes.\n— ${companyName || authUser?.name || 'Our Company'}`, 
              outletId
            );
          } catch (waErr) {
            console.warn('WhatsApp send failed:', waErr);
          }
        }

        // 2. Send via Email
        const targetEmail = emp.email || data.email;
        if (targetEmail) {
          try {
            await sendEmail(
              targetEmail,
              `Biometric Security Verification OTP — ${emp.name}`,
              `<div style="font-family:sans-serif;max-width:440px;margin:auto;padding:32px;background:#f8fafc;border-radius:16px;border:1px solid #e2e8f0">
                <h2 style="color:#0f172a;margin-bottom:6px">🔒 Biometric Security OTP</h2>
                <p>Hi <strong>${emp.name}</strong>,</p>
                <p style="color:#64748b;font-size:13px">Use the verification code below to authorize modifying your face or fingerprint biometrics:</p>
                <div style="font-size:36px;font-weight:900;letter-spacing:10px;color:#6366f1;background:#fff;padding:18px;border-radius:12px;text-align:center;margin:20px 0;border:1.5px solid #e2e8f0">
                  ${otpCode}
                </div>
                <p style="color:#94a3b8;font-size:12px;margin-top:16px">Valid for 10 minutes. If you did not request this, contact your administrator immediately.</p>
              </div>`,
              outletId
            );
          } catch (emailErr) {
            console.warn('Email send failed:', emailErr);
          }
        }

        console.log(`[Employee OTP]: ${otpCode}`);
      } catch (err) {
        console.warn('Failed to request OTP', err);
      } finally {
        setEmpBioOtpLoading(false);
      }
    } else {
      // First-time enrollment
      if (action === 'face') {
        setShowAddEmp(false);
        startFaceRegisterEmp(emp);
      } else if (action === 'fingerprint') {
        setBioEmpId(String(emp.id));
        setSavedEmpId(emp.id);
        setEditingEmp(emp);
        setModalPhase('done');
        setBioStatus('idle');
        setBioMessage('');
        setShowAddEmp(true);
      }
    }
  };

  const verifyEmpBioOtpAndProceed = async () => {
    if (!empBioTarget || !empBioOtpInput.trim()) return;
    setEmpBioOtpLoading(true);
    try {
      const r = await fetch(`${BASE}/employees/${empBioTarget.id}/verify-bio-otp`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ otp: empBioOtpInput.trim() }),
      });
      if (!r.ok) {
        alert('Invalid or expired Security OTP.');
        setEmpBioOtpLoading(false);
        return;
      }

      const target = empBioTarget;
      const act = empBioActionType;
      setShowEmpBioOtpModal(false);
      setEmpBioOtpInput('');
      setEmpBioTarget(null);
      setEmpBioActionType(null);

      if (act === 'face') {
        setShowAddEmp(false);
        startFaceRegisterEmp(target);
      } else if (act === 'fingerprint') {
        setBioEmpId(String(target.id));
        setSavedEmpId(target.id);
        setEditingEmp(target);
        setModalPhase('done');
        setBioStatus('idle');
        setBioMessage('');
        setShowAddEmp(true);
      } else if (act === 'delete_face') {
        await fetch(`${BASE}/employees/${target.id}/face`, { method: 'DELETE', headers: authHeaders() });
        postLog('BIOMETRIC_FACE_REMOVED', `Face registration removed for: ${target.name}`);
        fetchEmployees();
      } else if (act === 'delete_fp') {
        await fetch(`${BASE}/employees/${target.id}/webauthn`, { method: 'DELETE', headers: authHeaders() });
        postLog('BIOMETRIC_FP_REMOVED', `Fingerprint removed for: ${target.name}`);
        fetchEmployees();
      }
    } catch (e: any) {
      alert('Verification failed.');
    } finally {
      setEmpBioOtpLoading(false);
    }
  };

  const startEmpPhoneEnrollment = async (empId: number) => {
    try {
      const r = await fetch(`${BASE}/employees/${empId}/create-phone-enroll-session`, {
        method: 'POST', headers: authHeaders(),
      });
      const data = await r.json();
      if (!r.ok || !data.token) {
        alert(data.detail || 'Could not create phone enrollment session.');
        return;
      }

      const QRCode = (await import('qrcode')).default;
      const enrollUrl = `${window.location.origin}/remote-enroll?token=${data.token}&type=employee`;
      const qr = await QRCode.toDataURL(enrollUrl, { width: 220, margin: 1 });

      setEmpPhoneEnrollQR(qr);
      setWaitingEmpPhoneBio(true);

      if (empPhoneBioPollRef.current) clearInterval(empPhoneBioPollRef.current);
      empPhoneBioPollRef.current = setInterval(async () => {
        try {
          // Poll employee endpoint
          let checkR = await fetch(`${BASE}/employees/remote-enroll/${data.token}`);
          let check = await checkR.json().catch(() => ({}));
          
          if (check.status === 'completed' || check.status === 'success') {
            if (empPhoneBioPollRef.current) {
              clearInterval(empPhoneBioPollRef.current);
              empPhoneBioPollRef.current = null;
            }
            
            // ⚡ Instantly clear QR and flip UI to green check!
            setWaitingEmpPhoneBio(false);
            setEmpPhoneEnrollQR('');
            setBioStatus('success');
            setBioMessage('Fingerprint registered from phone! 🎉');

            // ⚡ Update local state immediately so modal flips without waiting for network
            setEmployees(prev => prev.map(e => e.id === empId ? { ...e, webauthn_enrolled: true, webauthn_credential_id: 'enrolled' } : e));
            setEditingEmp(prev => prev ? { ...prev, webauthn_enrolled: true, webauthn_credential_id: 'enrolled' } : prev);
            
            await fetchEmployees();
          }
        } catch (err) {
          // Keep polling; do not abort on transient network hiccup
        }
      }, 1200);
    } catch (e: any) {
      alert(e.message || 'Failed to start phone enrollment');
    }
  };
const startRemotePhonePunch = async () => {
    setShowRemotePunchModal(true);
    setRemotePunchQR('');
    try {
      const q = outletId ? `?outlet_id=${outletId}` : '';
      const r = await fetch(`${BASE}/employees/attendance/create-phone-punch-session${q}`, {
        method: 'POST', headers: authHeaders(),
      });
      const data = await r.json();
      if (!r.ok || !data.session_id) {
        alert('Could not start attendance punch session.');
        setShowRemotePunchModal(false);
        return;
      }

      const QRCode = (await import('qrcode')).default;
      const punchUrl = `${window.location.origin}/remote-attendance?session=${data.session_id}`;
      const qr = await QRCode.toDataURL(punchUrl, { width: 220, margin: 1 });

      setRemotePunchQR(qr);

      if (remotePunchPollRef.current) clearInterval(remotePunchPollRef.current);
      remotePunchPollRef.current = setInterval(async () => {
        try {
          const checkR = await fetch(`${BASE}/employees/attendance/remote-punch-session/${data.session_id}`);
          const check = await checkR.json();
          if (check.status === 'completed' && check.result) {
            clearInterval(remotePunchPollRef.current);
            setShowRemotePunchModal(false);
            setRemotePunchQR('');
            showScanResult(check.result);
            fetchTodayAtt();
            await fireAttendanceNotification(check.result);
          }
        } catch {
          clearInterval(remotePunchPollRef.current);
        }
      }, 1500);
    } catch (e: any) {
      alert(e.message || 'Failed to generate punch QR');
      setShowRemotePunchModal(false);
    }
  };
  const startFaceRegisterEmp = async (emp: Employee) => {
    setFaceRegisterEmpId(emp.id);
    setFaceRegisterEmpName(emp.name);
    setFaceRegisterEmpStatus('loading');
    setShowFaceRegisterEmp(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
      faceEmpStreamRef.current = stream;
      setTimeout(() => {
        if (faceEmpVideoRef.current) {
          faceEmpVideoRef.current.srcObject = stream;
          setFaceRegisterEmpStatus('capturing');
        }
      }, 300);
    } catch {
      setFaceRegisterEmpStatus('error');
    }
  };

  const captureFaceEmp = async () => {
    const faceapi = (window as any).faceapi;
    if (!faceapi || !faceEmpVideoRef.current || !faceRegisterEmpId) return;
    setFaceRegisterEmpStatus('loading');
    try {
      const detection = await faceapi
        .detectSingleFace(faceEmpVideoRef.current, new faceapi.TinyFaceDetectorOptions())
        .withFaceLandmarks()
        .withFaceDescriptor();
      if (!detection) {
        setFaceRegisterEmpStatus('capturing');
        return;
      }
      const descriptor = Array.from(detection.descriptor);

      // ⚡ Grab a quick canvas snapshot from the webcam stream for Mobile AI
      let image_base64: string | null = null;
      if (faceEmpVideoRef.current) {
        const canvas = document.createElement('canvas');
        canvas.width = faceEmpVideoRef.current.videoWidth || 640;
        canvas.height = faceEmpVideoRef.current.videoHeight || 480;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(faceEmpVideoRef.current, 0, 0, canvas.width, canvas.height);
        image_base64 = canvas.toDataURL('image/jpeg', 0.75);
      }

      // ⚡ Send BOTH in 1 request to populate BOTH database columns!
      const res = await fetch(`${BASE}/employees/${faceRegisterEmpId}/face`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({
          face_descriptor: descriptor,
          image_base64: image_base64,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        alert(errData.detail || 'Face registration failed');
        setFaceRegisterEmpStatus('error');
        return;
      }

      stopFaceEmpStream();
      postLog('BIOMETRIC_FACE_REGISTERED', `Face registered for employee: ${faceRegisterEmpName}`);
      setFaceRegisterEmpStatus('done');
      fetchEmployees();
    } catch {
      setFaceRegisterEmpStatus('error');
    }
  };

  const startFaceScanEmp = async () => {
    setFaceScanEmpResult(null);
    setFaceScanEmpStatus('scanning');
    setShowFaceScanEmp(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      faceEmpStreamRef.current = stream;
      setTimeout(() => {
        if (faceEmpVideoRef.current) faceEmpVideoRef.current.srcObject = stream;
      }, 300);
      const faceapi = (window as any).faceapi;
      if (!faceapi) return;
      faceEmpScanIntervalRef.current = setInterval(async () => {
        const v = faceEmpVideoRef.current;
        if (!v || v.readyState < 2) return;
        const detection = await faceapi
          .detectSingleFace(v, new faceapi.TinyFaceDetectorOptions())
          .withFaceLandmarks()
          .withFaceDescriptor();
        if (!detection) return;
        clearInterval(faceEmpScanIntervalRef.current);
        stopFaceEmpStream();
        const descriptor = Array.from(detection.descriptor);
        try {
          const r = await fetch(`${BASE}/employees/attendance/face-scan`, {
            method: 'POST', headers: authHeaders(),
            body: JSON.stringify({ descriptor, outlet_id: outletId }),
          });
          const data = await r.json();
          if (!r.ok) {
            setFaceScanEmpResult({ status: 'error', message: data.detail || 'Scan failed' });
            setFaceScanEmpStatus('error');
            return;
          }
          setFaceScanEmpResult(data);
          setFaceScanEmpStatus(data.status === 'success' ? 'match' : 'nomatch');
          if (data.status === 'success') {
            postLog('ATTENDANCE_FACE', `Face scan: ${data.name} checked ${data.scan_type} at ${data.time}`);
            fetchTodayAtt();
            await fireAttendanceNotification(data);
          } else {
            postLog('ATTENDANCE_FACE_FAILED', `Face scan failed: no match found`);
          }
        } catch {
          setFaceScanEmpStatus('error');
        }
      }, 800);
    } catch {
      setFaceScanEmpStatus('error');
      setFaceScanEmpResult({ status: 'error', message: 'Camera access denied' });
    }
  };

  // ─── WebAuthn ─────────────────────────────────────────────────────────────
  const handleWebAuthnEnroll = async (empId?: number) => {
    const id = empId || Number(bioEmpId);
    if (!id) { alert('Select employee first'); return; }
    setBioStatus('enrolling');
    setBioMessage('');
    try {
      const challenge = new Uint8Array(32);
      crypto.getRandomValues(challenge);
      const emp = employees.find(e => e.id === id);
      const credential = await navigator.credentials.create({
        publicKey: {
          challenge,
          rp: { name: 'Employee Attendance', id: window.location.hostname },
          user: {
            id: new TextEncoder().encode(String(id)),
            name: emp?.name || `emp_${id}`,
            displayName: emp?.name || `Employee ${id}`,
          },
          pubKeyCredParams: [{ alg: -7, type: 'public-key' }],
          timeout: 60000, attestation: 'none',
        },
      }) as PublicKeyCredential;
      const credId = btoa(String.fromCharCode(...new Uint8Array(credential.rawId)));
      const response = credential.response as AuthenticatorAttestationResponse;
      const pubKey = btoa(String.fromCharCode(...new Uint8Array(response.attestationObject)));
      const r = await fetch(`${BASE}/employees/${id}/webauthn/register`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ credential_id: credId, public_key: pubKey }),
      });
      if (r.ok) { 
        const emp = employees.find(e => e.id === id);
        postLog('BIOMETRIC_FP_ENROLLED', `Fingerprint enrolled for: ${emp?.name || `#${id}`}`);
        setBioStatus('success'); setBioMessage('Fingerprint enrolled!'); fetchEmployees(); 
      }
      else { setBioStatus('error'); setBioMessage('Enrollment failed.'); }
    } catch (e: any) {
      setBioStatus('error');
      setBioMessage(e.name === 'NotAllowedError' ? 'Permission denied or timed out.' : e.message);
    }
  };

  const handleWebAuthnScan = async () => {
    setBioStatus('scanning');
    setBioMessage('');
    try {
      const challenge = new Uint8Array(32);
      crypto.getRandomValues(challenge);
      const assertion = await navigator.credentials.get({
        publicKey: { challenge, timeout: 60000, userVerification: 'required' },
      }) as PublicKeyCredential;
      const credId = btoa(String.fromCharCode(...new Uint8Array(assertion.rawId)));
      const r = await fetch(`${BASE}/employees/attendance/webauthn`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ credential_id: credId, outlet_id: outletId }),
      });
      if (r.ok) {
        const data = await r.json();
        postLog('ATTENDANCE_FINGERPRINT', `Fingerprint scan: ${data.name} checked ${data.scan_type} at ${data.time}`);
        setBioStatus('success');
        setBioMessage(`${data.name} checked ${data.scan_type} at ${data.time}`);
        fetchTodayAtt();
        await fireAttendanceNotification(data);
      } else {
        postLog('ATTENDANCE_FINGERPRINT_FAILED', `Fingerprint scan failed: no employee matched`);
        setBioStatus('error');
        setBioMessage('No employee found for this biometric.');
      }
    } catch (e: any) {
      setBioStatus('error');
      setBioMessage(e.name === 'NotAllowedError' ? 'Permission denied or timed out.' : e.message);
    }
  };



  const handleDeleteEmployee = async (id: number) => {
    if (!window.confirm('Permanently delete this employee? This cannot be undone.')) return;
    const emp = employees.find(e => e.id === id);
    let r: Response;
    try {
      r = await fetch(`${BASE}/employees/${id}/permanent`, { method: 'DELETE', headers: authHeaders() });
    } catch {
      alert("Couldn't connect. Check your internet connection and try again.");
      return;
    }
    if (!r.ok) {
      const data = await r.json().catch(() => ({}));
      alert(data?.detail || `${emp?.name || 'This employee'} couldn't be deleted. Please try again.`);
      return;
    }
    postLog('EMPLOYEE_DELETED', `Permanently deleted employee: ${emp?.name || `#${id}`}`);
    fetchEmployees();
  };

  const handleSaveShift = async () => {
    let r: Response;
    try {
      r = await fetch(`${BASE}/employees/shifts`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(shiftForm),
      });
    } catch {
      alert("Couldn't connect. Check your internet connection and try again.");
      return;
    }
    const saved = await r.json().catch(() => ({}));
    if (!r.ok) {
      alert(saved?.detail || "This shift couldn't be saved. Please check the details and try again.");
      return;
    }
    postLog('SHIFT_ADDED', `Shift added: ${shiftForm.name} | ${shiftForm.start_time}–${shiftForm.end_time}`);
    setShowAddShift(false);
    setShiftForm({ name:'', start_time:'', end_time:'', outlet_id: outletId ? String(outletId) : '' });
    await fetchShifts();
    if (saved?.id) setEmpForm(p => ({ ...p, shift_id: String(saved.id) }));
  };

  const handleSaveRole = async () => {
    let r: Response;
    try {
      r = await fetch(`${BASE}/employees/roles`, { method:'POST', headers: authHeaders(), body: JSON.stringify(roleForm) });
    } catch {
      alert("Couldn't connect. Check your internet connection and try again.");
      return;
    }
    const saved = await r.json().catch(() => ({}));
    if (!r.ok) {
      alert(saved?.detail || "This role couldn't be saved. Please check the details and try again.");
      return;
    }
    postLog('ROLE_ADDED', `Role added: ${roleForm.name}`);
    setShowAddRole(false); setRoleForm({ name:'', outlet_id: outletId ? String(outletId) : '' });
    await fetchRoles();
    if (saved?.id) setEmpForm(p => ({ ...p, role: saved.name }));
  };

  const handleSaveSalaryType = async () => {
    let r: Response;
    try {
      r = await fetch(`${BASE}/employees/salary-types`, { method:'POST', headers: authHeaders(), body: JSON.stringify(salaryTypeForm) });
    } catch {
      alert("Couldn't connect. Check your internet connection and try again.");
      return;
    }
    const saved = await r.json().catch(() => ({}));
    if (!r.ok) {
      alert(saved?.detail || "This salary type couldn't be saved. Please check the details and try again.");
      return;
    }
    postLog('SALARY_TYPE_ADDED', `Salary type added: ${salaryTypeForm.name} | ${salaryTypeForm.calc_type}`);
    setShowAddSalaryType(false); setSalaryTypeForm({ name:'', calc_type:'fixed', outlet_id: outletId ? String(outletId) : '' });
    await fetchSalaryTypes();
    if (saved?.id) setEmpForm(p => ({ ...p, salary_type: saved.name }));
  };

  const handleSaveDept = async () => {
    let r: Response;
    try {
      r = await fetch(`${BASE}/employees/departments`, { method:'POST', headers: authHeaders(), body: JSON.stringify(deptForm) });
    } catch {
      alert("Couldn't connect. Check your internet connection and try again.");
      return;
    }
    const saved = await r.json().catch(() => ({}));
    if (!r.ok) {
      alert(saved?.detail || "This department couldn't be saved. Please check the details and try again.");
      return;
    }
    postLog('DEPARTMENT_ADDED', `Department added: ${deptForm.name}`);
    setShowAddDept(false); setDeptForm({ name:'', outlet_id: outletId ? String(outletId) : '' });
    await fetchDepartments();
    if (saved?.id) setEmpForm(p => ({ ...p, department: saved.name }));
  };

  const handleSaveDesignation = async () => {
    let r: Response;
    try {
      r = await fetch(`${BASE}/employees/designations`, { method:'POST', headers: authHeaders(), body: JSON.stringify(designationForm) });
    } catch {
      alert("Couldn't connect. Check your internet connection and try again.");
      return;
    }
    const saved = await r.json().catch(() => ({}));
    if (!r.ok) {
      alert(saved?.detail || "This designation couldn't be saved. Please check the details and try again.");
      return;
    }
    postLog('DESIGNATION_ADDED', `Designation added: ${designationForm.name}`);
    setShowAddDesignation(false); setDesignationForm({ name:'', outlet_id: outletId ? String(outletId) : '' });
    await fetchDesignations();
    if (saved?.id) setEmpForm(p => ({ ...p, designation: saved.name }));
  };

  const handleSaveDeviceId = async () => {
    let r: Response;
    try {
      r = await fetch(`${BASE}/employees/device-ids`, { method:'POST', headers: authHeaders(), body: JSON.stringify(deviceIdForm) });
    } catch {
      alert("Couldn't connect. Check your internet connection and try again.");
      return;
    }
    const saved = await r.json().catch(() => ({}));
    if (!r.ok) {
      alert(saved?.detail || "This device ID couldn't be saved. Please check the details and try again.");
      return;
    }
    postLog('DEVICE_ID_ADDED', `Device ID added: ${deviceIdForm.name}`);
    setShowAddDeviceId(false); setDeviceIdForm({ name:'', outlet_id: outletId ? String(outletId) : '' });
    await fetchDeviceIds();
    if (saved?.id) setEmpForm(p => ({ ...p, device_employee_id: saved.name }));
  };

  // ─── Native Excel (.xlsx) Engine Builder ─────────────────────────────────
  const generateExcelFile = async (sheets: { name: string; data: (string | number)[][] }[], filename: string) => {
    let XLSX: any = (window as any).XLSX;

    // 1. Try local module import first
    if (!XLSX || !XLSX.utils || !XLSX.utils.book_new) {
      try {
        const mod: any = await import('xlsx');
        XLSX = mod?.utils ? mod : mod?.default;
      } catch {}
    }

    // 2. Load from CDN if not already loaded in the window
    if (!XLSX || !XLSX.utils || !XLSX.utils.book_new) {
      await new Promise<void>((resolve) => {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
        script.onload = () => resolve();
        script.onerror = () => {
          const s2 = document.createElement('script');
          s2.src = 'https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js';
          s2.onload = () => resolve();
          s2.onerror = () => resolve();
          document.head.appendChild(s2);
        };
        document.head.appendChild(script);
      });
      XLSX = (window as any).XLSX;
    }

    // 3. Build native Excel workbook (.xlsx)
    if (XLSX && XLSX.utils && XLSX.utils.book_new) {
      const wb = XLSX.utils.book_new();

      sheets.forEach(({ name, data }) => {
        const ws = XLSX.utils.aoa_to_sheet(data);
        const maxCols = data.reduce((max, row) => Math.max(max, row.length), 0);
        const colWidths = [];
        for (let c = 0; c < maxCols; c++) {
          let maxLen = 12;
          data.forEach(row => {
            const val = row[c] != null ? String(row[c]) : '';
            if (val.length > maxLen) maxLen = Math.min(val.length + 3, 60);
          });
          colWidths.push({ wch: maxLen });
        }
        ws['!cols'] = colWidths;
        XLSX.utils.book_append_sheet(wb, ws, name.slice(0, 31));
      });

      XLSX.writeFile(wb, filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`);
    } else {
      // 4. Zero-fail fallback
      const mainSheet = sheets[0]?.data || [];
      const csvContent = '\uFEFF' + mainSheet.map(row =>
        row.map(cell => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(',')
      ).join('\r\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename.replace(/\.xlsx$/i, '') + '.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  // ─── All-Employees Range Report in Excel (.xlsx) ───────────────────────────
  const handleExportAttendance = async () => {
    setExportingToday(true);
    try {
      const getSourceLabel = (src: string = '') => {
        const s = src.toLowerCase();
        if (s.includes('face')) return 'Face ID';
        if (s.includes('finger') || s.includes('webauthn')) return 'Fingerprint';
        if (s.includes('qr') || s.includes('kiosk')) return 'QR Badge';
        if (s.includes('device') || s.includes('zkteco')) return 'Machine DEV';
        return 'Manual';
      };

      const tableRows = await Promise.all(
        todayAtt.map(async (rec) => {
          const emp = employees.find(e => e.id === rec.employee_id);
          const outletName = outlets.find(o => o.id === rec.outlet_id)?.name || (rec.outlet_id ? `Outlet #${rec.outlet_id}` : 'Unassigned');

          let firstIn = rec.check_in || '—';
          let lastOut = rec.check_out || '—';
          let totalMins = 0;
          let inCount = rec.check_in ? 1 : 0;
          let outCount = rec.check_out ? 1 : 0;
          let missedOutCount = 0;
          let punchStatus = 'Completed';
          let timelineParts: string[] = [];

          try {
            const r = await fetch(`${BASE}/employees/${rec.employee_id}/logs?date=${rec.date}`, { headers: authHeaders() });
            const data = await r.json();
            const logs: any[] = (data.logs || []).filter((l: any) => l.date === rec.date);
            logs.sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time));

            if (logs.length > 0) {
              inCount = logs.filter(l => l.scan_type === 'IN').length;
              outCount = logs.filter(l => l.scan_type === 'OUT').length;
              firstIn = logs.find(l => l.scan_type === 'IN')?.time || firstIn;
              lastOut = [...logs].reverse().find(l => l.scan_type === 'OUT')?.time || (rec.date === localDateStr() ? 'Working Now' : 'No Out Punch');

              let curIn: any = null;
              logs.forEach(l => {
                if (l.scan_type === 'IN') {
                  if (curIn) {
                    missedOutCount++;
                    timelineParts.push(`[IN: ${curIn.time} ➔ ⚠️ Skipped OUT]`);
                  }
                  curIn = l;
                } else if (l.scan_type === 'OUT') {
                  if (curIn) {
                    const dur = Math.max(0, timeToMinutes(l.time) - timeToMinutes(curIn.time));
                    totalMins += dur;
                    timelineParts.push(`[IN: ${curIn.time} ➔ OUT: ${l.time} (${formatMinutes(dur)})]`);
                    curIn = null;
                  } else {
                    missedOutCount++;
                    timelineParts.push(`[⚠️ Skipped IN ➔ OUT: ${l.time}]`);
                  }
                }
              });

              if (curIn) {
                if (rec.date === localDateStr()) {
                  punchStatus = '🟢 Working Now';
                  totalMins += Math.max(0, timeToMinutes(new Date().toTimeString().slice(0, 5)) - timeToMinutes(curIn.time));
                  timelineParts.push(`[IN: ${curIn.time} ➔ 🟢 Working Now]`);
                } else {
                  missedOutCount++;
                  punchStatus = '⚠️ Incomplete';
                  timelineParts.push(`[IN: ${curIn.time} ➔ ⚠️ Skipped OUT]`);
                }
              }
            } else if (rec.check_in && rec.check_out) {
              totalMins = Math.max(0, timeToMinutes(rec.check_out) - timeToMinutes(rec.check_in));
              timelineParts.push(`[IN: ${rec.check_in} ➔ OUT: ${rec.check_out}]`);
            }
          } catch {
            if (rec.check_in && rec.check_out) {
              totalMins = Math.max(0, timeToMinutes(rec.check_out) - timeToMinutes(rec.check_in));
            }
          }

          if (missedOutCount > 0 && punchStatus !== '🟢 Working Now') {
            punchStatus = `⚠️ ${missedOutCount} Missed Out`;
          }

          return [
            `#${rec.employee_id}`,
            rec.employee_name || emp?.name || `Employee #${rec.employee_id}`,
            emp?.department || '—',
            emp?.designation || '—',
            rec.date,
            firstIn,
            lastOut,
            formatMinutes(totalMins),
            inCount,
            outCount,
            missedOutCount > 0 ? `${missedOutCount} Missed Out` : '0',
            punchStatus,
            timelineParts.length > 0 ? timelineParts.join(' | ') : '—',
            (rec.status || 'present').toUpperCase(),
            (rec.source ? getSourceLabel(rec.source) : 'Manual').toUpperCase(),
            outletName,
          ];
        })
      );

      const fileDate = attStartDate === attEndDate ? attStartDate : `${attStartDate}_to_${attEndDate}`;

      const sheetData: (string | number)[][] = [
        ['COMPANY ATTENDANCE REPORT'],
        ['Date Range:', `${attStartDate} to ${attEndDate}`],
        ['Total Records:', tableRows.length],
        ['Generated On:', new Date().toLocaleString()],
        [],
        [
          'Employee ID',
          'Employee Name',
          'Department',
          'Designation',
          'Date',
          'First Check In',
          'Last Check Out',
          'Total Duration',
          'Check-Ins Count',
          'Check-Outs Count',
          'Missed Out Punches',
          'Punch Status',
          'Complete Punch Timeline Breakdown',
          'Attendance Status',
          'Primary Source',
          'Branch / Outlet',
        ],
        ...tableRows,
      ];

      await generateExcelFile(
        [{ name: 'Attendance Report', data: sheetData }],
        `Attendance_Report_${fileDate}.xlsx`
      );

      postLog('ATTENDANCE_EXPORTED', `Exported all-employee attendance Excel for ${fileDate} (${tableRows.length} rows)`);
    } catch {
      alert('Failed to generate Excel report. Please try again.');
    } finally {
      setExportingToday(false);
    }
  };

  // ─── Forensic Legal & Audit Breakdown Monthly Excel (.xlsx) ───────────────
  const handleExportMonth = async () => {
    if (!calEmpId) { alert('Select an employee first'); return; }
    setExportingMonth(true);

    try {
      const emp = employees.find(e => e.id === calEmpId);
      const empName = emp?.name || `Employee #${calEmpId}`;
      const monthName = MONTHS[calMonth - 1];

      // 1. Fetch all detailed logs for this employee
      let allLogs: any[] = [];
      try {
        const r = await fetch(`${BASE}/employees/${calEmpId}/logs`, { headers: authHeaders() });
        const data = await r.json();
        allLogs = data.logs || [];
      } catch {}

      // 2. Fetch monthly attendance summary records
      let monthMap: Record<string, AttendanceRecord> = {};
      try {
        const q = outletId ? `&outlet_id=${outletId}` : '';
        const r = await fetch(`${BASE}/employees/attendance/by-employee/${calEmpId}/month?month=${calMonth}&year=${calYear}${q}`, { headers: authHeaders() });
        const data: AttendanceRecord[] = await r.json();
        if (Array.isArray(data)) {
          data.forEach(rec => { monthMap[rec.date] = rec; });
        }
      } catch {}

      const totalDays = getDaysInMonth(calMonth, calYear);
      let monthlyTotalMins = 0;
      let monthlyPresentDays = 0;
      let monthlySkippedPunches = 0;

      const getSourceLabel = (src: string = '') => {
        const s = src.toLowerCase();
        if (s.includes('face')) return 'Face Recognition';
        if (s.includes('finger') || s.includes('webauthn')) return 'Fingerprint Biometric';
        if (s.includes('qr') || s.includes('kiosk')) return 'QR Badge Kiosk';
        if (s.includes('device') || s.includes('zkteco')) return 'ZKTeco DEV Machine';
        return 'Manual Admin';
      };

      const dailyRows: (string | number)[][] = [];
      const rawAuditLogsRows: (string | number)[][] = [];

      // 3. Loop through every calendar day
      for (let day = 1; day <= totalDays; day++) {
        const dateStr = `${calYear}-${String(calMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const dayDate = new Date(calYear, calMonth - 1, day);
        const dayName = dayDate.toLocaleDateString('en-US', { weekday: 'long' });
        const isToday = dateStr === localDateStr();

        const rec = monthMap[dateStr];
        const dayLogs = allLogs.filter((l: any) => l.date === dateStr);
        dayLogs.sort((a: any, b: any) => timeToMinutes(a.time) - timeToMinutes(b.time));

        let firstIn = rec?.check_in || '—';
        let lastOut = rec?.check_out || '—';
        let status = rec ? (rec.status || 'present').toUpperCase() : 'NO RECORD';
        let dailyMins = 0;
        let missedPunches = 0;

        const inTimesList: string[] = [];
        const outTimesList: string[] = [];
        const timelinePairs: string[] = [];

        if (dayLogs.length > 0) {
          if (!rec) status = 'PRESENT';
          let curIn: any = null;

          dayLogs.forEach((l) => {
            // Populate Sheet 2 (Forensic Raw Logs)
            rawAuditLogsRows.push([
              l.id ? `#${l.id}` : '—',
              l.date,
              l.time,
              `#${calEmpId}`,
              empName,
              l.scan_type === 'IN' ? 'CHECK-IN (ENTRY)' : 'CHECK-OUT (EXIT)',
              getSourceLabel(l.source),
              outlets.find(o => o.id === outletId)?.name || 'Main Branch',
              l.note || 'Verified Punch',
            ]);

            if (l.scan_type === 'IN') {
              inTimesList.push(`${l.time} (${getSourceLabel(l.source)})`);
              if (curIn) {
                missedPunches++;
                timelinePairs.push(`[IN: ${curIn.time} ➔ ⚠️ SKIPPED OUT]`);
              }
              curIn = l;
            } else if (l.scan_type === 'OUT') {
              outTimesList.push(`${l.time} (${getSourceLabel(l.source)})`);
              if (curIn) {
                const dur = Math.max(0, timeToMinutes(l.time) - timeToMinutes(curIn.time));
                dailyMins += dur;
                timelinePairs.push(`[IN: ${curIn.time} ➔ OUT: ${l.time} (${formatMinutes(dur)})]`);
                curIn = null;
              } else {
                missedPunches++;
                timelinePairs.push(`[⚠️ SKIPPED IN ➔ OUT: ${l.time}]`);
              }
            }
          });

          if (curIn) {
            if (isToday) {
              const liveDur = Math.max(0, timeToMinutes(new Date().toTimeString().slice(0, 5)) - timeToMinutes(curIn.time));
              dailyMins += liveDur;
              timelinePairs.push(`[IN: ${curIn.time} ➔ 🟢 Working Now]`);
              lastOut = 'Working Now';
            } else {
              missedPunches++;
              timelinePairs.push(`[IN: ${curIn.time} ➔ ⚠️ SKIPPED OUT]`);
              lastOut = '⚠️ No Out Punch';
            }
          }

          firstIn = dayLogs.find(l => l.scan_type === 'IN')?.time || firstIn;
          const lastOutLog = [...dayLogs].reverse().find(l => l.scan_type === 'OUT');
          if (lastOutLog) lastOut = lastOutLog.time;
        } else if (rec && (rec.check_in || rec.check_out)) {
          if (rec.check_in) inTimesList.push(`${rec.check_in} (Manual)`);
          if (rec.check_out) outTimesList.push(`${rec.check_out} (Manual)`);
          if (rec.check_in && rec.check_out) {
            dailyMins = Math.max(0, timeToMinutes(rec.check_out) - timeToMinutes(rec.check_in));
            timelinePairs.push(`[IN: ${rec.check_in} ➔ OUT: ${rec.check_out} (${formatMinutes(dailyMins)})]`);
          } else if (rec.check_in && !rec.check_out) {
            missedPunches++;
            timelinePairs.push(`[IN: ${rec.check_in} ➔ ⚠️ SKIPPED OUT]`);
            lastOut = isToday ? 'Working Now' : '⚠️ No Out Punch';
          }
        }

        if (status === 'PRESENT' || status === 'LATE' || inTimesList.length > 0) {
          monthlyPresentDays++;
        }
        monthlyTotalMins += dailyMins;
        monthlySkippedPunches += missedPunches;

        dailyRows.push([
          `#${calEmpId}`,
          empName,
          dateStr,
          dayName,
          status,
          firstIn,
          lastOut,
          dailyMins > 0 ? formatMinutes(dailyMins) : (status === 'PRESENT' || inTimesList.length > 0 ? 'Active' : '—'),
          inTimesList.length,
          outTimesList.length,
          missedPunches > 0 ? `${missedPunches} Skipped` : '0',
          inTimesList.length > 0 ? inTimesList.join(', ') : 'None',
          outTimesList.length > 0 ? outTimesList.join(', ') : 'None',
          timelinePairs.length > 0 ? timelinePairs.join(' | ') : '—',
        ]);
      }

      // ── SHEET 1: Daily Breakdown Table ──
      const sheet1Data: (string | number)[][] = [
        ['OFFICIAL MONTHLY ATTENDANCE REPORT & AUDIT BREAKDOWN'],
        ['Employee Name:', empName],
        ['Employee ID:', `#${calEmpId}`],
        ['Month / Period:', `${monthName} ${calYear}`],
        ['Total Present Days:', `${monthlyPresentDays} of ${totalDays} days`],
        ['Total Active Hours:', formatMinutes(monthlyTotalMins)],
        ['Total Skipped Punches:', `${monthlySkippedPunches} Skipped Out / In`],
        ['Export Timestamp:', new Date().toLocaleString()],
        [], // empty spacer
        [
          'Employee ID',
          'Employee Name',
          'Date',
          'Day of Week',
          'Status',
          'First Check-In',
          'Last Check-Out',
          'Total Duration',
          'Check-Ins (Count)',
          'Check-Outs (Count)',
          'Skipped Punches',
          'ALL CHECK-IN TIMES (Exact)',
          'ALL CHECK-OUT TIMES (Exact)',
          'Complete Punch Pairing Timeline',
        ],
        ...dailyRows,
      ];

      // ── SHEET 2: Forensic Raw Audit Log (Chronological Punch-by-Punch) ──
      const sheet2Data: (string | number)[][] = [
        ['FORENSIC AUDIT TRAIL — EXACT PUNCH-BY-PUNCH LOGS'],
        ['Employee Name:', empName],
        ['Employee ID:', `#${calEmpId}`],
        ['Period:', `${monthName} ${calYear}`],
        ['Total Individual Punches:', rawAuditLogsRows.length],
        ['Verification Authenticity:', 'System Generated — Biometric & Scan Logs'],
        [],
        [
          'Log ID',
          'Date',
          'Exact Punch Time',
          'Employee ID',
          'Employee Name',
          'Punch Event (IN / OUT)',
          'Verification Method / Biometric Source',
          'Branch / Outlet',
          'System Remarks',
        ],
        ...(rawAuditLogsRows.length > 0
          ? rawAuditLogsRows
          : [['—', '—', '—', `#${calEmpId}`, empName, 'NO PUNCHES RECORDED', '—', '—', '—']]),
      ];

      await generateExcelFile(
        [
          { name: 'Monthly Breakdown', data: sheet1Data },
          { name: 'Forensic Audit Logs', data: sheet2Data },
        ],
        `Attendance_Audit_${empName.replace(/\s+/g, '_')}_${monthName}_${calYear}.xlsx`
      );

      postLog('ATTENDANCE_EXPORTED', `Exported legal forensic audit Excel for ${empName} (${monthName} ${calYear})`);
    } catch (e: any) {
      alert('Failed to generate Excel export: ' + (e.message || 'Unknown error'));
    } finally {
      setExportingMonth(false);
    }
  };


  const handleManualAttendance = async () => {
    if (!manualEmpId) { alert('Select an employee'); return; }
    let r: Response;
    try {
      r = await fetch(`${BASE}/employees/attendance/manual`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          employee_id: Number(manualEmpId), date: manualDate,
          check_in: manualCheckIn || null, check_out: manualCheckOut || null, status: manualStatus,
          outlet_id: outletId || null,
        }),
      });
    } catch {
      alert("Couldn't connect. Check your internet connection and try again.");
      return;
    }
    if (!r.ok) {
      const data = await r.json().catch(() => ({}));
      alert(data?.detail || "This attendance entry couldn't be saved. Please check the details and try again.");
      return;
    }
    const emp = employees.find(e => e.id === Number(manualEmpId));
    postLog('ATTENDANCE_MANUAL', `Manual attendance: ${emp?.name || `#${manualEmpId}`} | ${manualStatus} on ${manualDate}${manualCheckIn ? ` | In: ${manualCheckIn}` : ''}${manualCheckOut ? ` | Out: ${manualCheckOut}` : ''}`);
    fetchTodayAtt();
    if (calEmpId === Number(manualEmpId)) fetchCalendar(calEmpId);
  };

  const handleKioskScan = async (token: string) => {
    if (!token.trim()) return;
    setKioskLoading(true);
    setKioskResult(null);
    try {
      const r = await fetch(`${BASE}/employees/attendance/qr-scan`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ qr_token: token.trim(), outlet_id: outletId }),
      });
      if (r.ok) {
        const data = await r.json();
        setKioskResult({ name: data.name, scan_type: data.scan_type, time: data.time, status: 'success' });
        postLog('ATTENDANCE_QR', `QR scan: ${data.name} checked ${data.scan_type} at ${data.time}`);
        setKioskInput('');
        fetchTodayAtt();
        await fireAttendanceNotification(data);
      } else {
        setKioskResult({ name: '', scan_type: '', time: '', status: 'error' });
      }
    } catch {
      setKioskResult({ name: '', scan_type: '', time: '', status: 'error' });
    }
    setKioskLoading(false);
  };

const stopKioskCamera = () => {
  clearInterval(kioskScanInterval.current);
  const stream = kioskVideoRef.current?.srcObject as MediaStream;
  stream?.getTracks().forEach(t => t.stop());
  if (kioskVideoRef.current) kioskVideoRef.current.srcObject = null;
  setKioskCameraOn(false);
};

const startKioskCamera = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'environment' }
    });
    if (kioskVideoRef.current) {
      kioskVideoRef.current.srcObject = stream;
    }
    setKioskCameraOn(true);

    if (!(window as any).jsQR) {
      await new Promise<void>((res) => {
        const s = document.createElement('script');
        s.src = 'https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.js';
        s.onload = () => res();
        document.head.appendChild(s);
      });
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;

    const scanTick = () => {
      const video = kioskVideoRef.current;
      if (!video || video.readyState < 2) return;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = (window as any).jsQR(imageData.data, imageData.width, imageData.height);
      if (code?.data) {
        clearInterval(kioskScanInterval.current);
        if (kioskKeepScanningRef.current) {
          // Keep camera ON — just handle scan + restart scanning after 2s
          handleKioskScan(code.data);
          setTimeout(() => {
            kioskScanInterval.current = setInterval(scanTick, 300);
          }, 2000);
        } else {
          // Stop camera after scan
          stopKioskCamera();
          handleKioskScan(code.data);
        }
      }
    };

    kioskScanInterval.current = setInterval(scanTick, 300);
  } catch {
    alert('Camera access denied. Please allow camera permission.');
  }
};


  const handleLeaveStatus = async (id: number, status: string, customMsg?: string) => {
  let statusR: Response;
  try {
    statusR = await fetch(`${BASE}/employees/leaves/${id}`, {
      method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ status }),
    });
  } catch {
    alert("Couldn't connect. Check your internet connection and try again.");
    return;
  }
  if (!statusR.ok) {
    const data = await statusR.json().catch(() => ({}));
    alert(data?.detail || `This leave request couldn't be ${status}. Please try again.`);
    return;
  }
  const leave = leaves.find(l => l.id === id);
  const leaveEmp = employees.find(e => e.id === leave?.employee_id);
  postLog('LEAVE_STATUS_CHANGED', `Leave ${status}: ${leaveEmp?.name || `#${leave?.employee_id}`} | ${leave?.leave_type} ${leave?.from_date} to ${leave?.to_date}`);

  // WhatsApp / Email notification
  const notifyFailures: string[] = [];
  if (leave) {
    const emp = employees.find(e => e.id === leave.employee_id);
    const outletCountry = outlets.find(o => o.id === outletId)?.country || 'IN';
    const waPhone = resolveWaPhone(emp?.phone || '', outletCountry);
    if (waPhone) {
      const emoji = status === 'approved' ? '✅' : '❌';
      const msg = customMsg || `${emoji} Hi ${emp.name}, your leave request from ${leave.from_date} to ${leave.to_date} (${leave.leave_type}) has been ${status}.`;
      try {
        await sendWhatsApp(waPhone, msg, outletId);
      } catch (e: any) {
        notifyFailures.push(`WhatsApp: ${friendlyError(e, 'failed to send')}`);
      }
      if (emp.email) {
        try {
          await sendEmail(
            emp.email,
            `Leave Request ${status === 'approved' ? 'Approved' : 'Rejected'}`,
            `<div style="font-family:sans-serif;max-width:480px;margin:auto;padding:32px;background:#f8fafc;border-radius:16px"><h2 style="color:${status === 'approved' ? '#16a34a' : '#dc2626'}">${status === 'approved' ? '✅ Leave Approved' : '❌ Leave Rejected'}</h2><p>${msg.replace(/\n/g, '<br/>')}</p></div>`,
            outletId
          );
        } catch (e: any) {
          notifyFailures.push(`Email: ${friendlyError(e, 'failed to send')}`);
        }
      }
    }
  }

  fetchLeaves();
  if (notifyFailures.length) {
    postLog('LEAVE_NOTIFY_FAILED', `Leave status notification failed for ${leave?.employee_name || `#${leave?.employee_id}`}: ${notifyFailures.join(' | ')}`);
    alert(`Leave marked as ${status}, but notifying the employee failed:\n${notifyFailures.join('\n')}`);
  }
};
  const handleAddLeave = async () => {
    let r: Response;
    try {
      r = await fetch(`${BASE}/employees/leaves`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          employee_id: Number(leaveForm.employee_id),
          from_date: leaveForm.from_date, to_date: leaveForm.to_date,
          leave_type: leaveForm.leave_type, reason: leaveForm.reason,
          outlet_id: outletId || null,
        }),
      });
    } catch {
      alert("Couldn't connect. Check your internet connection and try again.");
      return;
    }
    if (!r.ok) {
      const data = await r.json().catch(() => ({}));
      alert(data?.detail || "This leave request couldn't be submitted. Please check the details and try again.");
      return;
    }
    const emp = employees.find(e => e.id === Number(leaveForm.employee_id));
    postLog('LEAVE_ADDED', `Leave request: ${emp?.name || `#${leaveForm.employee_id}`} | ${leaveForm.leave_type} from ${leaveForm.from_date} to ${leaveForm.to_date}`);
    setShowAddLeave(false);
    setLeaveForm({ employee_id:'', from_date:'', to_date:'', leave_type:'casual', reason:'' });
    fetchLeaves();
  };

  const handleGenPayroll = async () => {
    if (!genEmpId) { alert('Select employee'); return; }
    let r: Response;
    try {
      r = await fetch(`${BASE}/employees/payroll/generate`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          employee_id: Number(genEmpId), month: payMonth, year: payYear,
          bonus: Number(genBonus) || 0, deductions: Number(genDeductions) || 0,
          note: genNote || null,
          outlet_id: outletId || null,
        }),
      });
    } catch {
      alert("Couldn't connect. Check your internet connection and try again.");
      return;
    }
    if (!r.ok) {
      const data = await r.json().catch(() => ({}));
      alert(data?.detail || "Payroll couldn't be generated. Please check the details and try again.");
      return;
    }
    const emp = employees.find(e => e.id === Number(genEmpId));
    postLog('PAYROLL_GENERATED', `Payroll generated: ${emp?.name || `#${genEmpId}`} | ${MONTHS[payMonth-1]} ${payYear}${genBonus ? ` | Bonus: ${currSym}${genBonus}` : ''}${genDeductions ? ` | Deductions: ${currSym}${genDeductions}` : ''}`);
    setShowGenPayroll(false); setGenEmpId(''); setGenBonus(''); setGenDeductions(''); setGenNote('');
    fetchPayroll();
  };

  const handleMarkPaid = async (id: number) => {
    const record = payroll.find(p => p.id === id);
    let r: Response;
    try {
      r = await fetch(`${BASE}/employees/payroll/${id}/pay`, { method: 'PATCH', headers: authHeaders() });
    } catch {
      alert("Couldn't connect. Check your internet connection and try again.");
      return;
    }
    if (!r.ok) {
      const data = await r.json().catch(() => ({}));
      alert(data?.detail || "This payroll record couldn't be marked as paid. Please try again.");
      return;
    }
    postLog('PAYROLL_PAID', `Marked paid: ${record?.employee_name || `#${record?.employee_id}`} | ${record ? MONTHS[record.month-1] : ''} ${record?.year} | ${currSym}${record?.net.toLocaleString()}`);
    fetchPayroll();
  };

  // ─── Calendar helpers ─────────────────────────────────────────────────────
  const getDaysInMonth = (m: number, y: number) => new Date(y, m, 0).getDate();
  const getFirstDay = (m: number, y: number) => new Date(y, m - 1, 1).getDay();
  const calMap = calData.reduce<Record<string, AttendanceRecord>>((acc, r) => { acc[r.date] = r; return acc; }, {});

  const employeeOutlets = outlets.filter(o => o.modules?.includes('employees'));

  const filteredEmployees = employees.filter(e =>
    e.name.toLowerCase().includes(empSearch.toLowerCase()) ||
    (e.department || '').toLowerCase().includes(empSearch.toLowerCase()) ||
    (e.designation || '').toLowerCase().includes(empSearch.toLowerCase())
  );

  const activeCount = employees.filter(e => e.is_active).length;
  const presentToday = todayAtt.filter(a => a.status === 'present' || a.status === 'late').length;
  const pendingLeaves = leaves.filter(l => l.status === 'pending').length;
  const unpaidPayroll = payroll.filter(p => !p.is_paid).length;
  const todayStr = localDateStr();
  const newLeavesToday = leaves.filter(l => l.status === 'pending' && l.created_at.slice(0, 10) === todayStr).length;
  
  // ─── Styles ───────────────────────────────────────────────────────────────
  const S = {
    page: {
      flex: 1,
      background: '#f8fafc',
      overflowY: 'auto' as const,
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    },
    header: { background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '20px 32px 0', position: 'sticky' as const, top: 0, zIndex: 10 },
    headerTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
    title: { fontSize: 22, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' },
    subtitle: { fontSize: 12, color: '#94a3b8', fontWeight: 500, marginTop: 2 },
    body: { padding: '28px 32px' },
    card: { background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', overflow: 'hidden' },
    cardHeader: { padding: '14px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fafafa' },
    cardTitle: { fontSize: 13, fontWeight: 700, color: '#0f172a' },
    table: { width: '100%', borderCollapse: 'collapse' as const },
    th: { padding: '10px 16px', textAlign: 'left' as const, fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' as const, letterSpacing: '0.06em', borderBottom: '1px solid #f1f5f9', background: '#fafafa', whiteSpace: 'nowrap' as const },
    td: { padding: '12px 16px', fontSize: 13, color: '#334155', borderBottom: '1px solid #f8fafc', verticalAlign: 'middle' as const },
    btn: (variant: 'primary' | 'ghost' | 'danger' | 'success') => ({
      height: 36,
      padding: '0 14px',
      borderRadius: 10,
      cursor: 'pointer',
      fontSize: 13,
      fontWeight: 700,
      transition: 'all 0.15s ease',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      whiteSpace: 'nowrap' as const,
      background: variant === 'primary' ? 'linear-gradient(135deg, #0f172a, #1e293b)' : variant === 'danger' ? '#fef2f2' : variant === 'success' ? '#f0fdf4' : '#fff',
      color: variant === 'primary' ? '#fff' : variant === 'danger' ? '#dc2626' : variant === 'success' ? '#16a34a' : '#334155',
      border: variant === 'primary' ? 'none' : `1.5px solid ${variant === 'danger' ? '#fecaca' : variant === 'success' ? '#bbf7d0' : '#cbd5e1'}`,
      boxShadow: variant === 'primary' ? '0 2px 8px rgba(15,23,42,0.18)' : 'none',
    }),
    statCard: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '16px 20px', display: 'flex', flexDirection: 'column' as const, gap: 4 },
    statVal: { fontSize: 28, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', lineHeight: 1 },
    statLabel: { fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.06em' },
    grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
    grid4: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 },
    formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
    row: { display: 'flex', alignItems: 'center', gap: 8 },
    name: { fontSize: 13, fontWeight: 700, color: '#0f172a' },
    meta: { fontSize: 11, color: '#94a3b8', fontWeight: 500 },
    avatar: (i: number) => ({
      width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
      background: [`#e0e7ff`,`#fce7f3`,`#dcfce7`,`#fef9c3`,`#ffe4e6`][i % 5],
      color: [`#4f46e5`,`#be185d`,`#15803d`,`#a16207`,`#be123c`][i % 5],
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 12, fontWeight: 800,
    }),
    saveBtn: {
      padding: '9px 20px', background: '#0f172a', color: '#fff', border: 'none',
      borderRadius: 9, fontWeight: 700, fontSize: 13, cursor: 'pointer', width: '100%', marginTop: 8,
    },
  };

  const menuItemStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
    padding: '8px 10px', borderRadius: 6, border: 'none', background: 'transparent',
    fontSize: 12.5, fontWeight: 600, color: '#334155', cursor: 'pointer', textAlign: 'left',
  };

  // ─── STAFF TAB ────────────────────────────────────────────────────────────
const renderStaff = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

    {empLoadError && (
      <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#991b1b', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <span>⚠️ {empLoadError}</span>
        <button onClick={fetchEmployees} style={{ padding: '5px 12px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 11, cursor: 'pointer', flexShrink: 0 }}>
          Retry
        </button>
      </div>
    )}

    {/* ── Scanner Commander ── */}
    {/* Marking attendance (face/fingerprint/QR) is an attendance action, not
        an "add/edit employees" action — gate separately so a role with only
        canManageEmployees can't also punch attendance for anyone. */}
    {(isEmpOwner || ePerms.manageAttendance) && (
    <div style={{
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
      borderRadius: 16, padding: '20px 24px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
      boxShadow: '0 4px 24px rgba(15,23,42,0.15)',
    }}>
      <div>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#fff', letterSpacing: '-0.01em' }}>📡 Scanner Commander</div>
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, fontWeight: 500 }}>Mark attendance instantly — face, fingerprint, or QR</div>
      </div>
     <div className="emp-scanner-row" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button
          onClick={() => { startFaceScanEmp(); }}
          style={{
            height: 36, padding: '0 16px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.22)',
            background: 'rgba(255,255,255,0.14)', color: '#fff',
            fontWeight: 700, fontSize: 13, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.28)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.14)'; }}
        >
          👁️ <span>Face Scan</span>
        </button>
        <button
          onClick={() => { setShowKiosk(true); setTimeout(() => startKioskCamera(), 300); }}
          style={{
            height: 36, padding: '0 16px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.22)',
            background: 'rgba(255,255,255,0.14)', color: '#fff',
            fontWeight: 700, fontSize: 13, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.28)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.14)'; }}
        >
          📷 <span>QR Scan</span>
        </button>

        {/* Result toast */}
        {scannerResult && (
          <div style={{
            padding: '10px 16px', borderRadius: 10,
            background: scannerResult.status === 'success' ? 'rgba(22,163,74,0.15)' : 'rgba(220,38,38,0.15)',
            border: `1.5px solid ${scannerResult.status === 'success' ? '#16a34a' : '#dc2626'}`,
            display: 'flex', alignItems: 'center', gap: 8, minWidth: 200,
            animation: 'fadeIn 0.2s ease',
          }}>
            <span style={{ fontSize: 18 }}>{scannerResult.status === 'success' ? (scannerResult.scan_type === 'IN' ? '✅' : '🔴') : '❌'}</span>
            <div>
              {scannerResult.status === 'success' ? (
                <>
                  <div style={{ fontSize: 12, fontWeight: 800, color: '#fff' }}>{scannerResult.name}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>Checked {scannerResult.scan_type} · {scannerResult.time}</div>
                </>
              ) : (
                <div style={{ fontSize: 12, fontWeight: 700, color: '#fca5a5' }}>{scannerResult.time || 'Scan failed'}</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
    )}

    <div className="emp-grid4" style={S.grid4}>
        <div style={S.statCard}><div style={S.statVal}>{activeCount}</div><div style={S.statLabel}>Active Employees</div></div>
        <div style={S.statCard}><div style={S.statVal}>{shifts.length}</div><div style={S.statLabel}>Shifts Defined</div></div>
        <div style={S.statCard}><div style={S.statVal}>{employees.filter(e => e.salary_type === 'fixed').length}</div><div style={S.statLabel}>Fixed Salary</div></div>
        <div style={S.statCard}><div style={S.statVal}>{employees.filter(e => e.salary_type === 'daily').length}</div><div style={S.statLabel}>Daily Rate</div></div>
      </div>
      {empPlanInfo && empPlanInfo.plan_limit > 0 && empPlanInfo.plan_limit < 9999 && empPlanInfo.active_count < empPlanInfo.plan_limit && (
        <div style={{ margin: '0 0 4px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#1d4ed8', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span>
            ℹ️ Up to {empPlanInfo.plan_limit} employees allowed per outlet on your plan
            {outletId ? ` — ${empPlanInfo.active_count}/${empPlanInfo.plan_limit} used at ${outlets.find(o => o.id === outletId)?.name || 'this outlet'}.` : ` — ${empPlanInfo.active_count}/${empPlanInfo.plan_limit} used (unassigned employees).`}
          </span>
        </div>
      )}
      {/* Plan limit banner */}
      {empPlanInfo && empPlanInfo.plan_limit === 0 && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#991b1b', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <span>🔒 Employee management is not available on your current plan. Subscribe to the Employees module to add employees.</span>
          <button onClick={() => window.dispatchEvent(new CustomEvent('show-upgrade-plans'))}
            style={{ padding: '6px 14px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 11, cursor: 'pointer', flexShrink: 0 }}>
            Upgrade Now
          </button>
        </div>
      )}
      {empPlanInfo && empPlanInfo.plan_limit > 0 && empPlanInfo.plan_limit < 9999 && empPlanInfo.active_count >= empPlanInfo.plan_limit && (
        <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#92400e', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <span>
            ⚠️ Employee limit reached for {outletId ? (outlets.find(o => o.id === outletId)?.name || 'this outlet') : 'unassigned employees'} — {empPlanInfo.active_count}/{empPlanInfo.plan_limit} used (max {empPlanInfo.plan_limit} per outlet). Add employees to a different outlet or upgrade.
          </span>
          <button onClick={() => window.dispatchEvent(new CustomEvent('show-upgrade-plans'))}
            style={{ padding: '6px 14px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 11, cursor: 'pointer', flexShrink: 0 }}>
            Upgrade Now
          </button>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input value={empSearch} onChange={e => setEmpSearch(e.target.value)} placeholder="Search employees, departments..."
          style={{ flex: 1, height: 36, padding: '0 14px', borderRadius: 10, border: '1.5px solid #cbd5e1', fontSize: 13, outline: 'none', fontFamily: 'inherit', color: '#0f172a', background: '#fff', boxSizing: 'border-box' }}
          onFocus={e => e.currentTarget.style.borderColor = '#6366f1'}
          onBlur={e => e.currentTarget.style.borderColor = '#cbd5e1'}
        />
        {ePerms.manageEmployees && (
        <button style={S.btn('ghost')} onClick={() => setShowAddShift(true)}>＋ Add Shift</button>
        )}
        {shifts.length > 0 && (
        <button style={S.btn('ghost')} onClick={() => setShowShiftsList(true)}>📋 Shifts ({shifts.length})</button>
        )}
        {ePerms.manageEmployees && (
  <button
    style={{
      ...S.btn('primary'),
      opacity: (empPlanInfo && (empPlanInfo.plan_limit === 0 || empPlanInfo.active_count >= empPlanInfo.plan_limit)) ? 0.5 : 1,
      cursor: (empPlanInfo && (empPlanInfo.plan_limit === 0 || empPlanInfo.active_count >= empPlanInfo.plan_limit)) ? 'not-allowed' : 'pointer',
    }}
    onClick={() => {
      if (empPlanInfo && (empPlanInfo.plan_limit === 0 || empPlanInfo.active_count >= empPlanInfo.plan_limit)) {
        window.dispatchEvent(new CustomEvent('show-upgrade-plans'));
        return;
      }
      setEditingEmp(null); setEmpForm(emptyEmpForm); setModalPhase('form'); setShowAddEmp(true);
    }}>
    ＋ Add Employee {empPlanInfo && empPlanInfo.plan_limit > 0 && empPlanInfo.plan_limit < 9999 ? `(${empPlanInfo.active_count}/${empPlanInfo.plan_limit})` : ''}
  </button>
)}
      </div>
      <div style={S.card}>
        <div className="emp-table-wrap">
        <table style={S.table}>
          <thead>
            <tr>{['Employee','Department / Role','Shift','Salary', ...(employees.some(e => e.join_date) ? ['Join Date'] : []), ...(employees.some(e => e.device_employee_id) ? ['Device ID'] : []),'Biometrics','Status',''].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {(() => {
              const colCount = 5 + (employees.some(e => e.join_date) ? 1 : 0) + (employees.some(e => e.device_employee_id) ? 1 : 0) + 3;
              return loadingEmp ? (
              <tr><td colSpan={colCount} style={{ ...S.td, textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading...</td></tr>
            ) : filteredEmployees.length === 0 ? (
              <tr><td colSpan={colCount} style={{ ...S.td, textAlign: 'center', padding: 48, color: '#94a3b8' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>👔</div>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', marginBottom: 4 }}>No employees yet</div>
                <div style={{ fontSize: 12 }}>Click "+ Add Employee" to get started</div>
              </td></tr>
            ) : filteredEmployees.map((emp, i) => (
              <tr key={emp.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#f0f9ff')}
                onMouseLeave={e => (e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafafa')}
              >
                <td style={S.td}>
                  <div style={S.row}>
                    <div style={S.avatar(i)}>{emp.name?.[0]?.toUpperCase() || '?'}</div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={S.name}>{emp.name}</span>
                        <span style={{ fontSize: 10, fontWeight: 800, color: '#4f46e5', background: '#eef2ff', border: '1px solid #c7d2fe', padding: '1px 6px', borderRadius: 4, fontFamily: 'monospace' }}>
                          ID: #{emp.id}
                        </span>
                        {emp.device_employee_id && (
                          <span style={{ fontSize: 9, fontWeight: 800, color: '#0891b2', background: '#ecfeff', border: '1px solid #a5f3fc', padding: '1px 5px', borderRadius: 4, fontFamily: 'monospace' }} title="ZKTeco Machine Punch ID">
                            DEV: {emp.device_employee_id}
                          </span>
                        )}
                      </div>
                      <div style={{ display:'flex', alignItems:'center', gap:4, marginTop: 2 }}>
                        <span style={S.meta}>{emp.phone || '—'}{emp.email ? ` · ${emp.email}` : ''}</span>
                        {emp.phone_verified && <span title="Verified" style={{ fontSize:10, color:'#16a34a', fontWeight:800 }}>✓</span>}
                        {!emp.phone_verified && emp.phone && <span title="Not Verified" style={{ fontSize:10, color:'#f59e0b', fontWeight:800 }}>⚠</span>}
                      </div>
                    </div>
                  </div>
                </td>
                <td style={S.td}>
                  <div style={S.name}>{emp.designation || '—'}</div>
                  <div style={{ display: 'flex', gap: 4, marginTop: 2, flexWrap: 'wrap' as const }}>
                    {emp.department && <span style={S.meta}>{emp.department}</span>}
                    {(emp as any).role && (
                      <span style={{ fontSize: 9, fontWeight: 800, padding: '1px 6px', borderRadius: 99, textTransform: 'uppercase' as const, letterSpacing: '0.04em',
                        background: (emp as any).role === 'owner' ? '#fef3c7' : (emp as any).role === 'admin' ? '#eff6ff' : (emp as any).role === 'manager' ? '#f0fdf4' : '#f8fafc',
                        color: (emp as any).role === 'owner' ? '#92400e' : (emp as any).role === 'admin' ? '#1d4ed8' : (emp as any).role === 'manager' ? '#166534' : '#64748b',
                      }}>{(emp as any).role}</span>
                    )}
                  </div>
                </td>
                <td style={S.td}>
                  {emp.shift_name
                    ? <span style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{emp.shift_name}</span>
                    : <span style={{ color: '#cbd5e1' }}>—</span>}
                </td>
                <td style={S.td}>
                  <div style={S.name}>{emp.salary_type === 'fixed' ? `${currSym}${(emp.monthly_salary || 0).toLocaleString()}/mo` : `${currSym}${(emp.daily_rate || 0).toLocaleString()}/day`}</div>
                  <div style={S.meta}>{emp.salary_type === 'fixed' ? 'Fixed' : 'Daily rate'}</div>
                </td>
                {employees.some(e => e.join_date) && <td style={S.td}><span style={S.meta}>{emp.join_date || '—'}</span></td>}
                {employees.some(e => e.device_employee_id) && <td style={S.td}><span style={{ fontFamily: 'monospace', fontSize: 11, color: '#64748b', background: '#f8fafc', padding: '2px 6px', borderRadius: 4, border: '1px solid #e2e8f0' }}>{emp.device_employee_id || '—'}</span></td>}
                {/* Biometrics column */}
                <td style={S.td}>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' as const }}>
                    {emp.face_enrolled
                      ? <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99, background: '#fdf4ff', color: '#7c3aed', border: '1px solid #e9d5ff' }}>👁️ Face</span>
                      : <span style={{ fontSize: 10, color: '#cbd5e1' }}>No face</span>}
                    {emp.webauthn_enrolled
                      ? <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99, background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }}>👆 FP</span>
                      : <span style={{ fontSize: 10, color: '#cbd5e1' }}>No FP</span>}
                  </div>
                </td>
                <td style={S.td}><Badge label={emp.is_active ? 'Active' : 'Inactive'} colors={emp.is_active ? STATUS_COLORS.present : STATUS_COLORS.absent} /></td>
                <td style={S.td}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                    {ePerms.manageEmployees && (
                      <button style={S.btn('primary')} onClick={() => {
                        setSavedEmpId(emp.id);
                        setEditingEmp(emp);
                        setEmpForm({
                          name: emp.name, phone: emp.phone||'', email: emp.email||'',
                          department: emp.department||'', designation: emp.designation||'',
                          shift_id: emp.shift_id ? String(emp.shift_id) : '',
                          salary_type: emp.salary_type||'fixed',
                          monthly_salary: emp.monthly_salary ? String(emp.monthly_salary) : '',
                          daily_rate: emp.daily_rate ? String(emp.daily_rate) : '',
                          join_date: emp.join_date||'',
                          device_employee_id: emp.device_employee_id||'',
                          role: emp.role||'staff',
                          outlet_id: emp.outlet_id ? String(emp.outlet_id) : '',
                        });
                        setModalPhase('form');
                        setShowAddEmp(true);
                      }}>Edit</button>
                    )}

                    <div style={{ position: 'relative' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (openRowMenu === emp.id) {
                            setOpenRowMenu(null);
                          } else {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setRowMenuPos({ top: rect.bottom + 6, left: Math.max(8, rect.right - 200) });
                            setOpenRowMenu(emp.id);
                          }
                        }}
                        style={{
                          width: 30, height: 30, borderRadius: 8, border: '1px solid #e2e8f0',
                          background: openRowMenu === emp.id ? '#f1f5f9' : '#fff', cursor: 'pointer',
                          fontSize: 14, color: '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                        title="More actions"
                      >⋯</button>

                      {openRowMenu === emp.id && rowMenuPos && createPortal(
                        <div
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            position: 'fixed', top: rowMenuPos.top, left: rowMenuPos.left, zIndex: 2000,
                            background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
                            boxShadow: '0 12px 32px rgba(0,0,0,0.12)', minWidth: 200,
                            padding: 6, display: 'flex', flexDirection: 'column', gap: 2,
                          }}
                        >
                          {ePerms.manageEmployees && (
                            <button style={menuItemStyle} onClick={async () => {
                              setOpenRowMenu(null);
                              const failures: string[] = [];
                              try {
                                const waR = await fetch(`${BASE}/employees/${emp.id}/send-qr-whatsapp`, { method: 'POST', headers: authHeaders() });
                                if (!waR.ok) failures.push('WhatsApp');
                              } catch {
                                failures.push('WhatsApp');
                              }
                              if (emp.email && emp.qr_token) {
                                try {
                                  const QRCode = (await import('qrcode')).default;
                                  const qrUrl = await QRCode.toDataURL(emp.qr_token, { width: 200, margin: 2 });
                                  await sendEmail(
                                    emp.email,
                                    `Your Employee QR Badge — ${emp.name}`,
                                    `<div style="font-family:sans-serif;max-width:480px;margin:auto;padding:32px;background:#f8fafc;border-radius:16px"><h2 style="color:#0f172a">👔 Employee QR Badge</h2><p>Hi <strong>${emp.name}</strong>! Your attendance QR badge is below. Show this at the entrance to check in/out.</p><div style="text-align:center;margin:20px 0"><img src="${qrUrl}" style="width:200px;height:200px" /></div><p style="color:#6b7280;font-size:12px">Token: ${emp.qr_token}</p><p style="color:#6b7280;font-size:12px">— ${companyName || authUser?.name || 'Our Company'}</p></div>`,
                                    emp.outlet_id ?? outletId
                                  );
                                } catch (e: any) {
                                  failures.push(`Email: ${friendlyError(e, 'failed to send')}`);
                                }
                              }
                              if (failures.length) {
                                postLog('EMPLOYEE_QR_DELIVERY_FAILED', `QR badge resend failed for ${emp.name}: ${failures.join(', ')}`);
                                alert(`Couldn't send the QR badge:\n${failures.join('\n')}`);
                              } else {
                                postLog('EMPLOYEE_QR_RESENT', `QR badge resent to ${emp.name}`);
                              }
                            }}>📲 Send QR badge</button>
                          )}
                          {ePerms.manageEmployees && (
                            <button style={menuItemStyle} onClick={() => {
                              setOpenRowMenu(null);
                              setSavedEmpId(emp.id);
                              setEditingEmp(emp);
                              setModalPhase('done');
                              setBioStatus('idle');
                              setBioMessage('');
                              setShowAddEmp(true);
                            }}>🔐 Biometric setup</button>
                          )}
                          {ePerms.manageEmployees && (
                            <button style={menuItemStyle} onClick={() => { setOpenRowMenu(null); fetchEmployeeHistory(emp); }}>
                              🕘 Attendance history
                            </button>
                          )}
                          {ePerms.manageEmployees && ['admin','owner'].includes(authUser?.role || '') && (
                            <>
                              <div style={{ height: 1, background: '#f1f5f9', margin: '4px 0' }} />
                              <button style={{ ...menuItemStyle, color: '#dc2626' }} onClick={() => { setOpenRowMenu(null); handleDeleteEmployee(emp.id); }}>
                                🗑 Delete employee
                              </button>
                            </>
                          )}
                        </div>,
                        document.body
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            ));
            })()}
          </tbody>
        </table>
        </div>
      </div>
      </div>
  );

  // ─── ATTENDANCE TAB (ADVANCED DATE / MONTH / YEAR & RANGE) ──────────────────
  const renderAttendance = () => {
    const displayedAtt = todayAtt.filter(a => {
      if (!attSearchQuery) return true;
      const q = attSearchQuery.toLowerCase();
      const name = (a.employee_name || '').toLowerCase();
      const idStr = String(a.employee_id);
      return name.includes(q) || idStr.includes(q);
    });

    const presentCount = displayedAtt.filter(a => a.status === 'present' || a.status === 'late').length;
    const absentCount = displayedAtt.filter(a => a.status === 'absent').length;
    const lateCount = displayedAtt.filter(a => a.status === 'late').length;
    const halfDayCount = displayedAtt.filter(a => a.status === 'half_day').length;

    const isSingleDay = attStartDate === attEndDate;
    const rangeTitle = isSingleDay
      ? `Attendance for ${new Date(attStartDate + 'T00:00:00').toLocaleDateString('en-IN', { dateStyle: 'full' })}`
      : `Attendance: ${attStartDate} ➔ ${attEndDate} (${displayedAtt.length} records)`;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {attLoadError && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#991b1b', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <span>⚠️ {attLoadError}</span>
            <button onClick={fetchTodayAtt} style={{ padding: '5px 12px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 11, cursor: 'pointer', flexShrink: 0 }}>
              Retry
            </button>
          </div>
        )}

        {/* ── Filter Bar: Presets, Date Range & Month/Year Selectors ── */}
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            {/* Quick Presets */}
            <div style={{ display: 'flex', gap: 4, background: '#f1f5f9', padding: 4, borderRadius: 10, flexWrap: 'wrap' }}>
              {[
                { id: 'today', label: 'Today' },
                { id: 'yesterday', label: 'Yesterday' },
                { id: 'last7', label: 'Last 7 Days' },
                { id: 'this_month', label: 'This Month' },
                { id: 'month_year', label: '📅 By Month/Year' },
                { id: 'custom', label: 'Custom Range' },
              ].map(p => (
                <button
                  key={p.id}
                  onClick={() => handlePresetChange(p.id as DatePreset)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 7,
                    border: 'none',
                    fontSize: 12,
                    fontWeight: attDatePreset === p.id ? 800 : 600,
                    background: attDatePreset === p.id ? '#0f172a' : 'transparent',
                    color: attDatePreset === p.id ? '#fff' : '#64748b',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* Date Pickers OR Month/Year Dropdowns */}
            {attDatePreset === 'month_year' ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', border: '1.5px solid #6366f1', borderRadius: 8, padding: '4px 10px' }}>
                <span style={{ fontSize: 11, fontWeight: 800, color: '#6366f1' }}>MONTH:</span>
                <select
                  value={attSelMonth}
                  onChange={e => {
                    const m = Number(e.target.value);
                    setAttSelMonth(m);
                    handlePresetChange('month_year', m, attSelYear);
                  }}
                  style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, fontWeight: 700, color: '#0f172a' }}
                >
                  {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>

                <span style={{ fontSize: 11, fontWeight: 800, color: '#6366f1', marginLeft: 4 }}>YEAR:</span>
                <input
                  type="number"
                  value={attSelYear}
                  onChange={e => {
                    const y = Number(e.target.value);
                    setAttSelYear(y);
                    handlePresetChange('month_year', attSelMonth, y);
                  }}
                  style={{ width: 68, padding: '4px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, fontWeight: 700, color: '#0f172a' }}
                />
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>From:</span>
                  <input
                    type="date"
                    value={attStartDate}
                    onChange={e => {
                      setAttStartDate(e.target.value);
                      setAttDatePreset('custom');
                    }}
                    style={{ padding: '6px 10px', borderRadius: 8, border: '1.5px solid #cbd5e1', fontSize: 12, fontWeight: 600, color: '#0f172a' }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>To:</span>
                  <input
                    type="date"
                    value={attEndDate}
                    onChange={e => {
                      setAttEndDate(e.target.value);
                      setAttDatePreset('custom');
                    }}
                    style={{ padding: '6px 10px', borderRadius: 8, border: '1.5px solid #cbd5e1', fontSize: 12, fontWeight: 600, color: '#0f172a' }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Search, Specific Employee & Status Filter */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10, paddingTop: 4 }}>
            <input
              type="text"
              placeholder="🔍 Search employee name..."
              value={attSearchQuery}
              onChange={e => setAttSearchQuery(e.target.value)}
              style={{ padding: '7px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 12, outline: 'none' }}
            />
            <select
              value={attFilterEmpId}
              onChange={e => setAttFilterEmpId(e.target.value)}
              style={{ padding: '7px 10px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 12, color: '#334155', cursor: 'pointer' }}
            >
              <option value="all">👥 All Employees</option>
              {employees.map(e => <option key={e.id} value={String(e.id)}>{e.name} (#{e.id})</option>)}
            </select>
            <select
              value={attFilterStatus}
              onChange={e => setAttFilterStatus(e.target.value)}
              style={{ padding: '7px 10px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 12, color: '#334155', cursor: 'pointer' }}
            >
              <option value="all">⚡ All Statuses</option>
              <option value="present">Present</option>
              <option value="late">Late</option>
              <option value="half_day">Half Day</option>
              <option value="absent">Absent</option>
            </select>
            {outlets.length > 0 && (
              <select
                value={attFilterOutlet}
                onChange={e => setAttFilterOutlet(e.target.value)}
                style={{ padding: '7px 10px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 12, color: '#334155', cursor: 'pointer' }}
              >
                <option value="current">{outlets.find(o => o.id === outletId)?.name || 'Current Branch'} (current)</option>
                <option value="all">All Outlets</option>
                {employeeOutlets.filter(o => o.id !== outletId).map(o => (
                  <option key={o.id} value={String(o.id)}>{o.name}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* 4 Stat Summary Cards */}
        <div className="emp-grid4" style={S.grid4}>
          <div style={S.statCard}><div style={S.statVal}>{presentCount}</div><div style={S.statLabel}>Present Records</div></div>
          <div style={S.statCard}><div style={S.statVal}>{absentCount}</div><div style={S.statLabel}>Absent Records</div></div>
          <div style={S.statCard}><div style={S.statVal}>{lateCount}</div><div style={S.statLabel}>Late Punches</div></div>
          <div style={S.statCard}><div style={S.statVal}>{halfDayCount}</div><div style={S.statLabel}>Half Days</div></div>
        </div>

        {/* Main Attendance Layout */}
        <div className="emp-attendance-layout" style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20 }}>
          <div style={S.card}>
            <div style={S.cardHeader}>
              <span style={S.cardTitle}>{rangeTitle}</span>
              <div style={S.row}>
                {(isEmpOwner || ePerms.manageAttendance) && (
                  <button style={S.btn('ghost')} onClick={handleExportAttendance} disabled={exportingToday}>
                    {exportingToday ? '⏳ Exporting...' : '📤 Export Report'}
                  </button>
                )}
                <button style={S.btn('ghost')} onClick={fetchTodayAtt}>↺ Refresh</button>
                {(isEmpOwner || ePerms.manageAttendance) && (
                  <>
                    <button style={S.btn('primary')} onClick={() => { setShowKiosk(true); setTimeout(() => startKioskCamera(), 300); }}>📷 QR Kiosk</button>
                    <button style={S.btn('ghost')} onClick={() => startFaceScanEmp()}>👁️ Face Scan</button>
                  </>
                )}
              </div>
            </div>
            <div className="emp-table-wrap">
              <table style={S.table}>
                <thead>
                  <tr>
                    {['Employee', 'Date', 'Check In', 'Check Out', 'Status', 'Source', 'Outlet', ''].map(h => (
                      <th key={h} style={S.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {attLoading ? (
                    <tr><td colSpan={8} style={{ ...S.td, textAlign: 'center', padding: 32, color: '#94a3b8' }}>Loading records...</td></tr>
                  ) : displayedAtt.length === 0 ? (
                    <tr>
                      <td colSpan={8} style={{ ...S.td, textAlign: 'center', padding: 48, color: '#94a3b8' }}>
                        <div style={{ fontSize: 28, marginBottom: 6 }}>📅</div>
                        <div style={{ fontWeight: 600, color: '#0f172a' }}>No attendance records found</div>
                        <div style={{ fontSize: 12, marginTop: 4 }}>Try picking a different date range or month above</div>
                      </td>
                    </tr>
                  ) : (
                    displayedAtt.map((a, i) => {
                      const emp = employees.find(e => e.id === a.employee_id) || { id: a.employee_id, name: a.employee_name || 'Employee' };
                      return (
                        <tr
                          key={a.id || i}
                          onClick={() => openDayDetailModal(emp, a.date, a)}
                          style={{ background: i % 2 === 0 ? '#fff' : '#fafafa', cursor: 'pointer' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
                          onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafafa'}
                          title="Click to view total hours & check-in/out breakdown"
                        >
                          <td style={S.td}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={S.name}>{a.employee_name || `Employee #${a.employee_id}`}</span>
                              <span style={{ fontSize: 10, fontWeight: 800, color: '#4f46e5', background: '#eef2ff', border: '1px solid #c7d2fe', padding: '1px 5px', borderRadius: 4, fontFamily: 'monospace' }}>
                                #{a.employee_id}
                              </span>
                            </div>
                            {emp && (emp as any).phone ? (
                              <div style={S.meta}>{(emp as any).phone}</div>
                            ) : null}
                          </td>
                          <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: '#0f172a' }}>
                            {a.date}
                          </td>
                          <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 13 }}>{a.check_in || '—'}</td>
                          <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 13 }}>{a.check_out || '—'}</td>
                          <td style={S.td}><Badge label={a.status} colors={STATUS_COLORS[a.status] || STATUS_COLORS.present} /></td>
                          <td style={S.td}><span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>{(a.source || 'MANUAL').toUpperCase()}</span></td>
                          <td style={S.td}><span style={{ fontSize: 11, color: '#64748b' }}>{outlets.find(o => o.id === a.outlet_id)?.name || (a.outlet_id ? `#${a.outlet_id}` : '—')}</span></td>
                          <td style={{ ...S.td, textAlign: 'right' }}>
                            <span style={{ fontSize: 11, fontWeight: 800, color: '#6366f1', background: '#eef2ff', padding: '3px 8px', borderRadius: 6 }}>
                              Details ➔
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Right Side: Mark Attendance & Monthly View */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {(isEmpOwner || ePerms.manageAttendance) && (
              <div style={S.card}>
                <div style={S.cardHeader}><span style={S.cardTitle}>Mark Attendance</span></div>
                <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <label style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Employee</label>
                    <EmployeeSearchSelect employees={employees.filter(e => e.is_active)} value={manualEmpId} onChange={setManualEmpId} />
                  </div>
                  <Input label="Date" type="date" value={manualDate} onChange={e => setManualDate(e.target.value)} />
                  <Select label="Status" value={manualStatus} onChange={e => setManualStatus(e.target.value)}>
                    <option value="present">Present</option>
                    <option value="absent">Absent</option>
                    <option value="late">Late</option>
                    <option value="half_day">Half Day</option>
                  </Select>
                  <div style={S.grid2}>
                    <Input label="Check In" type="time" value={manualCheckIn} onChange={e => setManualCheckIn(e.target.value)} />
                    <Input label="Check Out" type="time" value={manualCheckOut} onChange={e => setManualCheckOut(e.target.value)} />
                  </div>
                  <button style={S.saveBtn} onClick={handleManualAttendance}>Save Attendance</button>
                </div>
              </div>
            )}

            <div style={S.card}>
              <div style={S.cardHeader}>
                <span style={S.cardTitle}>Monthly View</span>
                {(isEmpOwner || ePerms.manageAttendance) && (
                  <button style={S.btn('ghost')} onClick={handleExportMonth} disabled={!calEmpId || exportingMonth}>
                    {exportingMonth ? '...' : '📤 Export'}
                  </button>
                )}
              </div>
              <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <label style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Employee</label>
                    <button onClick={startCalQRCamera} style={{
                      fontSize: 11, fontWeight: 700, color: '#0369a1', background: '#f0f9ff',
                      border: '1px solid #bae6fd', borderRadius: 6, padding: '3px 10px', cursor: 'pointer',
                    }}>📷 Scan QR</button>
                  </div>
                  <EmployeeSearchSelect
                    employees={employees.filter(e => e.is_active)}
                    value={calEmpId ? String(calEmpId) : ''}
                    onChange={v => { const id = Number(v); setCalEmpId(id || null); if (id) fetchCalendar(id); }}
                  />
                </div>
                <div style={S.grid2}>
                  <Select label="Month" value={calMonth} onChange={e => setCalMonth(Number(e.target.value))}>
                    {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                  </Select>
                  <Input label="Year" type="number" value={calYear} onChange={e => setCalYear(Number(e.target.value))} />
                </div>
                {calEmpId && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3, marginTop: 4 }}>
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                      <div key={i} style={{ textAlign: 'center', fontSize: 9, fontWeight: 800, color: '#94a3b8', padding: '4px 0' }}>{d}</div>
                    ))}
                    {Array.from({ length: getFirstDay(calMonth, calYear) }).map((_, i) => <div key={`e${i}`} />)}
                    {Array.from({ length: getDaysInMonth(calMonth, calYear) }).map((_, i) => {
                      const d = i + 1;
                      const dateStr = `${calYear}-${String(calMonth).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                      const rec = calMap[dateStr];
                      const colors = rec ? (STATUS_COLORS[rec.status] || STATUS_COLORS.present) : null;
                      return (
                        <div
                          key={d}
                          title={rec ? `${rec.status} | Click to view total hours` : `Click to view ${dateStr}`}
                          onClick={() => {
                            const selectedEmp = employees.find(e => e.id === calEmpId) || { id: calEmpId, name: 'Employee' };
                            openDayDetailModal(selectedEmp, dateStr, rec);
                          }}
                          style={{
                            textAlign: 'center', fontSize: 10, fontWeight: rec ? 800 : 500,
                            padding: '5px 2px', borderRadius: 6, cursor: 'pointer',
                            background: colors?.bg || 'transparent', color: colors?.text || '#cbd5e1',
                            border: `1px solid ${colors?.border || 'transparent'}`,
                            transition: 'all 0.15s ease',
                          }}
                        >
                          {d}
                        </div>
                      );
                    })}
                  </div>
                )}
                {calEmpId && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                    {Object.entries(STATUS_COLORS).map(([k, v]) => (
                      <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <div style={{ width: 10, height: 10, borderRadius: 3, background: v.bg, border: `1px solid ${v.border}` }} />
                        <span style={{ fontSize: 9, color: '#64748b', fontWeight: 600, textTransform: 'capitalize' }}>{k.replace('_', ' ')}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ─── LEAVES TAB ───────────────────────────────────────────────────────────
  const renderLeaves = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {leavesLoadError && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#991b1b', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <span>⚠️ {leavesLoadError}</span>
          <button onClick={fetchLeaves} style={{ padding: '5px 12px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 11, cursor: 'pointer', flexShrink: 0 }}>
            Retry
          </button>
        </div>
      )}
      <div className="emp-grid5" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 24 }}>
        <div style={{ ...S.statCard, ...(newLeavesToday > 0 ? { background: '#fffbeb', borderColor: '#fde68a' } : {}) }}>
          <div style={{ ...S.statVal, color: newLeavesToday > 0 ? '#92400e' : S.statVal.color }}>{newLeavesToday}</div>
          <div style={{ ...S.statLabel, color: newLeavesToday > 0 ? '#92400e' : S.statLabel.color }}>🔔 New Today</div>
        </div>
        <div style={S.statCard}><div style={S.statVal}>{pendingLeaves}</div><div style={S.statLabel}>Pending</div></div>
        <div style={S.statCard}><div style={S.statVal}>{leaves.filter(l => l.status === 'approved').length}</div><div style={S.statLabel}>Approved</div></div>
        <div style={S.statCard}><div style={S.statVal}>{leaves.filter(l => l.status === 'rejected').length}</div><div style={S.statLabel}>Rejected</div></div>
        <div style={S.statCard}><div style={S.statVal}>{leaves.length}</div><div style={S.statLabel}>Total This Year</div></div>
      </div>
      <div style={S.card}>
        <div style={S.cardHeader}>
          <span style={S.cardTitle}>Leave Requests</span>
          {ePerms.requestLeave && (
  <button style={S.btn('primary')} onClick={() => setShowAddLeave(true)}>＋ Add Leave</button>
)}
        </div>
        <div style={{ padding: '12px 20px', borderBottom: '1px solid #f1f5f9' }}>
          <input
            value={leaveSearch}
            onChange={e => setLeaveSearch(e.target.value)}
            placeholder="Search by employee, type, status, reason..."
            style={{
              width: '100%', padding: '8px 14px', borderRadius: 9, border: '1.5px solid #e2e8f0',
              fontSize: 13, outline: 'none', fontFamily: 'inherit', color: '#0f172a', boxSizing: 'border-box' as const,
            }}
            onFocus={e => e.currentTarget.style.borderColor = '#6366f1'}
            onBlur={e => e.currentTarget.style.borderColor = '#e2e8f0'}
          />
        </div>
        <div className="emp-table-wrap">
        <table style={S.table}>
          <thead><tr>{['Employee','Type','From','To','Reason','Status','Actions'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
          <tbody>
            {(() => {
              const filteredLeaves = leaves.filter(l => {
                const q = leaveSearch.toLowerCase();
                if (!q) return true;
                return (
                  (l.employee_name || '').toLowerCase().includes(q) ||
                  l.leave_type.toLowerCase().includes(q) ||
                  l.status.toLowerCase().includes(q) ||
                  (l.reason || '').toLowerCase().includes(q)
                );
              });
              if (filteredLeaves.length === 0) {
                return (
                  <tr><td colSpan={7} style={{ ...S.td, textAlign: 'center', padding: 48, color: '#94a3b8' }}>
                    <div style={{ fontSize: 28, marginBottom: 6 }}>🏖️</div>
                    <div style={{ fontWeight: 600 }}>{leaveSearch ? 'No matching leave requests' : 'No leave requests yet'}</div>
                  </td></tr>
                );
              }
              return filteredLeaves.map((l, i) => (
                <tr key={l.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <td style={S.td}>
                    <div style={S.name}>{l.employee_name || `#${l.employee_id}`}</div>
                    {(() => {
                      const emp = employees.find(e => e.id === l.employee_id);
                      return emp && (emp.phone || emp.email) ? (
                        <div style={S.meta}>{emp.phone || ''}{emp.phone && emp.email ? ' · ' : ''}{emp.email || ''}</div>
                      ) : null;
                    })()}
                  </td>
                  <td style={S.td}><span style={{ textTransform: 'capitalize', fontWeight: 600 }}>{l.leave_type}</span></td>
                  <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 12 }}>{l.from_date}</td>
                  <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 12 }}>{l.to_date}</td>
                  <td style={{ ...S.td, maxWidth: 200, color: '#64748b' }}>{l.reason || '—'}</td>
                  <td style={S.td}><Badge label={l.status} colors={LEAVE_COLORS[l.status] || LEAVE_COLORS.pending} /></td>
                  <td style={S.td}>
                    {l.status === 'pending' && ePerms.manageLeaves && (
                      <div style={S.row}>
                      <button style={S.btn('success')} onClick={() => {
  const emp = employees.find(e => e.id === l.employee_id);
  const msg = `✅ Hi ${emp?.name || 'there'}! Your leave request from ${l.from_date} to ${l.to_date} (${l.leave_type}) has been approved. Enjoy your time off! 🙏\n\n— ${companyName || authUser?.name || 'Our Company'}`;
  setPendingLeaveAction({ id: l.id, status: 'approved', emp });
  setLeaveReplyMsg(msg);
  setShowLeaveReplyModal(true);
}}>✓ Approve</button>
<button style={S.btn('danger')} onClick={() => {
  const emp = employees.find(e => e.id === l.employee_id);
  const msg = `❌ Hi ${emp?.name || 'there'}! Your leave request from ${l.from_date} to ${l.to_date} (${l.leave_type}) has been rejected. Please contact your manager for more details.\n\n— ${companyName || authUser?.name || 'Our Company'}`;
  setPendingLeaveAction({ id: l.id, status: 'rejected', emp });
  setLeaveReplyMsg(msg);
  setShowLeaveReplyModal(true);
}}>✕ Reject</button>
                      </div>
                    )}
                  </td>
                </tr>
              ));
            })()}
          </tbody>
        </table>
        </div>
      </div>
       {showLeaveReplyModal && pendingLeaveAction && (
        <Modal title={pendingLeaveAction.status === 'approved' ? '✅ Approve Leave' : '❌ Reject Leave'} onClose={() => { setShowLeaveReplyModal(false); setPendingLeaveAction(null); }} width={480}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontSize: 13, color: '#64748b' }}>
              Customize the message sent to <strong>{pendingLeaveAction.emp?.name}</strong> via WhatsApp & Email.
            </div>
            <textarea value={leaveReplyMsg} onChange={e => setLeaveReplyMsg(e.target.value)} rows={6}
              style={{ padding: '10px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', outline: 'none', color: '#0f172a' }} />
            <button onClick={async () => {
              await handleLeaveStatus(pendingLeaveAction.id, pendingLeaveAction.status, leaveReplyMsg);
              setShowLeaveReplyModal(false);
              setPendingLeaveAction(null);
              setLeaveReplyMsg('');
            }} style={{ ...S.saveBtn, background: pendingLeaveAction.status === 'approved' ? '#16a34a' : '#dc2626' }}>
              {pendingLeaveAction.status === 'approved' ? '✅ Approve & Send' : '❌ Reject & Send'}
            </button>
          </div>
        </Modal>
      )}
    </div>

  );

  // ─── PAYROLL TAB ──────────────────────────────────────────────────────────
  const renderPayroll = () => {
    const totalNet = payroll.reduce((s, p) => s + p.net, 0);
    const totalPaid = payroll.filter(p => p.is_paid).reduce((s, p) => s + p.net, 0);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {payrollLoadError && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#991b1b', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <span>⚠️ {payrollLoadError}</span>
            <button onClick={fetchPayroll} style={{ padding: '5px 12px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 11, cursor: 'pointer', flexShrink: 0 }}>
              Retry
            </button>
          </div>
        )}
        <div className="emp-grid4" style={S.grid4}>
          <div style={S.statCard}><div style={S.statVal}>{currSym}{totalNet.toLocaleString()}</div><div style={S.statLabel}>Total Payroll</div></div>
          <div style={S.statCard}><div style={S.statVal}>{currSym}{totalPaid.toLocaleString()}</div><div style={S.statLabel}>Paid</div></div>
          <div style={S.statCard}><div style={S.statVal}>{currSym}{(totalNet - totalPaid).toLocaleString()}</div><div style={S.statLabel}>Pending</div></div>
          <div style={S.statCard}><div style={S.statVal}>{unpaidPayroll}</div><div style={S.statLabel}>Unpaid Records</div></div>
        </div>
        <div style={S.card}>
          <div style={S.cardHeader}>
            <div style={S.row}>
              <span style={S.cardTitle}>Payroll</span>
              <Select label="" value={payMonth} onChange={e => setPayMonth(Number(e.target.value))} style={{ width: 100, padding: '4px 8px', fontSize: 12 }}>
                {MONTHS.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
              </Select>
              <Input label="" type="number" value={payYear} onChange={e => setPayYear(Number(e.target.value))} style={{ width: 80, padding: '4px 8px', fontSize: 12 }} />
            </div>
            <div style={S.row}>
              <button style={S.btn('ghost')} onClick={fetchPayroll}>↺ Refresh</button>
            
            </div>
          </div>
          <div style={{ padding: '12px 20px', borderBottom: '1px solid #f1f5f9' }}>
            <input
              value={payrollSearch}
              onChange={e => setPayrollSearch(e.target.value)}
              placeholder="Search by employee or status..."
              style={{
                width: '100%', padding: '8px 14px', borderRadius: 9, border: '1.5px solid #e2e8f0',
                fontSize: 13, outline: 'none', fontFamily: 'inherit', color: '#0f172a', boxSizing: 'border-box' as const,
              }}
              onFocus={e => e.currentTarget.style.borderColor = '#6366f1'}
              onBlur={e => e.currentTarget.style.borderColor = '#e2e8f0'}
            />
          </div>
          <div className="emp-table-wrap">
          <table style={S.table}>
            <thead><tr>{['Employee','Month','Days Present','Base','Bonus','Deductions','Net Pay','Status',''].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
            <tbody>
              {(() => {
                const filteredPayroll = payroll.filter(p => {
                  const q = payrollSearch.toLowerCase();
                  if (!q) return true;
                  return (
                    (p.employee_name || '').toLowerCase().includes(q) ||
                    (p.is_paid ? 'paid' : 'unpaid').includes(q)
                  );
                });
                if (filteredPayroll.length === 0) {
                  return (
                    <tr><td colSpan={9} style={{ ...S.td, textAlign: 'center', padding: 48, color: '#94a3b8' }}>
                      <div style={{ fontSize: 28, marginBottom: 6 }}>💰</div>
                      <div style={{ fontWeight: 600 }}>{payrollSearch ? 'No matching payroll records' : `No payroll generated for ${MONTHS[payMonth-1]} ${payYear}`}</div>
                      {!payrollSearch && <div style={{ fontSize: 12, marginTop: 4 }}>Click "+ Generate" to create salary records</div>}
                    </td></tr>
                  );
                }
                return filteredPayroll.map((p, i) => (
                  <tr key={p.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={S.td}>
                      <div style={S.name}>{p.employee_name || `#${p.employee_id}`}</div>
                      {(() => {
                        const emp = employees.find(e => e.id === p.employee_id);
                        return emp && (emp.phone || emp.email) ? (
                          <div style={S.meta}>{emp.phone || ''}{emp.phone && emp.email ? ' · ' : ''}{emp.email || ''}</div>
                        ) : null;
                      })()}
                    </td>
                    <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 12 }}>{MONTHS[p.month-1]} {p.year}</td>
                    <td style={S.td}><span style={{ fontWeight: 700 }}>{p.present_days}</span><span style={{ color: '#94a3b8', fontSize: 11 }}> / {p.working_days}</span></td>
                    <td style={{ ...S.td, fontFamily: 'monospace' }}>{currSym}{p.base.toLocaleString()}</td>
                    <td style={{ ...S.td, fontFamily: 'monospace', color: '#16a34a' }}>{p.bonus > 0 ? `+${currSym}${p.bonus.toLocaleString()}` : '—'}</td>
                    <td style={{ ...S.td, fontFamily: 'monospace', color: '#dc2626' }}>{p.deductions > 0 ? `-${currSym}${p.deductions.toLocaleString()}` : '—'}</td>
                    <td style={{ ...S.td, fontFamily: 'monospace', fontWeight: 800, fontSize: 14, color: '#0f172a' }}>{currSym}{p.net.toLocaleString()}</td>
                    <td style={S.td}><Badge label={p.is_paid ? 'Paid' : 'Unpaid'} colors={p.is_paid ? STATUS_COLORS.present : STATUS_COLORS.absent} /></td>
                    <td style={S.td}>
                      {!p.is_paid && ePerms.managePayroll && (
                        <button style={S.btn('success')} onClick={() => handleMarkPaid(p.id)}>✓ Mark Paid</button>
                      )}
                      {p.paid_at && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{parseUtc(p.paid_at).toLocaleDateString()}</div>}
                    </td>
                  </tr>
                ));
              })()}
            </tbody>
          </table>
          </div>
        </div>
      </div>
    );
  };

  // ─── EMPLOYEE MODAL — Biometric Section (Sleek Scan-to-Pay Design Parity) ───
  const renderBiometricSection = (empId: number) => {
    const emp = employees.find(e => e.id === empId) || (editingEmp?.id === empId ? editingEmp : null);
    if (!emp) return null;
    const isFpEnrolled = !!(emp.webauthn_enrolled || (emp as any).webauthn_credential_id || bioStatus === 'success');

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        
        {/* 1. Face ID Card */}
        <div style={{
          border: `1.5px solid ${emp.face_enrolled ? '#bbf7d0' : '#e2e8f0'}`,
          borderRadius: 16, padding: 16,
          background: emp.face_enrolled ? '#f0fdf4' : '#ffffff',
          transition: 'all 0.15s ease',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12,
              background: emp.face_enrolled ? '#dcfce7' : '#f5f3ff',
              border: `1px solid ${emp.face_enrolled ? '#86efac' : '#ddd6fe'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
            }}>
              {emp.face_enrolled ? '✅' : '👁️'}
            </div>
            <div style={{ flex: 1, textAlign: 'left' }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: '#0f172a' }}>Face Recognition</div>
              <div style={{ fontSize: 11, color: emp.face_enrolled ? '#15803d' : '#64748b', fontWeight: 600 }}>
                {emp.face_enrolled ? 'Face ID enrolled & verified' : 'Touchless entrance kiosk check-in'}
              </div>
            </div>
            {!emp.face_enrolled ? (
              <button
                onClick={() => { setShowAddEmp(false); startFaceRegisterEmp(emp); }}
                style={{ background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 10, padding: '8px 14px', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}
              >
                Scan Face
              </button>
            ) : (
              <button
                onClick={() => handleEmpBiometricGuard(emp, 'face')}
                style={{ background: '#ffffff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 10, padding: '6px 12px', fontSize: 11, fontWeight: 800, cursor: 'pointer' }}
              >
                Change (OTP)
              </button>
            )}
          </div>
        </div>

        {/* 2. Fingerprint Card */}
        <div style={{
          border: `1.5px solid ${isFpEnrolled ? '#bbf7d0' : '#e2e8f0'}`,
          borderRadius: 16, padding: 16,
          background: isFpEnrolled ? '#f0fdf4' : '#ffffff',
          transition: 'all 0.15s ease',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: (!isFpEnrolled && empPhoneEnrollQR) ? 14 : 0 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12,
              background: isFpEnrolled ? '#dcfce7' : '#ecfdf5',
              border: `1px solid ${isFpEnrolled ? '#86efac' : '#a7f3d0'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
            }}>
              {isFpEnrolled ? '✅' : '👆'}
            </div>
            <div style={{ flex: 1, textAlign: 'left' }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: '#0f172a' }}>Fingerprint Biometric</div>
              <div style={{ fontSize: 11, color: isFpEnrolled ? '#15803d' : '#64748b', fontWeight: 600 }}>
                {isFpEnrolled ? 'Fingerprint registered & active' : 'Touch phone sensor or USB scanner'}
              </div>
            </div>
            {isFpEnrolled && (
              <button
                onClick={() => handleEmpBiometricGuard(emp, 'fingerprint')}
                style={{ background: '#ffffff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 10, padding: '6px 12px', fontSize: 11, fontWeight: 800, cursor: 'pointer' }}
              >
                Change (OTP)
              </button>
            )}
          </div>

          {/* 📱 Scan with Phone Section (Only if not enrolled) */}
          {!isFpEnrolled && (
            <div style={{ marginTop: 10 }}>
              {!empPhoneEnrollQR ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => startEmpPhoneEnrollment(emp.id)}
                    style={{
                      width: '100%', padding: '12px', background: 'linear-gradient(135deg, #10b981, #059669)',
                      color: '#fff', border: 'none', borderRadius: 12, fontSize: 13, fontWeight: 800,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      boxShadow: '0 4px 14px rgba(16,185,129,0.3)',
                    }}
                  >
                    📱 Scan QR with Phone to Enroll Fingerprint
                  </button>
                  <button
                    type="button"
                    onClick={() => handleWebAuthnEnroll(emp.id)}
                    disabled={bioStatus === 'enrolling'}
                    style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 11, fontWeight: 700, cursor: 'pointer', marginTop: 2 }}
                  >
                    {bioStatus === 'enrolling' ? '⏳ Waiting for sensor...' : 'Or click to use device sensor directly ▼'}
                  </button>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '10px 0' }}>
                  <div style={{
                    padding: 12, background: '#fff', borderRadius: 20,
                    border: '2px solid #e2e8f0', margin: '0 auto 12px',
                    width: 190, height: 190,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                  }}>
                    <img src={empPhoneEnrollQR} alt="Employee Phone QR" style={{ width: 165, height: 165, borderRadius: 8 }} />
                  </div>
                  <div style={{
                    background: '#eef2ff', borderRadius: 99, padding: '7px 16px',
                    fontSize: 11.5, color: '#4f46e5', fontWeight: 800,
                    display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 8,
                  }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#6366f1', animation: 'pulse 1.5s infinite' }} />
                    <span>Waiting for employee fingerprint on phone...</span>
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>
                    Point camera at QR to touch fingerprint sensor
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {bioMessage && (
          <div style={{
            fontSize: 12, fontWeight: 700, textAlign: 'center',
            color: bioStatus === 'success' ? '#15803d' : '#dc2626',
          }}>
            {bioMessage}
          </div>
        )}
      </div>
    );
  };

  // ─── RENDER ───────────────────────────────────────────────────────────────
  return (
    <>
    <style>{`
      @media (max-width: 768px) {
        .emp-grid4 { grid-template-columns: repeat(2, 1fr) !important; }
        .emp-grid5 { grid-template-columns: repeat(2, 1fr) !important; }
        .emp-table-wrap { overflow-x: auto; }
        .emp-table-wrap table { min-width: 700px; }
        .emp-attendance-layout { grid-template-columns: 1fr !important; }
        .emp-scanner-row { flex-wrap: wrap !important; }
      }
    `}</style>
    <div style={S.page}>
      <div style={S.header}>
        <div style={S.headerTop}>
          <div>
            <div style={S.title}>👔 Employee Management</div>
            <div style={S.subtitle}>{activeCount} active employees · {new Date().toLocaleDateString('en-IN', { dateStyle: 'long' })}</div>
          </div>
          {isEmpOwner && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button onClick={() => setShowKioskSetup(true)} style={{
                height: 36, padding: '0 14px', borderRadius: 10,
                border: '1.5px solid #bfdbfe', background: '#eff6ff',
                color: '#1d4ed8', fontSize: 13, fontWeight: 700,
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                transition: 'all 0.15s ease',
              }}>📺 Kiosk Setup</button>
              <button onClick={() => { setShowScanSettings(true); fetchScanSettings(); }} style={{
                height: 36, padding: '0 14px', borderRadius: 10,
                border: '1.5px solid #e9d5ff', background: '#faf5ff',
                color: '#7c3aed', fontSize: 13, fontWeight: 700,
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                transition: 'all 0.15s ease',
              }}>⚙️ Scan Settings</button>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 2, paddingBottom: 0 }}>
          {visibleTabs.map(t => {
            const labels = { staff:'👥 Staff', attendance:'📅 Attendance', leaves:'🏖️ Leaves', payroll:'💰 Payroll' };
            const active = tab === t;
            return (
              <button key={t} onClick={() => setTab(t)} style={{
                padding: '10px 18px', border: 'none', cursor: 'pointer', fontSize: 13,
                fontWeight: active ? 700 : 500, background: 'none',
                color: active ? '#0f172a' : '#94a3b8',
                borderBottom: active ? '2.5px solid #0f172a' : '2.5px solid transparent',
                transition: 'all 0.15s', fontFamily: 'inherit',
              }}>{labels[t]}</button>
            );
          })}
        </div>
      </div>

      <div style={S.body}>
        {tab === 'staff' && renderStaff()}
        {tab === 'attendance' && renderAttendance()}
        {tab === 'leaves' && renderLeaves()}
        {tab === 'payroll' && renderPayroll()}
      </div>
{/* ── Attendance History Modal ── */}
      {showHistoryEmp && (
        <Modal title={`🕘 Attendance History — ${showHistoryEmp.name}`} onClose={() => { setShowHistoryEmp(null); setHistoryLogs([]); }} width={520}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {historyLoading ? (
              <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>Loading...</div>
            ) : historyLogs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>No scan history yet</div>
            ) : (
              <div style={{ maxHeight: 400, overflowY: 'auto' as const, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {historyLogs.map(l => (
                  <div key={l.id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 14px', borderRadius: 8, border: '1px solid #f1f5f9',
                    background: l.scan_type === 'IN' ? '#f0fdf4' : '#fef2f2',
                  }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                        {l.scan_type === 'IN' ? '✅ Checked In' : '🔴 Checked Out'}
                      </div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                        {l.date} · {l.time} · via {l.source.toUpperCase()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal>
      )}
{/* ── Kiosk Setup Modal ── */}
      {showKioskSetup && (() => {
        const kioskUrl = `${window.location.origin}/kiosk/employee?key=${deviceApiKey}`;
        const copy = (text: string) => navigator.clipboard.writeText(text);
        const currentOutletName = outlets.find(o => o.id === outletId)?.name;
        return (
          <Modal title="📺 Kiosk Setup" onClose={() => setShowKioskSetup(false)} width={480}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                Set up a self check-in screen for employees at your entrance.
              </div>
              {outletId ? (
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '8px 12px', fontSize: 12, fontWeight: 700, color: '#1d4ed8' }}>
                  🏪 This kiosk key is for: {currentOutletName || `Outlet #${outletId}`}
                </div>
              ) : (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '8px 12px', fontSize: 12, fontWeight: 700, color: '#dc2626' }}>
                  ⚠ No branch selected — pick a branch from the top navbar before generating a key.
                </div>
              )}

              {!deviceApiKey ? (
                <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: 14, fontSize: 13, color: '#c2410c', fontWeight: 600 }}>
                  ⚠️ No device key set up yet for your account.
                  <button onClick={async () => {
                    if (!outletId) {
                      alert('Select a branch from the top navbar first — kiosk keys must be tied to a specific outlet.');
                      return;
                    }
                    try {
                      const r = await fetch(`${BASE}/subscriptions/kiosk-devices/generate-key`, {
                        method: 'POST', headers: authHeaders(),
                        body: JSON.stringify({ outlet_id: outletId, module: 'employees' }),
                      });
                      const data = await r.json();
                      setDeviceApiKey(data.api_key || '');
                    } catch {}
                  }} disabled={!outletId} style={{ marginTop: 10, display: 'block', background: outletId ? '#c2410c' : '#e2e8f0', color: outletId ? '#fff' : '#94a3b8', border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: outletId ? 'pointer' : 'not-allowed' }}>
                    {outletId ? 'Generate Key' : 'Select a branch first'}
                  </button>
                </div>
              ) : (
                <>
                  <div>
                    <label style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Employee Kiosk URL</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input readOnly value={kioskUrl} style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 11, color: '#475569', fontFamily: 'monospace' }} />
                      <button onClick={() => copy(kioskUrl)} style={{ background: '#0f172a', color: '#fff', border: 'none', borderRadius: 8, padding: '0 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>📋 Copy</button>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {[
                      ['1', 'On a tablet, phone, or laptop, open a browser and paste the link above.'],
                      ['2', 'Allow camera access when prompted — this lets the kiosk recognize employee faces or QR badges.'],
                      ['3', 'Place the device at your staff entrance.'],
                      ['4', 'Keep the browser fullscreen and the screen always on.'],
                      ['5', 'Employees just walk up — the camera detects their face or QR and marks check-in/check-out automatically.'],
                    ].map(([num, text]) => (
                      <div key={num} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                        <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#eff6ff', color: '#1d4ed8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, flexShrink: 0 }}>{num}</div>
                        <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{text}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#15803d', fontWeight: 600 }}>
                    💡 Tip: Register each employee's face first (👁️ in the Staff table) for face-scan check-in to work.
                  </div>
                </>
              )}
            </div>
          </Modal>
        );
      })()}
      {/* ── Add/Edit Employee Modal ── */}
      {showAddEmp && (
        <Modal
          title={editingEmp ? `Edit — ${editingEmp.name} (ID: #${editingEmp.id})` : 'Add Employee'}
          onClose={closeEmpModal}
          width={560}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

            {/* PHASE: form */}
            {modalPhase === 'form' && (
              <>
                <div style={S.formGrid}>
                  <Input label="Full Name *" value={empForm.name} onChange={e => setEmpForm(p => ({ ...p, name: e.target.value }))} placeholder="John Doe" />
                  <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
  <label style={{ fontSize:10, fontWeight:700, color:'#64748b', textTransform:'uppercase' as const, letterSpacing:'0.06em' }}>Phone</label>
  {empPhoneExists && <div style={{ fontSize:11, color:'#dc2626', fontWeight:700 }}>⚠️ This phone already has an active employee</div>}
  <EmpPhoneInput value={empForm.phone} onChange={v => setEmpForm(p => ({ ...p, phone: v }))} />
</div>
                  <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
  <label style={{ fontSize:10, fontWeight:700, color:'#64748b', textTransform:'uppercase' as const, letterSpacing:'0.06em' }}>Email</label>
  {empEmailExists && <div style={{ fontSize:11, color:'#dc2626', fontWeight:700 }}>⚠️ This email already has an active employee</div>}
  <input value={empForm.email} onChange={e => setEmpForm(p => ({ ...p, email: e.target.value }))} placeholder="john@company.com"
    style={{ padding:'8px 12px', borderRadius:8, border:'1.5px solid #e2e8f0', fontSize:13, color:'#0f172a', background:'#fff', outline:'none', fontFamily:'inherit', width:'100%', boxSizing:'border-box' as const }}
    onFocus={e => e.currentTarget.style.borderColor='#6366f1'}
    onBlur={e => e.currentTarget.style.borderColor='#e2e8f0'}
  />
</div>
                  <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                    <label style={{ fontSize:10, fontWeight:700, color:'#64748b', textTransform:'uppercase' as const, letterSpacing:'0.06em' }}>Department</label>
                    <input list="dept-options" value={empForm.department}
                      onChange={e => { if (e.target.value === '+ Add custom department...') { setShowAddDept(true); setEmpForm(p => ({ ...p, department: '' })); return; } setEmpForm(p => ({ ...p, department: e.target.value })); }}
                      placeholder="HR, Tech, Sales..."
                      style={{ padding:'8px 12px', borderRadius:8, border:'1.5px solid #e2e8f0', fontSize:13, color:'#0f172a', background:'#fff', outline:'none', fontFamily:'inherit', width:'100%', boxSizing:'border-box' as const }}
                      onFocus={e => e.currentTarget.style.borderColor='#6366f1'} onBlur={e => e.currentTarget.style.borderColor='#e2e8f0'} />
                    <datalist id="dept-options">{departments.map(d => <option key={d.id} value={d.name} />)}<option value="+ Add custom department..." /></datalist>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                    <label style={{ fontSize:10, fontWeight:700, color:'#64748b', textTransform:'uppercase' as const, letterSpacing:'0.06em' }}>Designation</label>
                    <input list="desig-options" value={empForm.designation}
                      onChange={e => { if (e.target.value === '+ Add custom designation...') { setShowAddDesignation(true); setEmpForm(p => ({ ...p, designation: '' })); return; } setEmpForm(p => ({ ...p, designation: e.target.value })); }}
                      placeholder="Manager, Analyst..."
                      style={{ padding:'8px 12px', borderRadius:8, border:'1.5px solid #e2e8f0', fontSize:13, color:'#0f172a', background:'#fff', outline:'none', fontFamily:'inherit', width:'100%', boxSizing:'border-box' as const }}
                      onFocus={e => e.currentTarget.style.borderColor='#6366f1'} onBlur={e => e.currentTarget.style.borderColor='#e2e8f0'} />
                    <datalist id="desig-options">{designations.map(d => <option key={d.id} value={d.name} />)}<option value="+ Add custom designation..." /></datalist>
                  </div>
                  <Select label="Shift" value={empForm.shift_id} onChange={e => { if (e.target.value === '__custom__') { setShowAddShift(true); return; } setEmpForm(p => ({ ...p, shift_id: e.target.value })); }}>
                    <option value="">No shift</option>
                    {shifts.map(s => <option key={s.id} value={s.id}>{s.name} ({s.start_time}–{s.end_time})</option>)}
                    <option value="__custom__">+ Add custom shift...</option>
                  </Select>
                  <Select label="Salary Type" value={empForm.salary_type} onChange={e => { if (e.target.value === '__custom__') { setShowAddSalaryType(true); return; } setEmpForm(p => ({ ...p, salary_type: e.target.value })); }}>
                    <option value="fixed">Fixed Monthly</option>
                    <option value="daily">Daily Rate</option>
                    {salaryTypes.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                    <option value="__custom__">+ Add custom salary type...</option>
                  </Select>
                  {empForm.salary_type === 'fixed'
                    ? <Input label={`Monthly Salary (${currSym})`} type="number" value={empForm.monthly_salary} onChange={e => setEmpForm(p => ({ ...p, monthly_salary: e.target.value }))} placeholder="25000" />
                    : <Input label={`Daily Rate (${currSym})`}type="number" value={empForm.daily_rate} onChange={e => setEmpForm(p => ({ ...p, daily_rate: e.target.value }))} placeholder="800" />}
                  <Input label="Join Date" type="date" value={empForm.join_date} onChange={e => setEmpForm(p => ({ ...p, join_date: e.target.value }))} />
                  <Select label="Outlet" value={empForm.outlet_id} onChange={e => setEmpForm(p => ({ ...p, outlet_id: e.target.value }))}>
  <option value="">No outlet (unassigned)</option>
  {employeeOutlets.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
</Select>
                  <Select label="Role" value={empForm.role} onChange={e => { if (e.target.value === '__custom__') { setShowAddRole(true); return; } setEmpForm(p => ({ ...p, role: e.target.value })); }}>
                    <option value="staff">Staff</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                    <option value="owner">Owner</option>
                    {roles.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
                    <option value="__custom__">+ Add custom role...</option>
                  </Select>
                  <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                    <label style={{ fontSize:10, fontWeight:700, color:'#64748b', textTransform:'uppercase' as const, letterSpacing:'0.06em' }}>ZKTeco Device ID</label>
                    <input list="device-options" value={empForm.device_employee_id}
                      onChange={e => { if (e.target.value === '+ Add custom device ID...') { setShowAddDeviceId(true); setEmpForm(p => ({ ...p, device_employee_id: '' })); return; } setEmpForm(p => ({ ...p, device_employee_id: e.target.value })); }}
                      placeholder="1, 2, 101..."
                      style={{ padding:'8px 12px', borderRadius:8, border:'1.5px solid #e2e8f0', fontSize:13, color:'#0f172a', background:'#fff', outline:'none', fontFamily:'inherit', width:'100%', boxSizing:'border-box' as const }}
                      onFocus={e => e.currentTarget.style.borderColor='#6366f1'} onBlur={e => e.currentTarget.style.borderColor='#e2e8f0'} />
                    <datalist id="device-options">{deviceIds.map(d => <option key={d.id} value={d.name} />)}<option value="+ Add custom device ID..." /></datalist>
                  </div>
                </div>
                <button style={{ ...S.saveBtn, opacity: empSaving || empPhoneExists || empEmailExists ? 0.6 : 1 }} onClick={handleSaveEmployee} disabled={!empForm.name.trim() || empSaving || empPhoneExists || empEmailExists}>
  {empSaving ? 'Saving...' : editingEmp ? 'Save Changes' : 'Save & Send OTP'}
</button>
              </>
            )}

            {/* PHASE: otp */}
            {modalPhase === 'otp' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: 14, fontSize: 13, color: '#166534', fontWeight: 600 }}>
                  ✅ Employee saved! OTP sent via WhatsApp & Email.
                </div>
                <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
                  Ask the employee for the OTP they received and enter it below.
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    value={otpValue}
                    onChange={e => setOtpValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleVerifyOTP(); }}
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                    autoFocus
                    style={{ flex: 1, padding: '12px 14px', borderRadius: 9, border: `1.5px solid ${otpError ? '#fca5a5' : '#6366f1'}`, fontSize: 20, fontWeight: 800, letterSpacing: '0.3em', textAlign: 'center', outline: 'none', fontFamily: 'monospace' }}
                  />
                  <button onClick={handleVerifyOTP} disabled={otpLoading || otpValue.length !== 6}
                    style={{ padding: '12px 20px', borderRadius: 9, border: 'none', background: otpValue.length === 6 ? '#0f172a' : '#e2e8f0', color: otpValue.length === 6 ? '#fff' : '#94a3b8', fontWeight: 700, fontSize: 13, cursor: otpValue.length === 6 ? 'pointer' : 'not-allowed' }}>
                    {otpLoading ? '...' : 'Verify'}
                  </button>
                </div>
                {otpError && <div style={{ fontSize: 12, color: '#dc2626', fontWeight: 600 }}>{otpError}</div>}
                <button onClick={handleResendOTP} disabled={otpLoading}
                  style={{ background: 'none', border: 'none', color: '#6366f1', fontSize: 12, cursor: 'pointer', fontWeight: 600, textAlign: 'left' as const, padding: 0 }}>
                  Resend OTP
                </button>
              </div>
            )}

            {/* PHASE: done */}
            {modalPhase === 'done' && savedEmpId && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ textAlign: 'center' as const, padding: '8px 0' }}>
                  <div style={{ fontSize: 36 }}>✅</div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', marginTop: 6 }}>
                    {editingEmp ? 'Updated!' : 'Employee Verified!'}
                  </div>
                  {!editingEmp && <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>QR code sent via WhatsApp & Email.</div>}
                </div>
                {renderBiometricSection(savedEmpId)}
                <button style={{ ...S.saveBtn, marginTop: 4 }} onClick={closeEmpModal}>Done</button>
              </div>
            )}

          </div>
        </Modal>
      )}

      {/* ── Auxiliary modals ── */}
      {showAddRole && (
        <Modal title="Add Role" onClose={() => setShowAddRole(false)} width={360}>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <Input label="Role Name *" value={roleForm.name} onChange={e => setRoleForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Intern, Supervisor..." />
            <Select label="Outlet" value={roleForm.outlet_id} onChange={e => setRoleForm(p => ({ ...p, outlet_id: e.target.value }))}>
              <option value="">No outlet (shared across all)</option>
              {employeeOutlets.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </Select>
            <button style={S.saveBtn} onClick={handleSaveRole}>Save Role</button>
          </div>
        </Modal>
      )}
      {showAddSalaryType && (
        <Modal title="Add Salary Type" onClose={() => setShowAddSalaryType(false)} width={360}>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <Input label="Name *" value={salaryTypeForm.name} onChange={e => setSalaryTypeForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Contract, Commission..." />
            <Select label="Calculation Based On" value={salaryTypeForm.calc_type} onChange={e => setSalaryTypeForm(p => ({ ...p, calc_type: e.target.value }))}>
              <option value="fixed">Fixed Monthly</option>
              <option value="daily">Daily Rate</option>
            </Select>
            <Select label="Outlet" value={salaryTypeForm.outlet_id} onChange={e => setSalaryTypeForm(p => ({ ...p, outlet_id: e.target.value }))}>
              <option value="">No outlet (shared across all)</option>
              {employeeOutlets.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </Select>
            <button style={S.saveBtn} onClick={handleSaveSalaryType}>Save Salary Type</button>
          </div>
        </Modal>
      )}
      {showAddDept && (
        <Modal title="Add Department" onClose={() => setShowAddDept(false)} width={360}>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <Input label="Department Name *" value={deptForm.name} onChange={e => setDeptForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. HR, Tech, Sales..." />
            <Select label="Outlet" value={deptForm.outlet_id} onChange={e => setDeptForm(p => ({ ...p, outlet_id: e.target.value }))}>
              <option value="">No outlet (shared across all)</option>
              {employeeOutlets.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </Select>
            <button style={S.saveBtn} onClick={handleSaveDept}>Save Department</button>
          </div>
        </Modal>
      )}
      {showAddDesignation && (
        <Modal title="Add Designation" onClose={() => setShowAddDesignation(false)} width={360}>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <Input label="Designation Name *" value={designationForm.name} onChange={e => setDesignationForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Manager, Analyst..." />
            <Select label="Outlet" value={designationForm.outlet_id} onChange={e => setDesignationForm(p => ({ ...p, outlet_id: e.target.value }))}>
              <option value="">No outlet (shared across all)</option>
              {employeeOutlets.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </Select>
            <button style={S.saveBtn} onClick={handleSaveDesignation}>Save Designation</button>
          </div>
        </Modal>
      )}
      {showAddDeviceId && (
        <Modal title="Add Device ID" onClose={() => setShowAddDeviceId(false)} width={360}>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <Input label="Device ID *" value={deviceIdForm.name} onChange={e => setDeviceIdForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. 1, 2, 101..." />
            <Select label="Outlet" value={deviceIdForm.outlet_id} onChange={e => setDeviceIdForm(p => ({ ...p, outlet_id: e.target.value }))}>
              <option value="">No outlet (shared across all)</option>
              {employeeOutlets.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </Select>
            <button style={S.saveBtn} onClick={handleSaveDeviceId}>Save Device ID</button>
          </div>
        </Modal>
      )}
      {showShiftsList && (
        <Modal title="📋 Shifts" onClose={() => setShowShiftsList(false)} width={440}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 420, overflowY: 'auto' as const }}>
            {shifts.map(s => (
              <div key={s.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 16px',
              }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>{s.name}</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 3, fontFamily: 'monospace' }}>{s.start_time} – {s.end_time}</div>
                </div>
                {ePerms.manageEmployees && (
                  <button onClick={async () => {
                    if (!window.confirm(`Delete shift "${s.name}"?`)) return;
                    await fetch(`${BASE}/employees/shifts/${s.id}`, { method: 'DELETE', headers: authHeaders() });
                    postLog('SHIFT_DELETED', `Shift deleted: ${s.name}`);
                    fetchShifts();
                  }}
                    style={{ background:'none', border:'none', cursor:'pointer', color:'#cbd5e1', fontSize:16, padding:4, lineHeight:1, flexShrink:0 }}
                    onMouseEnter={e => e.currentTarget.style.color='#dc2626'}
                    onMouseLeave={e => e.currentTarget.style.color='#cbd5e1'}>✕</button>
                )}
              </div>
            ))}
          </div>
        </Modal>
      )}
      {showAddShift && (
        <Modal title="Add Shift" onClose={() => setShowAddShift(false)} width={400}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Input label="Shift Name *" value={shiftForm.name} onChange={e => setShiftForm(p => ({ ...p, name: e.target.value }))} placeholder="Morning, Evening, Night..." />
            <div style={S.grid2}>
              <Input label="Start Time" type="time" value={shiftForm.start_time} onChange={e => setShiftForm(p => ({ ...p, start_time: e.target.value }))} />
              <Input label="End Time" type="time" value={shiftForm.end_time} onChange={e => setShiftForm(p => ({ ...p, end_time: e.target.value }))} />
            </div>
            <Select label="Outlet" value={shiftForm.outlet_id} onChange={e => setShiftForm(p => ({ ...p, outlet_id: e.target.value }))}>
              <option value="">No outlet (shared across all)</option>
              {employeeOutlets.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </Select>
            <button style={S.saveBtn} onClick={handleSaveShift}>Create Shift</button>
          </div>
        </Modal>
      )}
      {showAddLeave && (() => {
        const needsOtp = !ePerms.manageLeaves;
        const selectedEmp = employees.find(e => String(e.id) === leaveForm.employee_id);
        const maskedPhone = selectedEmp?.phone
          ? selectedEmp.phone.replace(/(\+?\d{2,4})\d+(\d{2})$/, (_m, a, b) => `${a}${'•'.repeat(6)}${b}`)
          : '';
        const canSubmit = needsOtp ? leaveOtpVerified : true;
        return (
        <Modal title="Add Leave Request" onClose={() => setShowAddLeave(false)} width={440}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Select label="Employee *" value={leaveForm.employee_id} onChange={e => setLeaveForm(p => ({ ...p, employee_id: e.target.value }))}>
              <option value="">Select your name</option>
              {employees.filter(e => e.is_active).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </Select>

            {needsOtp && selectedEmp && (
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' as const, letterSpacing: '0.06em' }}>Phone (verification)</label>
                  <input readOnly value={maskedPhone || 'No phone on file'} style={{ padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, color: '#64748b', background: '#f8fafc' }} />
                </div>

                {leaveOtpVerified ? (
                  <div style={{ fontSize: 12, color: '#16a34a', fontWeight: 700 }}>✓ Verified — you can submit this request</div>
                ) : !leaveOtpSent ? (
                  <button onClick={handleSendLeaveOtp} disabled={leaveOtpLoading || !selectedEmp.phone}
                    style={{ padding: '9px 14px', borderRadius: 8, border: 'none', background: '#6366f1', color: '#fff', fontWeight: 700, fontSize: 13, cursor: (leaveOtpLoading || !selectedEmp.phone) ? 'not-allowed' : 'pointer', opacity: (leaveOtpLoading || !selectedEmp.phone) ? 0.6 : 1 }}>
                    {leaveOtpLoading ? 'Sending...' : '📲 Send OTP to Verify'}
                  </button>
                ) : (
                  <>
                    <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <div style={{ fontSize: 13, color: '#166534', fontWeight: 700 }}>📲 Check your WhatsApp</div>
                      <div style={{ fontSize: 12, color: '#166534' }}>
                        We sent a 6-digit code to {maskedPhone || 'your registered number'} on WhatsApp.
                        {selectedEmp?.email ? ' Not on WhatsApp? Check your email — we sent a backup copy there too.' : ''}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input value={leaveOtpValue} onChange={e => setLeaveOtpValue(e.target.value)} maxLength={6}
                        placeholder="6-digit OTP"
                        style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: '1.5px solid #6366f1', fontSize: 16, fontWeight: 700, letterSpacing: '0.2em', textAlign: 'center', fontFamily: 'monospace', outline: 'none' }} />
                      <button onClick={handleVerifyLeaveOtp} disabled={leaveOtpLoading || leaveOtpValue.length !== 6}
                        style={{ padding: '10px 16px', borderRadius: 8, border: 'none', background: leaveOtpValue.length === 6 ? '#0f172a' : '#e2e8f0', color: leaveOtpValue.length === 6 ? '#fff' : '#94a3b8', fontWeight: 700, fontSize: 13, cursor: leaveOtpValue.length === 6 ? 'pointer' : 'not-allowed' }}>
                        {leaveOtpLoading ? '...' : 'Verify'}
                      </button>
                    </div>
                    <button onClick={handleSendLeaveOtp} disabled={leaveOtpLoading} style={{ background: 'none', border: 'none', color: '#6366f1', fontSize: 12, fontWeight: 600, cursor: 'pointer', textAlign: 'left' as const, padding: 0 }}>Resend OTP</button>
                  </>
                )}
                {leaveOtpError && <div style={{ fontSize: 12, color: '#dc2626', fontWeight: 600 }}>{leaveOtpError}</div>}
              </div>
            )}

            <div style={S.grid2}>
              <Input label="From Date" type="date" value={leaveForm.from_date} onChange={e => setLeaveForm(p => ({ ...p, from_date: e.target.value }))} />
              <Input label="To Date" type="date" value={leaveForm.to_date} onChange={e => setLeaveForm(p => ({ ...p, to_date: e.target.value }))} />
            </div>
            <Select label="Leave Type" value={leaveForm.leave_type} onChange={e => setLeaveForm(p => ({ ...p, leave_type: e.target.value }))}>
              <option value="casual">Casual</option>
              <option value="sick">Sick</option>
              <option value="unpaid">Unpaid</option>
            </Select>
            <Input label="Reason" value={leaveForm.reason} onChange={e => setLeaveForm(p => ({ ...p, reason: e.target.value }))} placeholder="Optional reason..." />
            <button style={{ ...S.saveBtn, opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? 'pointer' : 'not-allowed' }} onClick={handleAddLeave} disabled={!canSubmit}>
              {needsOtp && !leaveOtpVerified ? 'Verify OTP first' : 'Submit Leave'}
            </button>
          </div>
        </Modal>
        );
      })()}
      {showGenPayroll && (
        <Modal title={`Generate Payroll — ${MONTHS[payMonth-1]} ${payYear}`} onClose={() => setShowGenPayroll(false)} width={440}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Select label="Employee *" value={genEmpId} onChange={e => setGenEmpId(e.target.value)}>
              <option value="">Select employee</option>
              {employees.filter(e => e.is_active).map(e => <option key={e.id} value={e.id}>{e.name} ({e.salary_type === 'fixed' ? `${currSym}${e.monthly_salary}/mo` : `${currSym}${e.daily_rate}/day`})</option>)}
            </Select>
            <div style={S.grid2}>
              <Input label={`Bonus (${currSym})`} type="number" value={genBonus} onChange={e => setGenBonus(e.target.value)} placeholder="0" />
              <Input label={`Deductions (${currSym})`} type="number" value={genDeductions} onChange={e => setGenDeductions(e.target.value)} placeholder="0" />
            </div>
            <Input label="Note" value={genNote} onChange={e => setGenNote(e.target.value)} placeholder="Optional note..." />
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>
              💡 Salary auto-calculated from attendance. Fixed salary employees get full monthly pay regardless of days.
            </div>
            <button style={S.saveBtn} onClick={handleGenPayroll}>Generate Salary</button>
          </div>
        </Modal>
      )}

      {/* ── QR Kiosk Modal ── */}
      {showKiosk && (
        <Modal title="📷 QR Kiosk Scanner" onClose={() => { stopKioskCamera(); setShowKiosk(false); setKioskInput(''); setKioskResult(null); }} width={480}>
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

  {/* Camera at top */}
  <div style={{ borderRadius:12, overflow:'hidden', background:'#000', position:'relative', minHeight:200 }}>
    <video
      ref={kioskVideoRef}
      autoPlay playsInline muted
      style={{ width:'100%', display:'block', maxHeight:240, objectFit:'cover' }}
    />
    {kioskCameraOn && (
      <div style={{
        position:'absolute', inset:0,
        display:'flex', alignItems:'center', justifyContent:'center',
        pointerEvents:'none',
      }}>
        <div style={{
          width:180, height:180,
          border:'3px solid #6366f1',
          borderRadius:12,
          boxShadow:'0 0 0 9999px rgba(0,0,0,0.45)',
        }} />
      </div>
    )}
    <div style={{
      position:'absolute', bottom:8, left:0, right:0,
      textAlign:'center', fontSize:11, fontWeight:700,
      color: kioskCameraOn ? '#a5f3fc' : '#94a3b8',
    }}>
      {kioskCameraOn ? '● Scanning for QR code...' : 'Starting camera...'}
    </div>
  </div>

  {/* Keep scanning toggle */}
  <div style={{
    display:'flex', alignItems:'center', justifyContent:'space-between',
    background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:8, padding:'8px 12px',
  }}>
    <div>
      <div style={{ fontSize:12, fontWeight:700, color:'#0f172a' }}>🔁 Keep scanning</div>
      <div style={{ fontSize:11, color:'#94a3b8' }}>Camera stays on after each scan</div>
    </div>
    <div
      onClick={() => setKioskKeepScanning(s => !s)}
      style={{
        width:36, height:20, borderRadius:99, cursor:'pointer',
        background: kioskKeepScanning ? '#6366f1' : '#cbd5e1',
        position:'relative', transition:'background 0.2s', flexShrink:0,
      }}
    >
      <div style={{
        position:'absolute', top:2,
        left: kioskKeepScanning ? 18 : 2,
        width:16, height:16, borderRadius:'50%',
        background:'#fff', transition:'left 0.2s',
        boxShadow:'0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </div>
  </div>

  {/* Manual input */}
  <div style={{ display:'flex', gap:8 }}>
    <input
      value={kioskInput}
      onChange={e => setKioskInput(e.target.value)}
      onKeyDown={e => { if (e.key === 'Enter') handleKioskScan(kioskInput); }}
      placeholder="Or paste / type QR token manually..."
      style={{ flex:1, padding:'12px 16px', borderRadius:9, border:'2px solid #6366f1', fontSize:14, outline:'none', fontFamily:'monospace', letterSpacing:'0.05em' }}
    />
    <button
      onClick={() => handleKioskScan(kioskInput)}
      disabled={kioskLoading || !kioskInput.trim()}
      style={{ padding:'12px 20px', borderRadius:9, border:'none', background: kioskInput.trim() ? '#0f172a' : '#e2e8f0', color: kioskInput.trim() ? '#fff' : '#94a3b8', fontWeight:700, fontSize:13, cursor: kioskInput.trim() ? 'pointer' : 'not-allowed' }}>
      {kioskLoading ? '...' : '✓ Scan'}
    </button>
  </div>

  {/* Result */}
  {kioskResult && (
    <div style={{ borderRadius:12, padding:20, textAlign:'center' as const, background: kioskResult.status === 'success' ? '#f0fdf4' : '#fef2f2', border:`1px solid ${kioskResult.status === 'success' ? '#bbf7d0' : '#fecaca'}` }}>
      {kioskResult.status === 'success' ? (
        <>
          <div style={{ fontSize:40, marginBottom:8 }}>{kioskResult.scan_type === 'IN' ? '✅' : '🔴'}</div>
          <div style={{ fontSize:18, fontWeight:800, color:'#0f172a' }}>{kioskResult.name}</div>
          <div style={{ fontSize:14, color:'#16a34a', fontWeight:700, marginTop:4 }}>Checked {kioskResult.scan_type} · {kioskResult.time}</div>
          <div style={{ fontSize:11, color:'#64748b', marginTop:6 }}>Notification sent</div>
        </>
      ) : (
        <>
          <div style={{ fontSize:40, marginBottom:8 }}>❌</div>
          <div style={{ fontSize:14, fontWeight:700, color:'#dc2626' }}>Invalid QR token</div>
        </>
      )}
    </div>
  )}

  {/* Quick scan list */}
  <div style={{ borderTop:'1px solid #f1f5f9', paddingTop:12 }}>
    <div style={{ fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase' as const, letterSpacing:'0.06em', marginBottom:8 }}>Quick Scan</div>
    <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight:160, overflowY:'auto' as const }}>
      {employees.filter(e => e.is_active && e.qr_token).map(e => (
        <button key={e.id} onClick={() => handleKioskScan(e.qr_token!)}
          style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 12px', borderRadius:8, border:'1px solid #e2e8f0', background:'#fafafa', cursor:'pointer', textAlign:'left' as const }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontWeight:700, fontSize:13, color:'#0f172a' }}>{e.name}</span>
            <span style={{ fontSize: 10, fontWeight: 800, color: '#4f46e5', background: '#eef2ff', padding: '1px 5px', borderRadius: 4 }}>#{e.id}</span>
          </div>
          <span style={{ fontFamily:'monospace', fontSize:10, color:'#94a3b8' }}>{e.qr_token?.slice(0,16)}...</span>
        </button>
      ))}
    </div>
  </div>

</div>
        </Modal>
      )}

      {/* ── Biometric Standalone Modal ── */}
      {showBiometric && (
        <Modal title="🔐 Biometric Attendance" onClose={() => { setShowBiometric(false); setBioStatus('idle'); setBioMessage(''); }} width={440}>
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            <div style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:10, padding:14, fontSize:12, color:'#64748b', lineHeight:1.6 }}>
              Uses device fingerprint / Face ID. Enroll once per employee, scan each day.
            </div>
            <div style={{ border:'1px solid #e2e8f0', borderRadius:10, padding:16 }}>
              <div style={{ fontSize:12, fontWeight:800, color:'#0f172a', marginBottom:10 }}>Enroll</div>
              <Select label="Employee" value={bioEmpId} onChange={e => { setBioEmpId(e.target.value); setBioStatus('idle'); setBioMessage(''); }}>
                <option value="">Select employee</option>
                {employees.filter(e => e.is_active).map(e => <option key={e.id} value={e.id}>{e.name} {e.webauthn_enrolled ? '✓' : ''}</option>)}
              </Select>
              <button onClick={() => handleWebAuthnEnroll()} disabled={!bioEmpId || bioStatus === 'enrolling'}
                style={{ marginTop:10, padding:'9px 16px', borderRadius:8, border:'none', background: bioEmpId ? '#6366f1' : '#e2e8f0', color: bioEmpId ? '#fff' : '#94a3b8', fontWeight:700, fontSize:13, cursor: bioEmpId ? 'pointer' : 'not-allowed', width:'100%' }}>
                {bioStatus === 'enrolling' ? '⏳ Waiting...' : '👆 Enroll'}
              </button>
            </div>
            <div style={{ border:'1px solid #e2e8f0', borderRadius:10, padding:16 }}>
              <div style={{ fontSize:12, fontWeight:800, color:'#0f172a', marginBottom:10 }}>Mark Attendance</div>
              <button onClick={handleWebAuthnScan} disabled={bioStatus === 'scanning'}
                style={{ padding:'12px 16px', borderRadius:8, border:'none', background:'#0f172a', color:'#fff', fontWeight:700, fontSize:14, cursor:'pointer', width:'100%' }}>
                {bioStatus === 'scanning' ? '⏳ Waiting...' : '🔐 Scan Now'}
              </button>
            </div>
            {bioMessage && (
              <div style={{ borderRadius:10, padding:14, fontSize:13, fontWeight:600, textAlign:'center' as const, background: bioStatus === 'success' ? '#f0fdf4' : '#fef2f2', color: bioStatus === 'success' ? '#16a34a' : '#dc2626', border: `1px solid ${bioStatus === 'success' ? '#bbf7d0' : '#fecaca'}` }}>
                {bioMessage}
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* ── Face Register Modal ── */}
      {showFaceRegisterEmp && (
        <Modal title={`📸 Register Face — ${faceRegisterEmpName}`} onClose={() => { stopFaceEmpStream(); setShowFaceRegisterEmp(false); setFaceRegisterEmpStatus('idle'); }} width={360}>
          <div style={{ display:'flex', flexDirection:'column', gap:16, textAlign:'center' as const }}>
            {faceRegisterEmpStatus === 'done' ? (
              <>
                <div style={{ fontSize:56 }}>✅</div>
                <div style={{ fontSize:15, fontWeight:800, color:'#15803d' }}>Face Registered!</div>
                <button style={S.saveBtn} onClick={() => { setShowFaceRegisterEmp(false); setFaceRegisterEmpStatus('idle'); fetchEmployees(); }}>Done</button>
              </>
            ) : faceRegisterEmpStatus === 'error' ? (
              <>
                <div style={{ fontSize:56 }}>❌</div>
                <div style={{ fontSize:13, fontWeight:700, color:'#dc2626' }}>Camera access denied</div>
                <button style={S.saveBtn} onClick={() => { stopFaceEmpStream(); setShowFaceRegisterEmp(false); setFaceRegisterEmpStatus('idle'); }}>Close</button>
              </>
            ) : (
              <>
                <div style={{ borderRadius:12, overflow:'hidden', background:'#000', position:'relative', height:220, marginBottom:4 }}>
                  <video ref={faceEmpVideoRef} autoPlay playsInline muted style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                  <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', pointerEvents:'none' }}>
                    <div style={{ width:150, height:180, border:'3px solid #7c3aed', borderRadius:'50%', boxShadow:'0 0 0 9999px rgba(0,0,0,0.4)' }} />
                  </div>
                </div>
                <div style={{ fontSize:12, color:'#64748b' }}>
                  {faceRegisterEmpStatus === 'loading' ? 'Starting camera...' : 'Position face in the oval and tap Capture'}
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={() => { stopFaceEmpStream(); setShowFaceRegisterEmp(false); setFaceRegisterEmpStatus('idle'); }}
                    style={{ flex:1, padding:'9px', borderRadius:8, border:'1px solid #e2e8f0', background:'#f8fafc', color:'#475569', fontWeight:700, fontSize:13, cursor:'pointer' }}>Cancel</button>
                  <button onClick={captureFaceEmp} disabled={faceRegisterEmpStatus !== 'capturing'}
                    style={{ flex:1, padding:'9px', borderRadius:8, border:'none', background: faceRegisterEmpStatus !== 'capturing' ? '#e2e8f0' : '#7c3aed', color: faceRegisterEmpStatus !== 'capturing' ? '#94a3b8' : '#fff', fontWeight:700, fontSize:13, cursor: faceRegisterEmpStatus !== 'capturing' ? 'not-allowed' : 'pointer' }}>
                    {faceRegisterEmpStatus === 'loading' ? 'Loading...' : '📸 Capture'}
                  </button>
                </div>
              </>
            )}
          </div>
        </Modal>
      )}
{/* ── Calendar QR Scanner Modal ── */}
      {showCalQR && (
        <Modal title="📷 Scan Employee QR — Monthly View" onClose={() => { stopCalQRCamera(); setShowCalQR(false); }} width={440}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ borderRadius: 12, overflow: 'hidden', background: '#000', position: 'relative', height: 300 }}>
              <video
                ref={calQRVideoRef}
                autoPlay playsInline muted
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
              {calQRCameraOn && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                  <div style={{ width: 180, height: 180, border: '3px solid #6366f1', borderRadius: 12, boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)' }} />
                </div>
              )}
              <div style={{ position: 'absolute', bottom: 10, left: 0, right: 0, textAlign: 'center', fontSize: 11, fontWeight: 700, color: calQRCameraOn ? '#a5f3fc' : '#94a3b8' }}>
                {calQRCameraOn ? '● Scanning for QR code...' : 'Starting camera...'}
              </div>
            </div>
            <div style={{ fontSize: 12, color: '#64748b', textAlign: 'center' as const }}>
              Point at an employee's QR badge — it will auto-select them and load their calendar.
            </div>
            <button onClick={() => { stopCalQRCamera(); setShowCalQR(false); }} style={{
              padding: '9px', borderRadius: 8, border: '1px solid #e2e8f0',
              background: '#f8fafc', color: '#475569', fontWeight: 700, fontSize: 13, cursor: 'pointer',
            }}>Cancel</button>
          </div>
        </Modal>
      )}
      {/* ── Face Scan Modal ── */}
      {showFaceScanEmp && (
        <Modal title="👁️ Face Scan Attendance" onClose={() => { stopFaceEmpStream(); setShowFaceScanEmp(false); setFaceScanEmpResult(null); setFaceScanEmpStatus('scanning'); }} width={360}>
          <div style={{ display:'flex', flexDirection:'column', gap:16, textAlign:'center' as const }}>
            {faceScanEmpStatus === 'scanning' ? (
              <>
                <div style={{ borderRadius:12, overflow:'hidden', background:'#000', position:'relative', height:220 }}>
                  <video ref={faceEmpVideoRef} autoPlay playsInline muted style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                  <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', pointerEvents:'none' }}>
                    <div style={{ width:150, height:180, border:'3px solid #7c3aed', borderRadius:'50%', boxShadow:'0 0 0 9999px rgba(0,0,0,0.4)' }} />
                  </div>
                </div>
                <div style={{ fontSize:12, color:'#64748b' }}>Scanning... keep face visible</div>
                <button onClick={() => { stopFaceEmpStream(); setShowFaceScanEmp(false); }}
                  style={{ padding:'9px', borderRadius:8, border:'1px solid #e2e8f0', background:'#f8fafc', color:'#475569', fontWeight:700, fontSize:13, cursor:'pointer' }}>Cancel</button>
              </>
            ) : (
              <>
                <div style={{ fontSize:56 }}>
                  {faceScanEmpStatus === 'match' ? (faceScanEmpResult?.scan_type === 'IN' ? '✅' : '🔴') : faceScanEmpStatus === 'nomatch' ? '❓' : '❌'}
                </div>
                <div style={{ fontSize:15, fontWeight:800, color:'#0f172a' }}>
                  {faceScanEmpStatus === 'match' ? (faceScanEmpResult?.scan_type === 'IN' ? 'Checked In!' : 'Checked Out!') : faceScanEmpStatus === 'nomatch' ? 'Face Not Recognized' : 'Error'}
                </div>
                {faceScanEmpStatus === 'error' && faceScanEmpResult?.message && (
                  <div style={{ fontSize:13, color:'#dc2626', lineHeight:1.5 }}>{faceScanEmpResult.message}</div>
                )}
                {faceScanEmpResult?.name && (
                  <div style={{ fontSize:13, color:'#64748b' }}>
                    <strong>{faceScanEmpResult.name}</strong>
                    {faceScanEmpStatus === 'match' && ` — at ${faceScanEmpResult.time}`}
                  </div>
                )}
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={() => { setFaceScanEmpResult(null); setFaceScanEmpStatus('scanning'); startFaceScanEmp(); }}
                    style={{ flex:1, padding:'9px', borderRadius:8, border:'1px solid #e9d5ff', background:'#fdf4ff', color:'#7c3aed', fontWeight:700, fontSize:13, cursor:'pointer' }}>Scan Again</button>
                  <button onClick={() => { stopFaceEmpStream(); setShowFaceScanEmp(false); setFaceScanEmpResult(null); setFaceScanEmpStatus('scanning'); }}
                    style={{ flex:1, padding:'9px', borderRadius:8, border:'1px solid #e2e8f0', background:'#f8fafc', color:'#475569', fontWeight:700, fontSize:13, cursor:'pointer' }}>Done</button>
                </div>
              </>
            )}
          </div>
        </Modal>
      )}

    {/* ── 🔒 Anti-Fraud Biometric Security OTP Modal ── */}
      {showEmpBioOtpModal && empBioTarget && (
        <Modal title="🔒 Biometric Security Verification" onClose={() => { setShowEmpBioOtpModal(false); setEmpBioTarget(null); setEmpBioOtpInput(''); }} width={400}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, textAlign: 'center' }}>
            <div style={{
              width: 52, height: 52, borderRadius: 16, background: '#fef2f2',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 26, margin: '0 auto 2px', border: '1.5px solid #fecaca',
            }}>
              🔒
            </div>

            <div>
              <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a' }}>Security Verification</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, lineHeight: 1.5 }}>
                A biometric credential is already active for <strong>{empBioTarget.name}</strong>.<br/>
                Sent via <strong>WhatsApp</strong> & <strong>Email</strong> to authorize changes.
              </div>
            </div>

            <div style={{ marginBottom: 4 }}>
              <input
                type="text"
                maxLength={6}
                placeholder="Enter 6-Digit OTP"
                value={empBioOtpInput}
                onChange={e => setEmpBioOtpInput(e.target.value.replace(/\D/g, ''))}
                onKeyDown={e => { if (e.key === 'Enter') verifyEmpBioOtpAndProceed(); }}
                style={{
                  width: '100%', padding: '12px', borderRadius: 12, border: '2px solid #6366f1',
                  fontSize: 22, fontWeight: 900, letterSpacing: 6, textAlign: 'center',
                  background: '#f5f3ff', color: '#1e1b4b', outline: 'none', boxSizing: 'border-box',
                }}
                autoFocus
              />
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => { setShowEmpBioOtpModal(false); setEmpBioTarget(null); setEmpBioOtpInput(''); }}
                style={{ flex: 1, background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 12, padding: '12px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={verifyEmpBioOtpAndProceed}
                disabled={empBioOtpLoading || empBioOtpInput.length < 4}
                style={{
                  flex: 1, background: empBioOtpInput.length < 4 ? '#e2e8f0' : '#0f172a',
                  color: empBioOtpInput.length < 4 ? '#94a3b8' : '#fff', border: 'none',
                  borderRadius: 12, padding: '12px', fontSize: 13, fontWeight: 800, cursor: 'pointer',
                  opacity: empBioOtpLoading ? 0.6 : 1,
                }}
              >
                {empBioOtpLoading ? 'Verifying...' : 'Verify & Unlock 🔓'}
              </button>
            </div>

            <button
              type="button"
              onClick={() => handleEmpBiometricGuard(empBioTarget, empBioActionType || 'face')}
              disabled={empBioOtpLoading}
              style={{
                background: 'none', border: 'none', color: '#6366f1',
                fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0,
              }}
            >
              🔄 Resend OTP via WhatsApp & Email
            </button>
          </div>
        </Modal>
      )}

      {/* ── 📱 Remote Phone Fingerprint Attendance Punch Modal ── */}
      {showRemotePunchModal && (
        <Modal title="" onClose={() => {
          if (remotePunchPollRef.current) clearInterval(remotePunchPollRef.current);
          setShowRemotePunchModal(false);
          setRemotePunchQR('');
        }} width={420}>
          <div style={{ textAlign: 'center', padding: '12px 6px 18px' }}>
            <div style={{
              width: 52, height: 52, borderRadius: 18, background: '#ecfdf5',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 26, margin: '0 auto 12px', border: '1.5px solid #a7f3d0',
            }}>
              👆
            </div>

            <div style={{ fontSize: 22, fontWeight: 900, color: '#0f172a', letterSpacing: -0.5 }}>
              Scan to Punch Attendance
            </div>
            <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600, marginTop: 4, marginBottom: 18 }}>
              Open phone camera ➔ touch sensor to mark IN/OUT
            </div>

            {/* QR Card Container */}
            <div style={{
              padding: 16, background: '#fff', borderRadius: 22,
              border: '2px solid #e2e8f0', margin: '0 auto 14px',
              width: 210, height: 210,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 6px 20px rgba(0,0,0,0.05)',
            }}>
              {remotePunchQR ? (
                <img src={remotePunchQR} alt="Punch QR" style={{ width: 180, height: 180, borderRadius: 10 }} />
              ) : (
                <div style={{ color: '#94a3b8', fontSize: 12 }}>Generating QR...</div>
              )}
            </div>

            {/* Pulsing Status Pill */}
            <div style={{
              background: '#eef2ff', borderRadius: 99, padding: '7px 18px',
              fontSize: 12, color: '#4f46e5', fontWeight: 800,
              display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 12,
            }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#6366f1', animation: 'pulse 1.5s infinite' }} />
              <span>Waiting for phone scan & touch...</span>
            </div>

            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 20 }}>
              Works with iPhone Face ID / Touch ID and Android Fingerprint [1, 2]
            </div>

            <button
              onClick={() => {
                if (remotePunchPollRef.current) clearInterval(remotePunchPollRef.current);
                setShowRemotePunchModal(false);
                setRemotePunchQR('');
              }}
              style={{
                width: '100%', padding: '12px', background: '#f1f5f9', color: '#475569',
                border: 'none', borderRadius: 12, fontSize: 13, fontWeight: 800, cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}
{/* ── 📊 Day Punch & Time Breakdown Modal ── */}
      {dayModalData && (() => {
        const { emp, date, record, logs } = dayModalData;
        type Session = { inTime: string; inSource: string; outTime?: string; outSource?: string; durationMins: number };
        const sessions: Session[] = [];
        let totalActiveMinutes = 0;
        let totalInCount = 0;
        let totalOutCount = 0;

        const isToday = date === localDateStr();

        if (logs.length > 0) {
          let currentIn: { time: string; source: string } | null = null;
          logs.forEach((log, i) => {
            if (log.scan_type === 'IN') {
              totalInCount++;
              if (currentIn) {
                // Prior IN was abandoned without an OUT punch
                sessions.push({
                  inTime: currentIn.time,
                  inSource: currentIn.source,
                  outTime: '⚠️ Missed Out',
                  outSource: 'no_out',
                  durationMins: 0,
                });
              }
              currentIn = { time: log.time, source: log.source };
            } else if (log.scan_type === 'OUT') {
              totalOutCount++;
              if (currentIn) {
                const dur = Math.max(0, timeToMinutes(log.time) - timeToMinutes(currentIn.time));
                totalActiveMinutes += dur;
                sessions.push({
                  inTime: currentIn.time,
                  inSource: currentIn.source,
                  outTime: log.time,
                  outSource: log.source,
                  durationMins: dur,
                });
                currentIn = null;
              } else {
                sessions.push({
                  inTime: '⚠️ Missed In',
                  inSource: 'no_in',
                  outTime: log.time,
                  outSource: log.source,
                  durationMins: 0,
                });
              }
            }
          });

          // Handle the very last punch of the day
          if (currentIn) {
            sessions.push({
              inTime: (currentIn as any).time,
              inSource: (currentIn as any).source,
              outTime: isToday ? '🟢 Working Now' : '⚠️ No Out Punch',
              outSource: isToday ? 'active' : 'no_out',
              durationMins: isToday ? Math.max(0, timeToMinutes(new Date().toTimeString().slice(0, 5)) - timeToMinutes((currentIn as any).time)) : 0,
            });
            if (isToday) {
              totalActiveMinutes += Math.max(0, timeToMinutes(new Date().toTimeString().slice(0, 5)) - timeToMinutes((currentIn as any).time));
            }
          }
        } else if (record && (record.check_in || record.check_out)) {
          if (record.check_in) totalInCount++;
          if (record.check_out) totalOutCount++;
          const dur = (record.check_in && record.check_out)
            ? Math.max(0, timeToMinutes(record.check_out) - timeToMinutes(record.check_in))
            : 0;
          totalActiveMinutes = dur;
          sessions.push({
            inTime: record.check_in || '⚠️ Missed In',
            inSource: record.source || 'manual',
            outTime: record.check_out || (isToday ? '🟢 Working Now' : '⚠️ No Out Punch'),
            outSource: record.check_out ? (record.source || 'manual') : (isToday ? 'active' : 'no_out'),
            durationMins: dur,
          });
        }

        const firstIn = logs.find(l => l.scan_type === 'IN')?.time || record?.check_in || '—';
        const lastOutLog = [...logs].reverse().find(l => l.scan_type === 'OUT');
        const lastOut = lastOutLog?.time || record?.check_out || (isToday && logs.some(l => l.scan_type === 'IN') ? '🟢 Working' : '⚠️ Incomplete');

        const getSourceIcon = (src: string = '') => {
          const s = src.toLowerCase();
          if (s.includes('face')) return '👁️ Face ID';
          if (s.includes('finger') || s.includes('webauthn')) return '👆 Fingerprint';
          if (s.includes('qr') || s.includes('kiosk')) return '📷 QR Badge';
          if (s.includes('device') || s.includes('zkteco')) return '📠 Machine DEV';
          return '✍️ Manual';
        };

        return (
          <Modal
            title={`📅 Attendance Breakdown — ${new Date(date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}`}
            onClose={() => setDayModalData(null)}
            width={560}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div style={{
                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                borderRadius: 14, padding: '18px 20px', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 900 }}>{emp.name}</div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                    ID: #{emp.id} · {record?.status?.toUpperCase() || (sessions.length ? 'PRESENT' : 'NO RECORD')}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 24, fontWeight: 900, color: '#38bdf8' }}>
                    {formatMinutes(totalActiveMinutes)}
                  </div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Total Time Inside
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 8px', textAlign: 'center' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>First In</div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#16a34a', marginTop: 2 }}>{firstIn}</div>
                </div>
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 8px', textAlign: 'center' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Last Out</div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#dc2626', marginTop: 2 }}>{lastOut}</div>
                </div>
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 8px', textAlign: 'center' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Check-Ins</div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a', marginTop: 2 }}>{totalInCount} times</div>
                </div>
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 8px', textAlign: 'center' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Check-Outs</div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a', marginTop: 2 }}>{totalOutCount} times</div>
                </div>
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                  ⏱️ Punch Log Timeline
                </div>
                {dayModalLoading ? (
                  <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>Loading punch logs...</div>
                ) : sessions.length === 0 ? (
                  <div style={{ background: '#fef2f2', border: '1px dashed #fca5a5', borderRadius: 12, padding: 24, textAlign: 'center' }}>
                    <div style={{ fontSize: 24, marginBottom: 4 }}>🚫</div>
                    <div style={{ fontWeight: 700, color: '#991b1b', fontSize: 13 }}>No punches recorded for this date</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 280, overflowY: 'auto' }}>
                    {sessions.map((sess, idx) => (
                      <div key={idx} style={{
                        border: '1.5px solid #e2e8f0', borderRadius: 12, padding: '12px 16px',
                        background: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 32, height: 32, borderRadius: 8, background: '#f0fdf4', border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#16a34a', fontWeight: 900, fontSize: 12 }}>
                            IN
                          </div>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a', fontFamily: 'monospace' }}>{sess.inTime}</div>
                            <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b' }}>{getSourceIcon(sess.inSource)}</div>
                          </div>
                        </div>

                        <div style={{ textAlign: 'center', padding: '0 8px' }}>
                          <div style={{ fontSize: 11, fontWeight: 800, color: sess.durationMins > 0 ? '#4f46e5' : '#94a3b8', background: '#eef2ff', padding: '2px 8px', borderRadius: 99 }}>
                            {sess.durationMins > 0 ? formatMinutes(sess.durationMins) : 'Active Session'}
                          </div>
                          <div style={{ fontSize: 12, color: '#cbd5e1' }}>────────▶</div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, textAlign: 'right' }}>
                          <div>
                            <div style={{
                              fontSize: 13,
                              fontWeight: 800,
                              color: sess.outTime?.includes('Working') ? '#16a34a' : sess.outTime?.includes('⚠️') ? '#d97706' : '#0f172a',
                              fontFamily: sess.outTime?.includes(':') ? 'monospace' : 'inherit',
                            }}>
                              {sess.outTime}
                            </div>
                            <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b' }}>
                              {sess.outSource === 'active' ? 'Currently Inside' : sess.outSource === 'no_out' ? 'Missing punch-out' : getSourceIcon(sess.outSource)}
                            </div>
                          </div>
                          <div style={{
                            width: 32, height: 32, borderRadius: 8,
                            background: sess.outTime?.includes('Working') ? '#f0fdf4' : sess.outTime?.includes('⚠️') ? '#fffbeb' : '#fef2f2',
                            border: `1px solid ${sess.outTime?.includes('Working') ? '#86efac' : sess.outTime?.includes('⚠️') ? '#fde68a' : '#fecaca'}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: sess.outTime?.includes('Working') ? '#16a34a' : sess.outTime?.includes('⚠️') ? '#d97706' : '#dc2626',
                            fontWeight: 900, fontSize: 11,
                          }}>
                            OUT
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={() => setDayModalData(null)}
                style={{ ...S.saveBtn, marginTop: 4, background: '#0f172a' }}
              >
                Close Breakdown
              </button>
            </div>
          </Modal>
        );
      })()}
      {showScanSettings && (
        <Modal title="⚙️ Scan Settings — Employees" onClose={() => setShowScanSettings(false)} width={440}>
          {scanSettingsLoading ? (
            <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>Loading...</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                Control which scan methods employees can use to mark attendance, and whether repeat scans are blocked once a day is complete.
              </div>
              {[
                { key: 'allow_qr' as const, label: '📷 QR Code Scan' },
                { key: 'allow_face' as const, label: '👁️ Face Recognition' },
                { key: 'allow_fingerprint' as const, label: '👆 Fingerprint / Face ID' },
              ].map(({ key, label }) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{label}</span>
                  <div
                    onClick={() => setScanSettings(s => ({ ...s, [key]: !s[key] }))}
                    style={{
                      width: 40, height: 22, borderRadius: 99, cursor: 'pointer',
                      background: scanSettings[key] ? '#7c3aed' : '#cbd5e1',
                      position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                    }}
                  >
                    <div style={{
                      position: 'absolute', top: 2, left: scanSettings[key] ? 20 : 2,
                      width: 18, height: 18, borderRadius: '50%', background: '#fff',
                      transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }} />
                  </div>
                </div>
              ))}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid #fde68a', background: '#fffbeb', borderRadius: 10, padding: '10px 14px' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>🔒 Single Scan Only</div>
                  <div style={{ fontSize: 11, color: '#92400e', marginTop: 2 }}>Block further scans once an employee has completed check-in + check-out today</div>
                </div>
                <div
                  onClick={() => setScanSettings(s => ({ ...s, single_scan_only: !s.single_scan_only }))}
                  style={{
                    width: 40, height: 22, borderRadius: 99, cursor: 'pointer', flexShrink: 0,
                    background: scanSettings.single_scan_only ? '#d97706' : '#cbd5e1',
                    position: 'relative', transition: 'background 0.2s',
                  }}
                >
                  <div style={{
                    position: 'absolute', top: 2, left: scanSettings.single_scan_only ? 20 : 2,
                    width: 18, height: 18, borderRadius: '50%', background: '#fff',
                    transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  }} />
                </div>
              </div>
              {!scanSettings.allow_qr && !scanSettings.allow_face && !scanSettings.allow_fingerprint && (
                <div style={{ fontSize: 12, color: '#dc2626', fontWeight: 600 }}>⚠️ At least one scan method must stay enabled.</div>
              )}
              <button
                style={{ ...S.saveBtn, opacity: (!scanSettings.allow_qr && !scanSettings.allow_face && !scanSettings.allow_fingerprint) || scanSettingsSaving ? 0.5 : 1 }}
                disabled={(!scanSettings.allow_qr && !scanSettings.allow_face && !scanSettings.allow_fingerprint) || scanSettingsSaving}
                onClick={saveScanSettings}
              >
                {scanSettingsSaving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          )}
        </Modal>
      )}

      {/* ⚠️ WhatsApp Disconnected Alert Modal */}
      {notConnectedModal && (
        <Modal title="⚠️ WhatsApp Not Connected" onClose={() => setNotConnectedModal(null)} width={420}>
          <div style={{ textAlign: 'center', padding: '10px 0' }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%', background: '#fef2f2',
              border: '2px solid #fecaca', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 28, margin: '0 auto 12px',
            }}>
              📱
            </div>
            <div style={{ fontSize: 17, fontWeight: 900, color: '#0f172a', marginBottom: 6 }}>
              WhatsApp is Not Connected
            </div>
            <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.5, marginBottom: 20 }}>
              {notConnectedModal.reason || "WhatsApp isn't connected for this outlet. Connect it in Settings to send messages."}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                type="button"
                onClick={() => {
                  setNotConnectedModal(null);
                  window.dispatchEvent(new CustomEvent('open-whatsapp-settings'));
                }}
                style={{
                  width: '100%', background: '#25D366', color: '#fff', border: 'none',
                  borderRadius: 10, padding: '12px', fontSize: 13, fontWeight: 800, cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(37,211,102,0.3)',
                }}
              >
                📲 Connect WhatsApp Now
              </button>
              <button
                type="button"
                onClick={() => setNotConnectedModal(null)}
                style={{
                  width: '100%', background: '#f1f5f9', color: '#475569', border: 'none',
                  borderRadius: 10, padding: '10px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                }}
              >
                Dismiss
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* 💬 Floating Toast Notification */}
      {toastMsg && (
        <div style={{
          position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)',
          background: toastMsg.type === 'error' ? '#ef4444' : toastMsg.type === 'info' ? '#3b82f6' : '#0f172a',
          color: '#fff', borderRadius: 14, padding: '12px 24px', fontSize: 13, fontWeight: 700,
          zIndex: 9999, boxShadow: '0 8px 32px rgba(0,0,0,0.25)', whiteSpace: 'nowrap',
        }}>
          {toastMsg.msg}
        </div>
      )}

    </div>
    </>
  );
}
